import * as THREE from 'three';
import { generateMap } from '../map/generator.js';
import type { MapData } from '../map/types.js';

/** Mirror of the server's `CASTLE_UPGRADE_COSTS`. Used to display button costs. */
const CASTLE_UPGRADE_COSTS: Record<number, { wood: number; stone: number; gold: number; iron: number; silver: number }> = {
  1: { wood: 20, stone: 25, gold: 20, iron: 5, silver: 0 },
  2: { wood: 40, stone: 50, gold: 40, iron: 15, silver: 3 },
};

/**
 * Per-unit-type sprite recipe (used by both the HTML chips and the 3D
 * composite textures placed on the map).
 *   bodyKind  → '<faction>_army.png' for grunts, '<faction>_hero.png' for heroes
 *   right     → weapon sprite on the right (mandatory)
 *   left      → optional weapon/shield on the left (paladin only for now)
 *
 * These are the DEFAULT weapons. The local player can override their own
 * army's weapons via the cosmetics screen — `getEffectiveRecipe` checks
 * `auth.user.cosmeticLoadout` and swaps in the chosen sprites when the
 * faction matches the logged-in user.
 */
const DEFAULT_UNIT_RECIPE: Record<
  'warrior' | 'archer' | 'berserker' | 'crossbowman' | 'paladin' | 'hero' | 'kamikaze',
  { bodyKind: 'army' | 'hero'; right: string; left?: string }
> = {
  warrior:     { bodyKind: 'army', right: '/sprites/arme_hache_pierre.png' },
  archer:      { bodyKind: 'army', right: '/sprites/arme_arc_bois.png' },
  berserker:   { bodyKind: 'army', right: '/sprites/arme_hache_fer.png' },
  crossbowman: { bodyKind: 'army', right: '/sprites/arme_warshooter_bois.png' },
  paladin:     { bodyKind: 'army', right: '/sprites/arme_epee.png', left: '/sprites/arme_bouclier_pierre.png' },
  hero:        { bodyKind: 'hero', right: '/sprites/arme_epee_or.png' },
  kamikaze:    { bodyKind: 'army', right: '/sprites/arme_bombe.png' },
};

/** Track which faction id is the local player's (set on HUD mount). When a
 *  unit chip / map texture is built for THIS faction, the loadout is read
 *  from `auth.user.cosmeticLoadout`; otherwise the defaults stay in place. */
let localFactionId: string | null = null;

/** Lookup a weapon sprite by id from the loadout. The loadout stores bare
 *  weapon ids (no path); we map them to `/sprites/<id>.png`. Unknown ids
 *  fall back to the default. */
function spriteForWeaponId(id: string | undefined, fallback: string): string {
  if (!id) return fallback;
  // The catalog uses id == filename (without `.png`). Defensive: only allow
  // ids that look like our naming convention to avoid foot-guns.
  if (!/^[a-zA-Z0-9_]+$/.test(id)) return fallback;
  return `/sprites/${id}.png`;
}

/** Resolve the effective sprite recipe for a unit type. Honors the local
 *  player's cosmetic loadout when `factionId` matches their own faction. */
function getEffectiveRecipe(
  unitType: ServerUnitType,
  factionId: string,
): { bodyKind: 'army' | 'hero'; right: string; left?: string } {
  const def = DEFAULT_UNIT_RECIPE[unitType];
  if (!localFactionId || factionId !== localFactionId) return def;
  const loadout = auth.user?.cosmeticLoadout;
  if (!loadout) return def;
  if (unitType === 'paladin') {
    const p = (loadout.paladin ?? {}) as { right?: string; left?: string };
    return {
      bodyKind: def.bodyKind,
      right: spriteForWeaponId(p.right, def.right),
      left: spriteForWeaponId(p.left, def.left ?? ''),
    };
  }
  const chosen = (loadout as Record<string, string | undefined>)[unitType];
  return { bodyKind: def.bodyKind, right: spriteForWeaponId(chosen, def.right), left: def.left };
}

/** Cache of composite (faction + unit type + weapon) army textures used on the
 *  3D map. The cache key embeds the resolved weapon sprites so swapping a
 *  cosmetic loadout immediately produces a different cached entry. Bump the
 *  version constant whenever the canvas drawing recipe changes. */
const COMPOSITE_CACHE_VERSION = 4;
const compositeArmyCache = new Map<string, THREE.CanvasTexture>();

/** Invalidate every cached composite texture. Called when the local user's
 *  cosmetic loadout changes so the next render rebuilds with the new sprites
 *  even for already-mounted armies. */
export function invalidateArmyTextureCache(): void {
  for (const tex of compositeArmyCache.values()) tex.dispose();
  compositeArmyCache.clear();
}

/**
 * Build a single texture that contains the faction's body sprite next to
 * (and slightly overlapping with) the weapon sprite for `unitType`. We draw
 * onto a wide canvas so a single billboard shows both at once, regardless
 * of camera angle. Cached because canvas+image work isn't free.
 */
async function getOrBuildArmyTexture(
  factionId: string,
  unitType: ServerUnitType,
): Promise<THREE.CanvasTexture | null> {
  const recipe = getEffectiveRecipe(unitType, factionId);
  const key = `${factionId}-${unitType}-${recipe.right}-${recipe.left ?? ''}-v${COMPOSITE_CACHE_VERSION}`;
  const cached = compositeArmyCache.get(key);
  if (cached) return cached;
  try {
    const [body, right, left] = await Promise.all([
      loadImg(`/sprites/${factionId}_${recipe.bodyKind}.png`),
      loadImg(recipe.right),
      recipe.left ? loadImg(recipe.left) : Promise.resolve(null),
    ]);
    // Body sprites are 260×260 with content roughly centred; weapons ~250×250
    // or 180×180. The composite is wider than tall to hold body + side weapons.
    const W = 480;
    const H = 260;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    // Body in the middle (native 260×260)
    ctx.drawImage(body, (W - 260) / 2, 0);
    // Weapons drawn at ~72% of native size (20% smaller than before). The
    // weapons read as "accents", not as a second body.
    // Previous version: 234×234. New: 187×187.
    const WSZ = 187;
    // Right weapon — overlaps the body's right side a bit. We tighten the
    // X offset proportionally so the smaller weapon still hugs the body.
    ctx.drawImage(right, (W - 260) / 2 + 162, 50, WSZ, WSZ);
    // Left weapon (paladin shield) — same logic on the left.
    if (left) ctx.drawImage(left, (W - 260) / 2 - 88, 78, WSZ, WSZ);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.needsUpdate = true;
    compositeArmyCache.set(key, tex);
    return tex;
  } catch {
    return null;
  }
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

import { MapRenderer, type MapEntity } from '../render/mapRenderer.js';
import { TECHS, BOOSTS, MISSIONS, type TechDef, type BoostDef, type MissionDef } from '../lobby/colyseus.js';
import type { LobbyState, Participant, Phase, Room } from '../lobby/colyseus.js';
import { fetchInsults, type FactionDef, type InsultDef } from '../lobby/api.js';
import { auth } from '../auth/state.js';
import { attachChat, detachChat, openPrivateChannel } from './chat.js';
import { el } from './login.js';
import { renderTextWithIcons, setIconText } from './inline_icons.js';
import {
  playMeow,
  playPurr,
  playWhoosh,
  playHiss,
  playFanfare,
  playPlouf,
  playTinkle,
  playTap,
  playPawStep,
  playMiauDesapprouv,
  playVictory,
  playSparkle,
  playMyTurn,
  playDiceThrow,
  playDiceImpact,
  playDiceBounce,
  playDiceReveal,
  playBarbarianArrival,
  playMeowForFaction,
  playBattleWin,
  playBattleLose,
  playCampBuild,
  playSnacaliaCollect,
  getMasterVolume,
  setMasterVolume,
} from '../sound/sounds.js';
import {
  getMusicVolume, setMusicVolume,
  isMusicMuted, setMusicMuted,
  skipToNext as skipToNextMusic,
  getCurrentMusicIndex, getMusicTrackCount,
  ensureMusicPlaying,
  pauseMusic,
} from '../sound/music.js';
import {
  attachTutorialOverlay,
  detachTutorialOverlay,
  getTutorialSession,
} from './tutorial.js';

/**
 * In-game view: full-screen 3D map + HUD overlays.
 *
 * Lifecycle:
 *   - mountInGame(): creates the renderer, generates the map from state.seed,
 *     hooks click events for placement, and starts watching the room state.
 *   - On every state patch, the HUD re-renders and ownership colours update.
 *   - unmountInGame(): destroys the renderer and HUD listeners.
 *
 * The map renderer mounts inside #app (the existing canvas host). The HUD
 * lives in #game-hud, an overlay div on top of the canvas.
 */
export interface InGameContext {
  room: Room;
  state: LobbyState;
  factions: FactionDef[];
}

let renderer: MapRenderer | null = null;
let unsubClick: (() => void) | null = null;
let unsubEmptyClick: (() => void) | null = null;
let unsubRightClick: (() => void) | null = null;
let unsubEntityClick: (() => void) | null = null;
let unsubActionMove: (() => void) | null = null;
let unsubActionRanged: (() => void) | null = null;
let unsubActionDropSpawn: (() => void) | null = null;
let unsubActionDropCollect: (() => void) | null = null;
let unsubMissionDrawn: (() => void) | null = null;
let unsubMissionComplete: (() => void) | null = null;
let activeDropSprite: import('three').Sprite | null = null;
let escapeHandler: ((e: KeyboardEvent) => void) | null = null;
let mountedSeed: number | null = null;
/**
 * The currently "armed" region — set by clicking on an army sprite. While set,
 * the renderer highlights adjacent regions and the next region click triggers
 * the move/attack modal pre-filled with this region as the source.
 */
let selectedArmyRegion: number | null = null;
let cachedMap: MapData | null = null;
let adjacency: Map<number, Set<number>> = new Map();
let continentRegions: Map<number, Set<number>> = new Map();
let selectedRegionId: number | null = null;
/** Tracks state.currentTurnIndex across patches so we can detect transitions
 *  ("turn N just started — focus the active player's castle"). */
let lastTurnIndexSeen = -1;
/** Per-region "hold the old colour" entries used so a winning conquest
 *  doesn't recolour the destination tile to the attacker's colour BEFORE
 *  the battle animation finishes (which would spoil the outcome). While
 *  a hold is active, applyOwnership paints `color` instead of the
 *  state-derived owner colour. Cleared automatically once `until` (a
 *  performance.now() timestamp) passes. */
const pendingRecolorHolds = new Map<number, { color: number; until: number }>();
/** Tracks whether the "game over" jingle has already played (one-shot). */
let endedSoundPlayed = false;
/** Live ctx — handlers read from this so they always see the latest state. */
let currentCtx: InGameContext | null = null;
/**
 * Active castle-shot targeting session. When non-null, the player is
 * choosing a target on the map for a "Tirer depuis le repaire" action.
 * `inRange` = every region reachable within the cannon's range — all are
 * highlighted so the player can see the cannon's reach even when there
 * are no enemies nearby. `targets` ⊆ `inRange` is the subset that's
 * actually clickable (enemy-owned regions). Click on a non-target
 * in-range region simply cancels the mode.
 *
 * Mutually exclusive with the regular army selection (`selectedArmyRegion`).
 */
let castleShotMode: {
  from: number;
  range: number;
  inRange: Set<number>;
  targets: Set<number>;
} | null = null;
/** Whether the slide-in leaderboard panel is currently expanded. Module-level
 *  so it survives HUD re-renders triggered by state patches. */
let leaderboardOpen = false;
let leaderboardHideTimer: number | null = null;
/** Same pattern for the audio-settings slide-in. */
let settingsOpen = false;
let settingsHideTimer: number | null = null;

/** Player preference: should the camera auto-follow actions (turn changes,
 *  ranged attacks, etc.)? Persisted in localStorage so it survives reloads.
 *  Default ON for new players who want to "see" what's happening. */
let cameraFollowEnabled: boolean = (() => {
  try {
    const v = window.localStorage.getItem('wol.cameraFollow');
    return v === null ? true : v === 'true';
  } catch { return true; }
})();
function setCameraFollow(enabled: boolean): void {
  cameraFollowEnabled = enabled;
  try { window.localStorage.setItem('wol.cameraFollow', String(enabled)); }
  catch { /* localStorage disabled — preference just lives in memory */ }
}
/** Wrapper around renderer.tweenCameraTo that respects the user preference. */
function tweenCameraIfFollowing(x: number, z: number, ms: number): void {
  if (!cameraFollowEnabled) return;
  renderer?.tweenCameraTo(x, z, ms);
}

// === Dice ceremony state (animated dice roll at game start) ===
/** True while we're currently running the per-participant dice animation. */
let diceCeremonyRunning = false;
/** ParticipantID → currently-displayed dice value. Populated by the dice
 *  ceremony as each participant rolls (round 0), and updated when ties are
 *  resolved (round 1+). The bottom turn-order strip reads from this map
 *  during dice_roll so the badges progressively reveal — and **update** —
 *  as the ceremony advances. */
const diceDisplayValues = new Map<string, number>();

/** Force a fresh HUD render from outside the normal state-patch path —
 *  used by the dice ceremony timer to redraw the bottom turn-order strip
 *  after each kingdom finishes rolling. */
function forceHudRender(): void {
  const gh = document.getElementById('game-hud') as HTMLDivElement | null;
  if (gh && currentCtx) renderHud(gh, currentCtx);
}

/**
 * DOM for the WebGL-init failure panel. Mounted into `#app` when the
 * Three.js renderer throws on first construction (GPU off, browser
 * pending an update, sandboxed tab, …). Self-contained — no app state
 * dependency — so it can run before any room data is available.
 */
function buildWebglFailPanel(): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'webgl-fail-panel';

  // Big sad iconchat at the top, animated wobble — sells the "le jeu
  // est triste de pas pouvoir s'afficher" mood.
  const sad = document.createElement('img');
  sad.src = '/sprites/iconchat_perdu.png';
  sad.alt = '';
  sad.className = 'webgl-fail-sad';
  panel.appendChild(sad);

  const title = document.createElement('h2');
  title.textContent = 'Oh non, mon écran fait la sieste !';
  panel.appendChild(title);

  const sub = document.createElement('p');
  sub.className = 'webgl-fail-sub';
  sub.textContent =
    'Ton navigateur n\'arrive pas à dessiner la carte 3D — WebGL boude. ' +
    'Pas de panique chaton, voici ce qui marche presque toujours :';
  panel.appendChild(sub);

  const tipList = document.createElement('ul');
  tipList.className = 'webgl-fail-tips';

  // Each tip = small iconchat + cat-themed text. Order = most likely
  // fix first so the player rarely needs to read past tip #1.
  const tips: Array<{ icon: string; text: string }> = [
    {
      icon: 'iconchat_capitulation',
      text:
        'Ferme COMPLÈTEMENT ton navigateur (tous les onglets, pas juste cette page) ' +
        'puis rouvre-le. C\'est souvent une mise à jour qui se cachait derrière le ' +
        'canapé en attendant que tu redémarres tout pour s\'installer. MIAOU magique.',
    },
    {
      icon: 'iconchat_interrogation',
      text:
        'Toujours bloqué ? Vérifie chrome://settings/system → active ' +
        '« Utiliser l\'accélération graphique si disponible », puis redémarre encore. ' +
        'Sans ça, ton navigateur dessine avec ses pattes au lieu de sa griffe magique.',
    },
    {
      icon: 'iconchat_champion',
      text:
        'Test ultime : ouvre get.webgl.org dans un nouvel onglet. Si le cube ' +
        'tourne, reviens ici et rafraîchis (Cmd/Ctrl + Shift + R). Si le cube ne ' +
        'tourne pas non plus → ton navigateur a vraiment besoin d\'aide pro.',
    },
  ];

  for (const t of tips) {
    const li = document.createElement('li');
    const ico = document.createElement('img');
    ico.src = `/sprites/${t.icon}.png`;
    ico.alt = '';
    ico.className = 'webgl-fail-tip-icon';
    li.appendChild(ico);
    const txt = document.createElement('span');
    txt.textContent = t.text;
    li.appendChild(txt);
    tipList.appendChild(li);
  }
  panel.appendChild(tipList);

  return panel;
}

/** Single-flight guard: room.ts can fire mountInGame from successive
 *  state patches (lobby → dice_roll → placement) before the previous
 *  call's async `await` returns. Without this, the WebGL renderer is
 *  constructed twice in a row, stacking error logs (and on a GPU-less
 *  sandbox, the second/third attempt fails noisily). */
let mountInFlight = false;
let webglFailureShown = false;

export async function mountInGame(ctx: InGameContext): Promise<void> {
  if (mountInFlight) return;
  mountInFlight = true;
  try {
    await mountInGameInner(ctx);
  } finally {
    mountInFlight = false;
  }
}

async function mountInGameInner(ctx: InGameContext): Promise<void> {
  const app = document.getElementById('app') as HTMLDivElement | null;
  const gameHud = document.getElementById('game-hud') as HTMLDivElement | null;
  if (!app || !gameHud) return;

  // Show canvas + HUD overlay; hide the lobby/screen container.
  app.style.display = '';
  gameHud.style.display = '';
  // Hide the top navbar while in-game — the screen real estate is more
  // valuable for the map than for the always-visible nav. The HUD has
  // its own quit/settings affordances. Restored in unmountInGame().
  const navbar = document.getElementById('navbar');
  if (navbar) navbar.style.display = 'none';
  // The body/app CSS uses `top: 56px` under the navbar; with it hidden
  // we need the canvas to take the full viewport.
  document.body.classList.add('in-game-no-navbar');

  // Make sure the background music is actually playing. First entry
  // starts it for real (the user-gesture window from whatever button
  // got them here — "Démarrer", "Rejoindre" or "Revenir à ma chasse" —
  // is still open). Subsequent entries (rejoin after a leave) resume
  // a paused audio element so the player doesn't sit in silence.
  ensureMusicPlaying();

  // Tutorial overlay — only mounts when the session was flagged via
  // the home-hub TUTO button. Idempotent; safe across re-mounts.
  if (getTutorialSession()) {
    attachTutorialOverlay(ctx);
  }

  // CRITICAL: wait one animation frame so the browser computes layout
  // after the `display = ''` flip BEFORE we construct the WebGL
  // renderer. Without this, `app.clientWidth` is still 0 (the element
  // was `display: none` a microsecond ago) and Three.js initialises
  // a 0×0 canvas — the map renders into the void and the host sees a
  // blank screen. The other player who joined when `app` was already
  // visible didn't hit this race. (Latent bug since Phase D.)
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  // (Re)create renderer if needed (first mount or seed changed).
  // Wrapped in try/catch — on a GPU-less sandbox / browser that can't
  // allocate a WebGL context (`Sandboxed = yes, GL_VENDOR = Disabled`),
  // Three.js throws here. Without the catch the error bubbles up
  // unhandled and each subsequent state patch retries → log spam.
  if (!renderer || mountedSeed !== ctx.state.seed) {
    // Tear down the previous renderer BEFORE constructing a new one.
    // Without this its canvas stays attached to #app, keeping its
    // WebGL context alive in the browser's ~16-context slot pool.
    // Multiple tutorial runs would exhaust the pool and the new
    // construction below would fail with "Could not create WebGL
    // context". We also belt-and-suspenders any orphan canvases left
    // by older mounts that didn't dispose.
    if (renderer) {
      try { renderer.dispose(); } catch { /* swallow */ }
      renderer = null;
    }
    app.querySelectorAll('canvas').forEach((c) => {
      try {
        const gl = (c as HTMLCanvasElement).getContext('webgl2')
          ?? (c as HTMLCanvasElement).getContext('webgl');
        (gl as WebGLRenderingContext | null)
          ?.getExtension('WEBGL_lose_context')
          ?.loseContext();
      } catch { /* noop */ }
      c.remove();
    });
    try {
      renderer = new MapRenderer(app);
      await renderer.init();
      mountedSeed = ctx.state.seed;
    } catch (err) {
      renderer = null;
      mountedSeed = null;
      if (!webglFailureShown) {
        webglFailureShown = true;
        console.error('[in_game] MapRenderer init failed — WebGL unavailable?', err);
        // Friendly on-screen fallback so the player isn't staring at a
        // blank screen wondering what broke. Cat-themed: big sad cat
        // up top, three tips below — each one prefixed with an iconchat
        // sprite (capitulation/interrogation/champion) rather than a
        // system emoji so the panel matches the rest of the game's UI.
        app.innerHTML = '';
        app.appendChild(buildWebglFailPanel());
      }
      return;
    }
  } else {
    // Reusing an existing renderer — make sure its canvas size still
    // matches the (now-visible) host, in case the browser was zoomed
    // / resized while we were in the lobby.
    renderer.resizeToHost();
  }

  // Defensive: if the renderer creation failed silently (no exception
  // bubbled but the object is null), bail rather than crash on
  // `null.setMap`. The user already sees the WebGL fail panel in
  // that case; this just keeps the console clean.
  if (!renderer) return;

  // Generate the same map every client will see, from the synced seed/dims.
  const map = generateMapForState(ctx.state);
  renderer.setMap(map);
  cachedMap = map;
  adjacency = buildAdjacency(map);
  continentRegions = buildContinentRegions(map);

  // Wire left click → context-sensitive: place castle / select region.
  if (unsubClick) unsubClick();
  unsubClick = renderer.onRegionClick((regionId) => {
    if (!currentCtx) return;
    handleRegionClick(currentCtx, regionId);
  });
  // Right click → quick action: open the move/attack modal on owned armies.
  if (unsubRightClick) unsubRightClick();
  unsubRightClick = renderer.onRegionRightClick((regionId) => {
    if (!currentCtx) return;
    handleRegionRightClick(currentCtx, regionId);
  });
  // Left click on a castle/army SPRITE → enters the "select target" mode if
  // it's our unit; otherwise falls through to a region click (no-op for now).
  if (unsubEntityClick) unsubEntityClick();
  unsubEntityClick = renderer.onEntityClick(({ kind, regionId }) => {
    if (!currentCtx) return;
    if (kind === 'army' || kind === 'castle') {
      handleEntityClick(currentCtx, regionId);
    }
  });
  // Click on ocean / off-map → dismiss any open region info panel and
  // clear army selection / castle-shot mode. Lets the user "click away"
  // to close the panel instead of having to click the same region again.
  if (unsubEmptyClick) unsubEmptyClick();
  unsubEmptyClick = renderer.onEmptyClick(() => {
    if (!currentCtx) return;
    let needsRender = false;
    if (selectedRegionId !== null) { selectedRegionId = null; needsRender = true; }
    if (selectedArmyRegion !== null) { clearArmySelection(); needsRender = true; }
    if (castleShotMode !== null) { clearCastleShotMode(); needsRender = true; }
    if (needsRender) {
      const gh = document.getElementById('game-hud') as HTMLDivElement | null;
      if (gh) renderHud(gh, currentCtx);
    }
  });

  // Server broadcasts authoritative animation events for every move/range
  // resolution. Each client (including the one that issued the command)
  // listens and spawns ghosts/projectiles/bursts in lockstep.
  if (unsubActionMove) unsubActionMove();
  unsubActionMove = ctx.room.onMessage('action.move', (payload: ActionMovePayload) => {
    if (!currentCtx) return;
    void onActionMoveBroadcast(currentCtx, payload);
  });
  if (unsubActionRanged) unsubActionRanged();
  unsubActionRanged = ctx.room.onMessage('action.ranged', (payload: ActionRangedPayload) => {
    if (!currentCtx) return;
    void onActionRangedBroadcast(currentCtx, payload);
  });
  if (unsubActionDropSpawn) unsubActionDropSpawn();
  // Random global event — at the moment only the barbarian raid fires
  // (fish_rain and big_sleep were retired as random events). The banner
  // is intentionally minimal: a small floating title card at the top of
  // the screen, no full-screen overlay, no rain effect. The boat
  // animation (handled by the separate action.barbarian_arrival message
  // below) carries the dramatic weight.
  ctx.room.onMessage('action.event', (payload: { kind?: string }) => {
    const kind = payload?.kind;
    if (kind === 'barbarian_raid') spawnEventBanner({
      kind,
      title: 'INVASION BARBARE !',
      sub: 'Des chats barbares débarquent sur la côte — armez vos griffes, miaoulez fort !',
      iconSprite: '/sprites/iconchat_barbare.png',
    });
  });

  // The barbarian arrival broadcast piggybacks on action.event but
  // carries the landing-region id + composition so we can play the boat
  // animation centred on the actual landing tile. The server pauses the
  // next-turn advance for `blockMs` so the camera doesn't whip away
  // mid-animation.
  ctx.room.onMessage('action.barbarian_arrival', (payload: {
    regionId: number;
    units?: UnitsLike;
    blockMs?: number;
  }) => {
    if (!currentCtx || !cachedMap || !renderer) return;
    const region = cachedMap.regions[payload.regionId];
    if (!region) return;
    // CRUCIAL: hide the barbarian army sprite on the landing tile for
    // the full duration of the boat journey + landing. Without this,
    // the server's regionOwners[r] = barbarianIdx + applyArmy() mutate
    // the state SYNCHRONOUSLY, so the army sprite would pop in on the
    // landing tile the very next render frame — before the boat even
    // appears. `hideArrivingEntity` masks the entity then fades it
    // back in once the timer elapses, matching the disembark feel.
    // 6 s boat trip + 0.6 s landing flourish = 6600 ms total hide.
    renderer.hideArrivingEntity(payload.regionId, 6600);
    // First snap the camera near the landing tile so the player sees
    // *where* the action is happening, then once the boat spawns the
    // followCamera flag takes over and tracks the sprite frame-by-frame.
    renderer.tweenCameraTo(region.centroid[0], region.centroid[1], 700);
    // Sail the coffre_bois "boat" from the open ocean to the coastal
    // landing tile (~6 s). The renderer computes an ocean-only path
    // so the sprite never crosses another kingdom's territory and
    // moves the camera along with the boat so the player sees the
    // whole journey.
    void (async (): Promise<void> => {
      await new Promise<void>((r) => setTimeout(r, 250));
      renderer!.spawnBarbarianBoat(payload.regionId, {
        duration: 6000,
        // Bumped scale from 48 → 64 — the new `barbare_ship.png`
        // sprite is more detailed than the previous coffre_bois
        // placeholder and reads better at this size on the 3D map.
        scale: 64,
        followCamera: true,
      });
    })();
    playBarbarianArrival();
  });

  unsubActionDropSpawn = ctx.room.onMessage('action.drop_spawn', (payload: { regionId: number }) => {
    if (!currentCtx || !cachedMap || !renderer) return;
    const region = cachedMap.regions[payload.regionId];
    if (!region) return;
    // Pre-load the texture (next call returns it from cache).
    void (async (): Promise<void> => {
      // The renderer.loadEntityTexture is private; we load via spawnDropFromSky
      // twice if needed — first call kicks the loader and returns null, second
      // call actually spawns. setTimeout gives the loader a frame.
      let sp = renderer!.spawnDropFromSky([region.centroid[0], region.centroid[1]], '/sprites/boisson_snacalia_or.png', { scale: 32 });
      if (!sp) {
        await new Promise<void>((r) => setTimeout(r, 200));
        sp = renderer!.spawnDropFromSky([region.centroid[0], region.centroid[1]], '/sprites/boisson_snacalia_or.png', { scale: 32 });
      }
      if (sp) activeDropSprite = sp;
    })();
    playSparkle();
  });
  if (unsubMissionDrawn) unsubMissionDrawn();
  unsubMissionDrawn = ctx.room.onMessage('mission.drawn', (p: { participantId: string; missionId: string }) => {
    // Only animate for ME (others don't matter visually).
    const meNow = findMyParticipant(currentCtx!.state);
    if (!meNow || meNow.id !== p.participantId) return;
    playSparkle();
    // Trigger flip animation next render — `renderMissionPanel` watches
    // `lastMissionIdSeen` and tags the new card with `.gh-mission-drawn`.
  });
  if (unsubMissionComplete) unsubMissionComplete();
  unsubMissionComplete = ctx.room.onMessage('mission.complete', (p: { participantId: string; missionId: string }) => {
    const meNow = findMyParticipant(currentCtx!.state);
    if (!meNow || meNow.id !== p.participantId) return;
    playFanfare();
    // Brief toast at the bottom of the screen
    showMissionToast(MISSIONS[p.missionId]?.name ?? 'Mission accomplie');
  });
  if (unsubActionDropCollect) unsubActionDropCollect();
  unsubActionDropCollect = ctx.room.onMessage('action.drop_collect', (p: { regionId: number; factionId: string; reward: { wood: number; stone: number; gold: number; iron: number; silver: number; fruit: number } }) => {
    if (!cachedMap || !renderer) return;
    const region = cachedMap.regions[p.regionId];
    if (region) {
      const color = factionColor(currentCtx!, p.factionId);
      renderer.spawnImpactBurst([region.centroid[0], region.centroid[1]], color, { count: 30, spread: 1.5 });
    }
    if (activeDropSprite) {
      renderer.clearDrop(activeDropSprite);
      activeDropSprite = null;
    }
    playSparkle();
    playFanfare();
    playSnacaliaCollect();
  });

  // Global Escape: closes the topmost open modal first, then falls back to
  // clearing the "selected army" state if any. Single listener for the whole
  // in-game lifetime, removed on unmount.
  if (escapeHandler) document.removeEventListener('keydown', escapeHandler);
  escapeHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    const overlays = document.querySelectorAll('.modal-overlay');
    if (overlays.length > 0) {
      // Remove the topmost (last appended) overlay only.
      overlays[overlays.length - 1]?.remove();
      e.preventDefault();
      return;
    }
    if (castleShotMode !== null) {
      clearCastleShotMode();
      if (currentCtx) {
        const gh = document.getElementById('game-hud') as HTMLDivElement | null;
        if (gh) renderHud(gh, currentCtx);
      }
      e.preventDefault();
      return;
    }
    if (selectedArmyRegion !== null) {
      clearArmySelection();
      // Force a HUD re-render so the side panel reflects the deselection.
      if (currentCtx) {
        const gh = document.getElementById('game-hud') as HTMLDivElement | null;
        if (gh) renderHud(gh, currentCtx);
      }
      e.preventDefault();
      return;
    }
    // Finally: close the region info panel if it's open.
    if (selectedRegionId !== null) {
      selectedRegionId = null;
      if (currentCtx) {
        const gh = document.getElementById('game-hud') as HTMLDivElement | null;
        if (gh) renderHud(gh, currentCtx);
      }
      e.preventDefault();
    }
  };
  document.addEventListener('keydown', escapeHandler);

  currentCtx = ctx;
  // Remember which faction is the local player's so getEffectiveRecipe()
  // knows when to honour the cosmetic loadout. We refresh this on every
  // mount (re-join / spectator → playing transition) in case the slot
  // changed. ArraySchema doesn't expose `.find` in its TS types — use a
  // plain index loop.
  localFactionId = null;
  if (auth.user) {
    for (let i = 0; i < ctx.state.participants.length; i++) {
      const p = ctx.state.participants[i];
      if (p && p.id === auth.user.id) {
        localFactionId = p.factionId ?? null;
        break;
      }
    }
  }
  applyOwnership(renderer, ctx);
  applyEntities(renderer, ctx);
  // Wire the chat subsystem now that we have the room + user id. The
  // chat module is idempotent on attach so this is safe to call again
  // on reconnect.
  if (auth.user) attachChat(ctx.room, auth.user.id);
  renderHud(gameHud, ctx);
}

/** Payload contracts for the server-side broadcasts (see lobby_room.ts). */
interface ActionMovePayload {
  from: number;
  to: number;
  /** Legacy total — server still sends this for older clients. */
  warriors: number;
  /** Full composition of the moving stack — server broadcasts `units` so the
   *  ghost sprite can render the actual unit mix instead of a generic warrior. */
  units?: {
    warriors: number;
    archers: number;
    berserkers: number;
    crossbowmen: number;
    paladins: number;
    heroes: number;
    kamikazes: number;
  };
  outcome: 'reinforce' | 'claim' | 'attacker_wins' | 'defender_wins' | 'draw';
  attackerFactionId: string;
  defenderFactionId: string | null;
  /** True only if the defender had units at the start of the fight. */
  defenderHadUnits: boolean;
}
interface ActionRangedPayload {
  from: number;
  to: number;
  attackerFactionId: string;
  defenderFactionId: string;
  attackerArchers: number;
  attackerCrossbowmen: number;
  defendersAnnihilated: boolean;
  /** Optional discriminator — `'cannonball'` for castle shots, otherwise
   *  treated as the default arrow volley. */
  kind?: 'cannonball' | 'arrows';
}

export function updateInGame(ctx: InGameContext): void {
  if (!renderer) return;
  currentCtx = ctx;
  applyOwnership(renderer, ctx);
  applyEntities(renderer, ctx);
  // Defensive snacalia-drop cleanup: if the server has cleared the
  // drop (state.dropRegionId === -1) but our local sprite reference
  // is still alive, drop it now. Covers the race where the
  // `action.drop_collect` broadcast was missed (reconnection,
  // network glitch) or where the drop expired naturally.
  if (ctx.state.dropRegionId === -1 && activeDropSprite) {
    renderer.clearDrop(activeDropSprite);
    activeDropSprite = null;
  }
  // Detect turn transitions: if the active turn index changed, slide the
  // camera onto the active player's starting castle so everyone sees who
  // is playing without having to read the HUD.
  if (
    ctx.state.phase === 'playing' &&
    ctx.state.currentTurnIndex !== lastTurnIndexSeen
  ) {
    // Skip the very first observation — `lastTurnIndexSeen` starts at -1,
    // so we'd otherwise play the "your turn" chime on first mount even
    // when it's an enemy's turn.
    const firstObservation = lastTurnIndexSeen === -1;
    lastTurnIndexSeen = ctx.state.currentTurnIndex;
    // Auto-follow respects the player preference (toggle in the leaderboard
    // panel). User-initiated focuses (clicking a slot / leaderboard row)
    // always tween regardless of the setting.
    if (cameraFollowEnabled) focusActivePlayerCastle(ctx);
    // Audio cue: when the active player is ME (and it isn't the initial
    // mount), play the "your turn!" chime so I don't have to watch the
    // banner to notice.
    if (!firstObservation) {
      const active = findParticipantByTurnOrder(ctx.state, ctx.state.currentTurnIndex);
      const me = findMyParticipant(ctx.state);
      if (active && me && active.id === me.id) {
        playMyTurn();
      }
    }
  }
  const gameHud = document.getElementById('game-hud') as HTMLDivElement | null;
  if (gameHud) renderHud(gameHud, ctx);
}

/**
 * Slide the camera onto a player's highest-tier remaining castle. Falls back
 * to the starting castle if none are tagged. Used by the classement panel's
 * row-click handler and the per-turn focus.
 */
function focusOnPlayer(ctx: InGameContext, p: Participant): void {
  if (!renderer || !cachedMap) return;
  const idx = findParticipantIndex(ctx.state, p);
  if (idx < 0) return;
  // Look for the BEST castle this player still owns (highest tier).
  let bestRegion = -1;
  let bestTier = 0;
  for (let r = 0; r < ctx.state.regionBuilding.length; r++) {
    if (ctx.state.regionOwners[r] !== idx) continue;
    const b = ctx.state.regionBuilding[r] ?? 0;
    const tier = b === 4 ? 3 : b === 3 ? 2 : b === 2 ? 1 : 0;
    if (tier > bestTier) { bestTier = tier; bestRegion = r; }
  }
  // Fallback : the starting castle position (may have changed owners).
  if (bestRegion < 0) {
    const start = ctx.state.castles[idx];
    if (start !== undefined && start >= 0 && ctx.state.regionOwners[start] === idx) {
      bestRegion = start;
    }
  }
  // Last resort : any region the player still owns.
  if (bestRegion < 0) {
    for (let r = 0; r < ctx.state.regionOwners.length; r++) {
      if (ctx.state.regionOwners[r] === idx) { bestRegion = r; break; }
    }
  }
  if (bestRegion < 0) return; // player has nothing left
  const region = cachedMap.regions[bestRegion];
  if (!region) return;
  renderer.tweenCameraTo(region.centroid[0], region.centroid[1], 600);
}

/** Slide the camera onto the active player's main castle. */
function focusActivePlayerCastle(ctx: InGameContext): void {
  let active: Participant | undefined;
  for (let i = 0; i < ctx.state.participants.length; i++) {
    const p = ctx.state.participants[i];
    if (p && p.turnOrder === ctx.state.currentTurnIndex) { active = p; break; }
  }
  if (!active) return;
  focusOnPlayer(ctx, active);
}

/** Resolve a factionId to its 0xRRGGBB color, falling back to white. */
function factionColor(ctx: InGameContext, factionId: string | null): number {
  if (!factionId) return 0xffffff;
  const f = ctx.factions.find((x) => x.id === factionId);
  return f ? parseHexColor(f.color) : 0xffffff;
}

/**
 * Render a move broadcast: a ghost army flies from source to destination,
 * and (depending on outcome) a confetti burst pops at the destination in the
 * loser's banner color — visually answering "what happened?" at a glance.
 *
 * outcome → animation:
 *   reinforce / claim      → ghost only (peaceful arrival)
 *   attacker_wins          → ghost + burst in defender's color
 *   defender_wins          → ghost + burst in attacker's color
 *   draw                   → ghost + both bursts (smaller)
 */
async function onActionMoveBroadcast(
  ctx: InGameContext,
  p: ActionMovePayload,
): Promise<void> {
  if (!cachedMap || !renderer) return;
  const fromR = cachedMap.regions[p.from];
  const toR = cachedMap.regions[p.to];
  if (!fromR || !toR) return;
  // Use the actual composition to pick the ghost texture so the moving
  // sprite reflects the highest-tier unit in the stack (paladin / hero /
  // crossbowman / archer / berserker / warrior). Falls back to warrior if
  // the server payload lacks `units` (older server build) or the stack
  // is empty.
  const composition = p.units ?? { warriors: p.warriors, archers: 0, berserkers: 0, crossbowmen: 0, paladins: 0, heroes: 0, kamikazes: 0 };
  const dominantType = dominantUnitType(composition) ?? 'warrior';
  const tex = await getOrBuildArmyTexture(p.attackerFactionId, dominantType);
  if (!tex) return;
  // If the attacker isn't me, slide the camera onto the action so the
  // player sees who's doing what without having to scan the map. Focus the
  // midpoint between source and destination so both fit in view.
  const me = findMyParticipant(ctx.state);
  if (!me || me.factionId !== p.attackerFactionId) {
    const mx = (fromR.centroid[0] + toR.centroid[0]) / 2;
    const mz = (fromR.centroid[1] + toR.centroid[1]) / 2;
    tweenCameraIfFollowing(mx, mz, 500);
  }
  // Hide whatever sits at the destination for the duration of the ghost
  // Hide the destination slot for the entire flight (and through the clash
  // for combats with a real defender). The player should see ONLY the
  // moving ghost during the flight — never an existing army at the
  // destination overlapping it.
  //
  //   • reinforce → hide the friendly garrison for the flight.
  //   • claim → no entity exists yet; the pending hide is consumed by the
  //     freshly-created entity (state patch) so it starts invisible and
  //     fades in once the ghost lands.
  //   • combat (defender_wins / attacker_wins / draw with units present)
  //     → hide the defender for the flight + 2 s clash; the clash sprites
  //     own the slot during that window and the eventual attacker entity
  //     fades in clean afterwards.
  //   • combat against an empty enemy region → flight-length hide is
  //     enough (no clash plays).
  const GHOST_FLIGHT_MS = 700;
  const isCombat = p.outcome === 'attacker_wins' || p.outcome === 'defender_wins' || p.outcome === 'draw';
  const showClash = isCombat && p.defenderHadUnits;
  const CLASH_MS = 2000;
  const hideDurationMs = showClash ? GHOST_FLIGHT_MS + CLASH_MS : GHOST_FLIGHT_MS;
  renderer?.hideArrivingEntity(p.to, hideDurationMs);
  // ── Colour-hold so the conquest result isn't spoiled ──────────────
  // When the attacker WINS a real battle (defender had units), the
  // server has already flipped regionOwners[to] to the attacker — so
  // the state patch would recolour the tile to the attacker's colour
  // instantly, telling the player the outcome before the clash even
  // plays out. Hold the OLD (defender's) colour on the destination
  // until the clash resolves, then let it recolour naturally. Only
  // applies when ownership actually changed hands (a real defender
  // existed) — claims / reinforces / defender_wins don't need it.
  if (p.outcome === 'attacker_wins' && p.defenderHadUnits && p.defenderFactionId) {
    const oldColor = factionColor(ctx, p.defenderFactionId);
    const holdMs = GHOST_FLIGHT_MS + CLASH_MS + 250;
    pendingRecolorHolds.set(p.to, { color: oldColor, until: performance.now() + holdMs });
    // Re-paint immediately so even if the state patch already ran
    // (recoloured to attacker), we revert to the old colour for the
    // hold window. The race between onStateChange + onMessage is thus
    // covered either way.
    if (renderer && currentCtx) applyOwnership(renderer, currentCtx);
    // After the hold elapses, drop it + repaint so the new colour
    // appears in sync with the death FX, even if no further state
    // patch arrives to trigger updateInGame. The colour WAVE + paw
    // stamp fire here too so the new colour sweeps in dynamically
    // (instead of snapping) right as the battle resolves.
    const newColor = factionColor(ctx, p.attackerFactionId);
    setTimeout(() => {
      pendingRecolorHolds.delete(p.to);
      if (renderer && currentCtx) {
        // Wave radiates FROM the conquering source region's centroid
        // (always an adjacent tile). The renderer falls back to the
        // captured region's own centre if origin is undefined.
        renderer.spawnCaptureWave(p.to, newColor, [fromR.centroid[0], fromR.centroid[1]]);
        renderer.spawnPawStamp([toR.centroid[0], toR.centroid[1]], newColor);
        applyOwnership(renderer, currentCtx);
      }
    }, holdMs);
  } else if (p.outcome === 'attacker_wins') {
    // Conquest of an EMPTY enemy tile (no defending units) — no clash,
    // so no colour hold. Still play the wave + stamp immediately for
    // consistency, radiating from the source region.
    const newColor = factionColor(ctx, p.attackerFactionId);
    setTimeout(() => {
      if (renderer) {
        renderer.spawnCaptureWave(p.to, newColor, [fromR.centroid[0], fromR.centroid[1]]);
        renderer.spawnPawStamp([toR.centroid[0], toR.centroid[1]], newColor);
      }
    }, GHOST_FLIGHT_MS);
  }
  // Ghost ≈ same scale as the army sprite (45), hops higher than wobble
  // (~28), 700ms feels brisk but readable.
  const attackerColor = factionColor(ctx, p.attackerFactionId);
  void renderer.spawnGhost({
    texture: tex,
    fromPosition: [fromR.centroid[0], fromR.centroid[1]],
    toPosition: [toR.centroid[0], toR.centroid[1]],
    aspect: 480 / 260,
    scale: 50,
    duration: 700,
    hopHeight: 28,
    yOffset: 4,
    // Kingdom-coloured dust pixels kicked up under the marching army.
    trailColor: attackerColor,
  });
  const at: [number, number] = [toR.centroid[0], toR.centroid[1]];
  const defenderColor = factionColor(ctx, p.defenderFactionId);
  // Audio cue at launch: every army move starts with a soft "coussinet"
  // paw step. Combat outcomes overlay a hiss on top so the player still
  // hears something appropriately tense when units clash.
  playPawStep();
  if (p.outcome === 'reinforce') {
    // Friendly stacking — no follow-up sound (paw step is enough).
  } else if (p.outcome === 'claim') {
    // Claiming empty land — leave the paw step alone, no extra cue.
  } else {
    // Combat — overlay the hiss right after the step so the cute opener
    // doesn't soften the "fight is starting" intent.
    setTimeout(() => playHiss(), 110);
  }
  // Combat with a defender alive → 2-second side-by-side melee clash
  // right after the ghost lands. Spawns two sprites that lunge at each
  // other with periodic impact bursts. Death FX + fanfare are pushed
  // back so they fire AFTER the clash resolves.
  if (showClash && p.defenderFactionId) {
    const defenderFactionId = p.defenderFactionId;
    void (async (): Promise<void> => {
      const attTex = await getOrBuildArmyTexture(p.attackerFactionId, dominantType);
      const defTex = await getOrBuildArmyTexture(defenderFactionId, 'warrior');
      if (!attTex || !defTex || !renderer) return;
      setTimeout(() => {
        renderer?.spawnMeleeClash({
          at,
          attackerTexture: attTex,
          defenderTexture: defTex,
          attackerColor,
          defenderColor,
          durationMs: CLASH_MS,
        });
        // Periodic hisses during the clash so the combat audio sustains.
        setTimeout(() => playHiss(), 400);
        setTimeout(() => playHiss(), 1100);
        setTimeout(() => playHiss(), 1700);
      }, GHOST_FLIGHT_MS);
    })();
  }

  // Outcome-specific resolution. For combat outcomes, fire the death FX +
  // sound stings AFTER the clash (delay = GHOST_FLIGHT_MS + CLASH_MS for
  // combat, original 550 ms otherwise).
  const resolveDelay = showClash ? GHOST_FLIGHT_MS + CLASH_MS : 550;
  if (p.outcome === 'attacker_wins') {
    if (p.defenderHadUnits) {
      setTimeout(() => renderer?.spawnImpactBurst(at, defenderColor, { count: 24, spread: 1.4 }), resolveDelay);
      if (p.defenderFactionId) {
        void spawnDeathFor(p.defenderFactionId, at, resolveDelay + 50);
      }
      setTimeout(() => playPlouf(), resolveDelay + 70);
      setTimeout(() => playFanfare(), resolveDelay + 300);
      // Faction-tinted "we won!" growl, layered on top of the generic
      // fanfare so the winning kingdom has its own voice in the mix.
      setTimeout(() => playBattleWin(p.attackerFactionId), resolveDelay + 320);
      // Defender's wail.
      if (p.defenderFactionId) {
        setTimeout(() => playBattleLose(p.defenderFactionId!), resolveDelay + 140);
      }
    } else {
      // Empty enemy region taken — fanfare only, no death plouf.
      setTimeout(() => playFanfare(), 700);
      setTimeout(() => playBattleWin(p.attackerFactionId), 740);
    }
  } else if (p.outcome === 'defender_wins') {
    setTimeout(() => renderer?.spawnImpactBurst(at, attackerColor, { count: 24, spread: 1.4 }), resolveDelay);
    void spawnDeathFor(p.attackerFactionId, at, resolveDelay + 50);
    setTimeout(() => playPlouf(), resolveDelay + 70);
    // Defender holds — they cheer, attacker grieves.
    if (p.defenderFactionId) {
      setTimeout(() => playBattleWin(p.defenderFactionId!), resolveDelay + 300);
    }
    setTimeout(() => playBattleLose(p.attackerFactionId), resolveDelay + 140);
  } else if (p.outcome === 'draw') {
    setTimeout(() => {
      renderer?.spawnImpactBurst(at, attackerColor, { count: 14, spread: 1.2 });
      renderer?.spawnImpactBurst(at, defenderColor, { count: 14, spread: 1.2 });
    }, resolveDelay);
    void spawnDeathFor(p.attackerFactionId, at, resolveDelay + 50);
    if (p.defenderFactionId) {
      void spawnDeathFor(p.defenderFactionId, at, resolveDelay + 100);
    }
    setTimeout(() => playPlouf(), resolveDelay + 70);
  }
}

/** Helper: build the death FX using the faction's body texture so the
 *  squashed corpse reads as "that kingdom's army." */
async function spawnDeathFor(
  factionId: string,
  at: [number, number],
  delayMs: number,
): Promise<void> {
  if (!renderer) return;
  const tex = await getOrBuildArmyTexture(factionId, 'warrior');
  if (!tex) return;
  void renderer.spawnDeathFx(at, tex, {
    bodyScale: 45,
    bodyAspect: 480 / 260,
    delayMs,
  });
}

/**
 * Render a ranged-attack broadcast: a volley of small projectiles flies from
 * source to destination, then a defender-colored burst at impact. The number
 * of projectiles is capped at 8 — it's about feel, not 1:1 simulation.
 */
function onActionRangedBroadcast(ctx: InGameContext, p: ActionRangedPayload): void {
  if (!cachedMap || !renderer) return;
  const fromR = cachedMap.regions[p.from];
  const toR = cachedMap.regions[p.to];
  if (!fromR || !toR) return;
  const fromPos: [number, number] = [fromR.centroid[0], fromR.centroid[1]];
  const toPos: [number, number] = [toR.centroid[0], toR.centroid[1]];
  const attackerColor = factionColor(ctx, p.attackerFactionId);
  const defenderColor = factionColor(ctx, p.defenderFactionId);
  const me = findMyParticipant(ctx.state);
  if (!me || me.factionId !== p.attackerFactionId) {
    const mx = (fromPos[0] + toPos[0]) / 2;
    const mz = (fromPos[1] + toPos[1]) / 2;
    tweenCameraIfFollowing(mx, mz, 500);
  }

  // Castle shot — a RAIN of arrows falling from the sky onto the target.
  // Replaces the previous cannonball: matches "the castle volleys archers
  // skyward and they rain down" intuition, and reuses the arrow sprite +
  // physics already wired up for ranged attacks.
  if (p.kind === 'cannonball') {
    playWhoosh();
    renderer.spawnArrowRain(toPos, attackerColor, { count: 14, spread: 22, stagger: 55 });
    if (p.defendersAnnihilated) {
      // Plouf + death FX timed to land roughly when the last arrows hit.
      setTimeout(() => playPlouf(), 1100);
      void spawnDeathFor(p.defenderFactionId, toPos, 1150);
    } else {
      // Survived the volley — knock the army around so the player sees
      // it took the hit. Arrows fall from the sky here so "from" for
      // the knock is just up — we shake in place by passing the same
      // position as origin (the knock helper handles len ≈ 0).
      setTimeout(() => renderer?.knockEntity(p.to, toPos[0], toPos[1], { strength: 4 }), 1000);
    }
    return;
  }

  // Default = arrow volley (archer / crossbow attacks).
  playWhoosh();
  if (p.defendersAnnihilated) {
    setTimeout(() => playPlouf(), 700);
  }
  const totalShots = Math.min(8, Math.max(1, p.attackerArchers + p.attackerCrossbowmen));
  for (let i = 0; i < totalShots; i++) {
    setTimeout(() => {
      const jitter = (): number => (Math.random() - 0.5) * 26;
      // size: 22 — visible from a typical zoom without overlapping the
      // whole tile. The previous `size: 3` made each arrow effectively
      // invisible (3 world units ≈ a few pixels) which is why the
      // player saw only the impact burst and never the volley itself.
      renderer?.spawnProjectile(
        [fromPos[0] + jitter(), fromPos[1] + jitter()],
        [toPos[0] + jitter() * 0.6, toPos[1] + jitter() * 0.6],
        attackerColor,
        { duration: 320 + Math.random() * 140, size: 22 },
      );
    }, i * 70);
  }
  const impactAt = totalShots * 70 + 360;
  setTimeout(
    () => renderer?.spawnImpactBurst(toPos, defenderColor, { count: 20, spread: 1.3 }),
    impactAt,
  );
  // Knock the defender sprite — fired BOTH when they survive (only
  // visible signal) AND right before the death FX (sells the "they got
  // hit hard and then died" beat instead of an immediate puff).
  setTimeout(
    () => renderer?.knockEntity(p.to, fromPos[0], fromPos[1], { strength: 6 }),
    impactAt,
  );
  if (p.defendersAnnihilated) {
    void spawnDeathFor(p.defenderFactionId, toPos, impactAt + 60);
  }
}

export function unmountInGame(): void {
  if (unsubClick) {
    unsubClick();
    unsubClick = null;
  }
  if (unsubEmptyClick) {
    unsubEmptyClick();
    unsubEmptyClick = null;
  }
  if (unsubRightClick) {
    unsubRightClick();
    unsubRightClick = null;
  }
  if (unsubEntityClick) {
    unsubEntityClick();
    unsubEntityClick = null;
  }
  if (unsubActionMove) {
    unsubActionMove();
    unsubActionMove = null;
  }
  if (unsubActionRanged) {
    unsubActionRanged();
    unsubActionRanged = null;
  }
  if (unsubActionDropSpawn) { unsubActionDropSpawn(); unsubActionDropSpawn = null; }
  if (unsubActionDropCollect) { unsubActionDropCollect(); unsubActionDropCollect = null; }
  if (unsubMissionDrawn) { unsubMissionDrawn(); unsubMissionDrawn = null; }
  if (unsubMissionComplete) { unsubMissionComplete(); unsubMissionComplete = null; }
  activeDropSprite = null;
  lastMissionIdSeen = null;
  detachChat();
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler);
    escapeHandler = null;
  }
  selectedArmyRegion = null;
  selectedRegionId = null;
  castleShotMode = null;
  currentCtx = null;
  // Clear the local-faction memo so a future game with a different faction
  // doesn't keep the previous user's loadout overrides.
  localFactionId = null;
  endedSoundPlayed = false;
  victoryAutoLeaveScheduled = false;
  // Drop any leftover confetti so it doesn't reappear at next mount.
  document.querySelectorAll('.gh-victory-confetti').forEach((n) => n.remove());
  pendingRecolorHolds.clear();
  lastTurnIndexSeen = -1;
  // Allow a fresh WebGL init attempt on the next mount (e.g. after
  // the user has rebooted the GPU process / refreshed the page).
  webglFailureShown = false;
  // Forget which turn we already shown the MIAOU toast for so a new game
  // gets a fresh announcement when it becomes the local player's turn.
  lastMiaouToastKey = null;
  document.querySelectorAll('.gh-miaou-toast').forEach((n) => n.remove());
  // Nuke every transient overlay we may have appended to body
  // throughout a match — without these the dice ceremony / mid-game
  // modals / context menus / confetti / mission toasts / floating
  // mission disintegrate clones stay on screen after the player
  // navigates to /profile or /.
  cleanupDiceCeremony();
  document.querySelectorAll('.gh-slot-ctxmenu').forEach((n) => n.remove());
  document.querySelectorAll('.modal-overlay').forEach((n) => n.remove());
  document.querySelectorAll('.dice-confetti-container').forEach((n) => n.remove());
  document.querySelectorAll('.gh-mission-toast').forEach((n) => n.remove());
  document.querySelectorAll('.gh-mission-disintegrating').forEach((n) => n.remove());
  // Tear down the tutorial overlay too (no-op when no tutorial).
  detachTutorialOverlay();
  // Music stops with the match — the lobby / profile / leaderboard
  // pages are silent. Next mountInGame resumes via ensureMusicPlaying.
  pauseMusic();
  // Release the WebGL renderer + canvas so its GL context slot is
  // returned to the browser pool. Without this, hopping between
  // games (or restarting a tutorial) leaks one canvas + one GL
  // context per visit until the browser runs out (~16 slots) and
  // refuses to grant new contexts.
  if (renderer) {
    try { renderer.dispose(); } catch { /* swallow */ }
    renderer = null;
    mountedSeed = null;
  }
  // Belt + suspenders: nuke any orphan canvas in #app from a
  // previous mount that died before disposing.
  const appEl = document.getElementById('app');
  if (appEl) {
    appEl.querySelectorAll('canvas').forEach((c) => {
      try {
        const gl = (c as HTMLCanvasElement).getContext('webgl2')
          ?? (c as HTMLCanvasElement).getContext('webgl');
        (gl as WebGLRenderingContext | null)
          ?.getExtension('WEBGL_lose_context')
          ?.loseContext();
      } catch { /* noop */ }
      c.remove();
    });
  }
  // Hide canvas + HUD overlay. We KEEP the renderer alive — it's expensive to
  // recreate, and on reconnection / new game we want to reuse it.
  const app = document.getElementById('app') as HTMLDivElement | null;
  const gameHud = document.getElementById('game-hud') as HTMLDivElement | null;
  if (app) app.style.display = 'none';
  if (gameHud) {
    gameHud.style.display = 'none';
    gameHud.innerHTML = '';
  }
  // Restore the top navbar that we hid on mount.
  const navbar = document.getElementById('navbar');
  if (navbar) navbar.style.display = '';
  document.body.classList.remove('in-game-no-navbar');
}

function buildAdjacency(map: MapData): Map<number, Set<number>> {
  const adj = new Map<number, Set<number>>();
  for (const cell of map.cells) {
    if (cell.isOcean) continue;
    let mine = adj.get(cell.regionId);
    if (!mine) {
      mine = new Set<number>();
      adj.set(cell.regionId, mine);
    }
    for (const ni of cell.neighbors) {
      const n = map.cells[ni];
      if (!n || n.isOcean) continue;
      if (n.regionId === cell.regionId) continue;
      mine.add(n.regionId);
    }
  }
  return adj;
}

/** Mirror of the server-side helper: regionIds grouped by continent. */
function buildContinentRegions(map: MapData): Map<number, Set<number>> {
  const out = new Map<number, Set<number>>();
  const seen = new Set<number>();
  for (const cell of map.cells) {
    if (cell.isOcean) continue;
    if (cell.regionId < 0) continue;
    if (seen.has(cell.regionId)) continue;
    seen.add(cell.regionId);
    let bucket = out.get(cell.continentId);
    if (!bucket) {
      bucket = new Set<number>();
      out.set(cell.continentId, bucket);
    }
    bucket.add(cell.regionId);
  }
  return out;
}

// ---------------------------------------------------------------------------

function generateMapForState(state: LobbyState): MapData {
  return generateMap({
    width: state.mapWidth || 1920,
    height: state.mapHeight || 1080,
    seed: state.seed,
    cellSpacing: 36,
    relaxationIterations: 2,
    cellsPerRegion: 5,
    maxRegions: state.mapMaxRegions || 100,
    continentCount: state.mapContinentCount || 9,
    seaLevel: 0,
    edgeDisplacement: 0.42,
  });
}

function applyOwnership(r: MapRenderer, ctx: InGameContext): void {
  const now = performance.now();
  // Drop expired colour holds.
  for (const [regionId, hold] of pendingRecolorHolds) {
    if (hold.until <= now) pendingRecolorHolds.delete(regionId);
  }
  const overrides = new Map<number, number>();
  ctx.state.regionOwners.forEach((ownerIdx, regionId) => {
    // Active colour hold → keep painting the OLD owner's colour until
    // the battle animation finishes, so the conquest result isn't
    // spoiled by an instant recolour.
    const hold = pendingRecolorHolds.get(regionId);
    if (hold && hold.until > now) {
      overrides.set(regionId, hold.color);
      return;
    }
    if (ownerIdx < 0) return;
    const p = ctx.state.participants[ownerIdx];
    if (!p) return;
    const f = ctx.factions.find((x) => x.id === p.factionId);
    if (!f) return;
    overrides.set(regionId, parseHexColor(f.color));
  });
  r.setOwnership(overrides);
}

type ServerUnitType = 'warrior' | 'archer' | 'berserker' | 'crossbowman' | 'paladin' | 'hero' | 'kamikaze';

/**
 * Mirror of `UNIT_DEFS` in server/src/game/units.ts. Kept here so the UI can
 * preview attack/defence numbers without a round-trip. KEEP IN SYNC.
 */
const UNIT_STATS: Record<ServerUnitType, { hp: number; damage: number; resistance: number }> = {
  warrior:     { hp: 10, damage: 3, resistance: 1 },
  archer:      { hp: 8,  damage: 3, resistance: 1 },
  berserker:   { hp: 10, damage: 6, resistance: 1 },
  crossbowman: { hp: 9,  damage: 4, resistance: 1 },
  paladin:     { hp: 18, damage: 3, resistance: 3 },
  hero:        { hp: 22, damage: 3, resistance: 4 },
  kamikaze:    { hp: 6,  damage: 25, resistance: 0 },
};

/**
 * Priority order used for the "main sprite" on the map. Higher index = higher
 * priority. Hero is the most prestigious, warrior the most basic. The map
 * displays the highest-priority type PRESENT in the stack as the main
 * billboard; up to 5 smaller satellites surround it for the other types.
 */
const UNIT_PRIORITY: ServerUnitType[] = [
  'warrior', 'archer', 'crossbowman', 'berserker', 'paladin', 'hero', 'kamikaze',
];

interface UnitsLike {
  warriors: number; archers: number; berserkers: number;
  crossbowmen: number; paladins: number; heroes: number;
  kamikazes: number;
}

/** Per-unit-type count, but keyed by the SINGULAR type names. */
function countsByType(u: UnitsLike): Record<ServerUnitType, number> {
  return {
    warrior: u.warriors, archer: u.archers, berserker: u.berserkers,
    crossbowman: u.crossbowmen, paladin: u.paladins, hero: u.heroes,
    kamikaze: u.kamikazes,
  };
}

/** Total head count regardless of type. */
function totalUnits(u: UnitsLike): number {
  return u.warriors + u.archers + u.berserkers + u.crossbowmen +
    u.paladins + u.heroes + u.kamikazes;
}

/** Cumulative damage per combat round (mirrors server's `totalDamage`). */
function armyAttack(u: UnitsLike): number {
  const c = countsByType(u);
  let dmg = 0;
  for (const t of UNIT_PRIORITY) dmg += c[t] * UNIT_STATS[t].damage;
  return dmg;
}

/** Cumulative HP across the stack (mirrors server's `totalHp`). */
function armyHp(u: UnitsLike): number {
  const c = countsByType(u);
  let hp = 0;
  for (const t of UNIT_PRIORITY) hp += c[t] * UNIT_STATS[t].hp;
  return hp;
}

/**
 * Pure client-side combat preview — MUST stay byte-identical to the server's
 * `resolveCombat` in server/src/game/units.ts. We replicate the same fixed-
 * point HP-ratio formula here so the "annihilation preview" the player sees
 * matches what actually happens on the server.
 */
function simulateCombat(attacker: UnitsLike, defender: UnitsLike, opts: { defenderMinHp?: number; defenderHpMult?: number } = {}): {
  attackerSurvivors: UnitsLike;
  defenderSurvivors: UnitsLike;
  winner: 'attacker' | 'defender' | 'draw';
} {
  const EMPTY: UnitsLike = { warriors: 0, archers: 0, berserkers: 0, crossbowmen: 0, paladins: 0, heroes: 0, kamikazes: 0 };
  const hpMult = opts.defenderHpMult ?? 1.0;
  const minHp = opts.defenderMinHp ?? 0;
  const aHp0 = armyHp(attacker);
  const dHpBase = armyHp(defender);
  const dHp0 = Math.max(dHpBase * hpMult, minHp);
  if (aHp0 === 0 && dHp0 === 0) return { attackerSurvivors: { ...EMPTY }, defenderSurvivors: { ...EMPTY }, winner: 'draw' };
  if (dHp0 === 0) return { attackerSurvivors: { ...attacker }, defenderSurvivors: { ...EMPTY }, winner: 'attacker' };
  if (aHp0 === 0) return { attackerSurvivors: { ...EMPTY }, defenderSurvivors: { ...defender }, winner: 'defender' };

  const aDmg = armyAttack(attacker);
  const dDmg = armyAttack(defender);
  let aHp = aHp0;
  let dHp = dHp0;
  for (let round = 0; round < 100; round++) {
    if (aHp <= 0 || dHp <= 0) break;
    aHp -= dDmg;
    dHp -= aDmg;
  }
  const aRatio = aHp > 0 ? aHp / aHp0 : 0;
  const dRatio = dHp > 0 ? dHp / dHp0 : 0;
  const scaleArmy = (a: UnitsLike, r: number): UnitsLike => r <= 0 ? { ...EMPTY } : {
    warriors: Math.floor(a.warriors * r),
    archers: Math.floor(a.archers * r),
    berserkers: Math.floor(a.berserkers * r),
    crossbowmen: Math.floor(a.crossbowmen * r),
    paladins: Math.floor(a.paladins * r),
    heroes: Math.floor(a.heroes * r),
    // Kamikazes are atomic — they die on combat, so survivors = 0 always.
    // The simulator zeroes them out post-resolve below; here we keep the
    // raw count so any drawing logic can show them mid-fight.
    kamikazes: a.kamikazes,
  };
  const attackerSurvivors = scaleArmy(attacker, aRatio);
  const defenderSurvivors = scaleArmy(defender, dRatio);
  let winner: 'attacker' | 'defender' | 'draw';
  if (totalUnits(attackerSurvivors) > 0 && totalUnits(defenderSurvivors) === 0) winner = 'attacker';
  else if (totalUnits(defenderSurvivors) > 0 && totalUnits(attackerSurvivors) === 0) winner = 'defender';
  else if (totalUnits(attackerSurvivors) === 0 && totalUnits(defenderSurvivors) === 0) winner = 'draw';
  else winner = 'defender';
  return { attackerSurvivors, defenderSurvivors, winner };
}

/**
 * One row of the combat preview. Shows each unit type "before → after"; lost
 * units (after < before) are crossed out in red, survivors stay bright.
 */
function simRow(label: string, survivors: UnitsLike, original: UnitsLike, _factionId: string): HTMLElement {
  const row = el('div', 'action-preview-row');
  row.appendChild(el('span', 'action-preview-label', label));
  const cells = el('div', 'action-preview-cells');
  const types: Array<[ServerUnitType, string, number, number]> = [
    ['warrior', 'G', original.warriors, survivors.warriors],
    ['archer', 'A', original.archers, survivors.archers],
    ['berserker', 'B', original.berserkers, survivors.berserkers],
    ['crossbowman', 'X', original.crossbowmen, survivors.crossbowmen],
    ['paladin', 'P', original.paladins, survivors.paladins],
    ['hero', 'H', original.heroes, survivors.heroes],
  ];
  for (const [, letter, orig, surv] of types) {
    if (orig === 0) continue;
    const cell = el('span', 'action-preview-cell');
    cell.textContent = `${letter} ${surv}/${orig}`;
    if (surv === 0) cell.classList.add('dead');
    else if (surv < orig) cell.classList.add('wounded');
    else cell.classList.add('intact');
    cells.appendChild(cell);
  }
  row.appendChild(cells);
  return row;
}

/**
 * Pick the MAIN unit type to show on the map. Strict priority order:
 *   Hero > Paladin > Berserker > Crossbowman > Archer > Warrior
 *
 * A hero in the stack always wins, regardless of how many warriors stand
 * beside him — it's the most legible visual cue ("there's a hero there").
 * Returns null only if the stack is empty.
 */
function dominantUnitType(u: UnitsLike): ServerUnitType | null {
  const counts = countsByType(u);
  for (let i = UNIT_PRIORITY.length - 1; i >= 0; i--) {
    const t = UNIT_PRIORITY[i]!;
    if (counts[t] > 0) return t;
  }
  return null;
}

/**
 * Up to 5 satellite types (all the unit types PRESENT in the stack other than
 * the main one). Used to add small "badge" sprites around the main entity
 * so the player can read the composition at a glance.
 */
function satelliteTypes(u: UnitsLike, main: ServerUnitType | null): ServerUnitType[] {
  if (!main) return [];
  const out: ServerUnitType[] = [];
  const counts = countsByType(u);
  // Iterate from highest priority down so the layout is stable + meaningful.
  for (let i = UNIT_PRIORITY.length - 1; i >= 0; i--) {
    const t = UNIT_PRIORITY[i]!;
    if (t === main) continue;
    if (counts[t] > 0) out.push(t);
    if (out.length >= 5) break;
  }
  return out;
}

/**
 * Walk the synced state and produce map entities. Castles use the tier sprite
 * directly. Armies are pre-composited into a single texture (body + weapon)
 * via `getOrBuildArmyTexture` so the billboard shows both, anchored together.
 */
async function buildMapEntities(ctx: InGameContext): Promise<MapEntity[]> {
  if (!cachedMap) return [];
  const entities: MapEntity[] = [];

  // Buildings (castles and camps) — driven by state.regionBuilding now,
  // which is set per-region (no longer per-participant). This is what
  // enables multi-castle ownership: each region carries its own tier.
  const castleRegions = new Set<number>();
  // Buildings shrunk another 10 % (castle 65→59, camp 45→41) so they
  // sit a touch more discreetly on their tile and leave more room for
  // the army stack billboarded above them.
  const CASTLE_SCALE = 59;
  const CAMP_SCALE = 41;
  for (let r = 0; r < ctx.state.regionBuilding.length; r++) {
    const b = ctx.state.regionBuilding[r] ?? 0;
    if (b === 0) continue;
    const region = cachedMap.regions[r];
    if (!region) continue;
    if (b === 1) {
      // Camp — wooden chest sprite.
      entities.push({
        id: `camp-${r}`,
        kind: 'castle', // re-use 'castle' kind to share the entity-click path
        regionId: r,
        position: [region.centroid[0], region.centroid[1]],
        spriteUrl: '/sprites/camp.png',
        scale: CAMP_SCALE,
        yOffset: 4,
        renderOrder: 88,
      });
    } else {
      const tier = b === 4 ? 3 : b === 3 ? 2 : 1;
      castleRegions.add(r);
      entities.push({
        id: `castle-${r}`,
        kind: 'castle',
        regionId: r,
        position: [region.centroid[0], region.centroid[1]],
        spriteUrl: `/sprites/chateau_tier${tier}.png`,
        scale: CASTLE_SCALE,
        yOffset: 4,
        renderOrder: 90,
      });
    }
  }

  // Visual tint per army:
  //   - bright (0xffffff): army I can act on this turn (mine + my turn + not yet moved)
  //   - dimmed (0x4c4c4c): everyone else — clearly "invalid for action right now"
  // This gives the local player an instant read of "what can I touch?"
  const me = findMyParticipant(ctx.state);
  const myIdx = me ? findParticipantIndex(ctx.state, me) : -1;
  const isMyTurn = me ? me.turnOrder === ctx.state.currentTurnIndex : false;

  // Armies on EVERY land region with units, INCLUDING castle regions. On a
  // castle region the army stacks above the castle (yOffset ≈ castle height)
  // so both are visible — the castle as the seat, the army as the garrison.
  const armyJobs: Promise<MapEntity | null>[] = [];
  for (let r = 0; r < ctx.state.regionUnits.length; r++) {
    const u = ctx.state.regionUnits[r];
    if (!u) continue;
    const ownerIdx = ctx.state.regionOwners[r];
    if (ownerIdx === undefined || ownerIdx < 0) continue;
    const owner = ctx.state.participants[ownerIdx];
    if (!owner?.factionId) continue;
    const region = cachedMap.regions[r];
    if (!region) continue;
    const dom = dominantUnitType(u);
    if (!dom) continue;
    const factionId = owner.factionId;
    const onCastle = castleRegions.has(r);
    const armyRegionId = r;
    const stackTotal = totalUnits(u);
    // Only my own non-actionable units get dimmed. Enemy armies always render
    // in full colour so I can read the threat at a glance.
    const isMine = ownerIdx === myIdx;
    const myArmyIdle = isMine && (!isMyTurn || u.movedThisTurn || stackTotal === 0);
    const tint = myArmyIdle ? 0x4c4c4c : 0xffffff;
    const satellites = satelliteTypes(u, dom);
    // Pre-seed satellite positions in a ring around the main sprite. Uses the
    // regionId as a deterministic angular offset so the cluster stays put
    // between two patches (otherwise the badges would dance every refresh).
    armyJobs.push(
      (async (): Promise<MapEntity | null> => {
        const tex = await getOrBuildArmyTexture(factionId, dom);
        if (!tex) return null;
        // Build satellite descriptors in parallel.
        const sats = await Promise.all(
          satellites.map(async (st, i) => {
            const t = await getOrBuildArmyTexture(factionId, st);
            if (!t) return null;
            const baseAngle = (armyRegionId * 137.5) % 360;
            const angleDeg = baseAngle + (i / Math.max(1, satellites.length)) * 360;
            const angle = (angleDeg * Math.PI) / 180;
            const ringRadius = 18;
            return {
              texture: t,
              aspect: 480 / 260,
              offset: [
                Math.cos(angle) * ringRadius,
                Math.sin(angle) * ringRadius * 0.55, // squash on Z so circle reads as ellipse from above
              ] as [number, number],
            };
          }),
        );
        return {
          id: `army-${armyRegionId}`,
          kind: 'army',
          regionId: armyRegionId,
          position: [region.centroid[0], region.centroid[1]],
          texture: tex,
          aspect: 480 / 260,
          scale: 45,
          // Always flush with the platform: armies stand on the ground, not in
          // the air. Z-order is what keeps them visible in front of a castle.
          yOffset: 0,
          // Bigger renderOrder than the castle (90) and the terrain → drawn
          // in front of both, regardless of camera angle.
          renderOrder: 200,
          // Idle sway/squash animation — "ready for action".
          wobble: true,
          tint,
          satellites: sats.filter((s): s is NonNullable<typeof s> => s !== null),
        };
      })(),
    );
  }
  const armies = (await Promise.all(armyJobs)).filter((e): e is MapEntity => e !== null);
  entities.push(...armies);
  return entities;
}

function applyEntities(r: MapRenderer, ctx: InGameContext): void {
  void buildMapEntities(ctx).then((entities) => r.setMapEntities(entities));
  // Castle flags — one waving faction banner per castle (tier ≥ 1),
  // coloured by the region's owner. Recomputed on every patch so a
  // captured castle's flag flips to the new owner. Camps don't get a
  // flag (they're rough barbarian/forward outposts, not seats of power).
  const flags: Array<{ regionId: number; factionId: string; castleScale: number }> = [];
  const CASTLE_SCALE = 59; // mirror buildMapEntities
  for (let region = 0; region < ctx.state.regionBuilding.length; region++) {
    const b = ctx.state.regionBuilding[region] ?? 0;
    if (b < 2) continue; // 2/3/4 = castle tiers; 0=none, 1=camp
    const ownerIdx = ctx.state.regionOwners[region] ?? -1;
    if (ownerIdx < 0) continue;
    const owner = ctx.state.participants[ownerIdx];
    if (!owner?.factionId) continue;
    flags.push({ regionId: region, factionId: owner.factionId, castleScale: CASTLE_SCALE });
  }
  r.setCastleFlags(flags);
}

function parseHexColor(hex: string): number {
  return parseInt(hex.replace(/^#/, ''), 16);
}

/**
 * Pretty label for a region. Uses the server-supplied "ContinentName #N"
 * if available; otherwise falls back to "Coin #regionId" so old saves /
 * lobby snapshots still render something readable.
 */
function regionLabel(state: LobbyState, regionId: number): string {
  const lab = state.regionLabels[regionId];
  if (lab && lab.length > 0) return lab;
  return `Coin #${regionId + 1}`;
}

/** Convert "#aabbcc" + alpha to "rgba(170, 187, 204, alpha)". */
function hexAlpha(hex: string, alpha: number): string {
  const n = parseHexColor(hex);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Right-click on a region: if it's yours, has units, and your turn is active
 * AND the stack hasn't moved this turn → open the move/attack modal directly.
 * Otherwise it's a no-op (the user keeps the left-click "select & inspect"
 * affordance for everything else).
 */
function handleRegionRightClick(ctx: InGameContext, regionId: number): void {
  if (ctx.state.phase !== 'playing') return;
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  if (me.turnOrder !== ctx.state.currentTurnIndex) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0) return;
  if (ctx.state.regionOwners[regionId] !== myIdx) return;
  const u = ctx.state.regionUnits[regionId];
  if (!u) return;
  if (u.movedThisTurn) return;
  const total = u.warriors + u.archers + u.berserkers + u.crossbowmen + u.paladins + u.heroes;
  if (total <= 0) return;
  openMoveModal(ctx, regionId);
}

/**
 * Click on a castle / army sprite. If it's ours and has movable units, we
 * enter "select target" mode: adjacent regions get an orange outline, and the
 * NEXT region click (handleRegionClick) opens the move/attack modal with this
 * region as the source. Clicking the same army again clears the mode.
 */
function handleEntityClick(ctx: InGameContext, regionId: number): void {
  if (ctx.state.phase !== 'playing') return;
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  if (me.turnOrder !== ctx.state.currentTurnIndex) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0) return;

  // ── A) When an army is already selected, a click on ANY sprite (friend OR
  // enemy) should resolve as a target click. The entity-click router bypasses
  // the terrain raycast, so without this shortcut clicking directly on an
  // enemy army sprite would silently do nothing.
  if (selectedArmyRegion !== null && selectedArmyRegion !== regionId) {
    handleRegionClick(ctx, regionId);
    return;
  }

  // ── B) Otherwise the click is a "select my army" — only valid on my own
  // territories with at least one mobile unit.
  if (ctx.state.regionOwners[regionId] !== myIdx) return;
  const u = ctx.state.regionUnits[regionId];
  if (!u) return;
  if (u.movedThisTurn) return;
  // Any non-empty stack is selectable (move OR ranged), regardless of unit
  // mix — the click resolution chooses move vs. ranged based on the target.
  // Includes kamikazes so a kamikaze-only stack is still pickable.
  const total = totalUnits(u);
  if (total <= 0) return;

  if (selectedArmyRegion === regionId) {
    clearArmySelection();
  } else {
    selectedArmyRegion = regionId;
    refreshActionHighlights();
  }
  // Re-render the side panel so it reflects the current selection state.
  const gh = document.getElementById('game-hud') as HTMLDivElement | null;
  if (gh) renderHud(gh, ctx);
}

function clearArmySelection(): void {
  selectedArmyRegion = null;
  renderer?.setActionHighlights([]);
}

/**
 * Enter castle-shot targeting mode: highlights every enemy region within
 * range of the source castle and waits for the next map click. Mutually
 * exclusive with army selection — calling this clears any active selection.
 * Closing modes happens via Escape, right-click, or by clicking outside
 * the highlight set.
 */
function enterCastleShotMode(ctx: InGameContext, from: number, castleTier: number): void {
  selectedArmyRegion = null;
  selectedRegionId = null;
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0 || me.castleShots <= 0) return;
  const maxRange = castleTier === 3 ? 3 : 2;
  // BFS within `maxRange` hops (mirror of server logic). Collect:
  //   • inRange — every reachable region, regardless of owner. Highlighted
  //     so the player visually sees the cannon's reach even with no enemies.
  //   • targets — the subset that's enemy-owned (the actually-shootable cells).
  const visited = new Set<number>([from]);
  let frontier: number[] = [from];
  const inRange = new Set<number>();
  const targets = new Set<number>();
  for (let d = 0; d < maxRange; d++) {
    const next: number[] = [];
    for (const r of frontier) {
      const adj = adjacency.get(r);
      if (!adj) continue;
      for (const n of adj) {
        if (visited.has(n)) continue;
        visited.add(n);
        next.push(n);
        inRange.add(n);
        const owner = ctx.state.regionOwners[n] ?? -1;
        if (owner >= 0 && owner !== myIdx) targets.add(n);
      }
    }
    frontier = next;
  }
  castleShotMode = { from, range: maxRange, inRange, targets };
  refreshActionHighlights();
  // Force a HUD re-render so the region panel closes and a hint banner can show.
  forceHudRender();
}

function clearCastleShotMode(): void {
  castleShotMode = null;
  renderer?.setActionHighlights([]);
}

function refreshActionHighlights(): void {
  // Castle shot mode wins — highlight EVERY in-range cell (not just enemy
  // targets) so the player can see the cannon's reach. Click handling
  // (handleRegionClick) is what enforces "you can only shoot enemies".
  if (castleShotMode !== null) {
    // Include the source castle itself so it gets the same white wash
    // as the target zone — the player sees one continuous "action set"
    // (where I'm shooting FROM + where I can shoot TO).
    const list = [castleShotMode.from, ...castleShotMode.inRange];
    renderer?.setActionHighlights(list, 0xff4040);
    return;
  }
  if (selectedArmyRegion === null || !currentCtx) {
    renderer?.setActionHighlights([]);
    return;
  }
  // Use the owning kingdom's banner color for the pulsing outline so the
  // player instinctively links "this army's adjacents" to "this kingdom".
  let color = 0xff8a3c; // fallback orange
  const ownerIdx = currentCtx.state.regionOwners[selectedArmyRegion];
  if (ownerIdx !== undefined && ownerIdx >= 0) {
    const owner = currentCtx.state.participants[ownerIdx];
    if (owner) {
      const f = currentCtx.factions.find((x) => x.id === owner.factionId);
      if (f) color = parseHexColor(f.color);
    }
  }
  const adj = adjacency.get(selectedArmyRegion);
  // Include the source region itself so the active territory shares
  // the same white wash as its neighbours — same visual treatment for
  // "where I am" and "where I can go".
  const list = [selectedArmyRegion, ...(adj ? Array.from(adj) : [])];
  // If the selected stack has archers/crossbowmen, ALSO highlight enemy
  // regions at distance 2 — that's the ranged-attack envelope. Same color
  // (the player's banner) so it visually reads as one action zone.
  const rangedTargets = computeRangedTargets(currentCtx, selectedArmyRegion, ownerIdx ?? -1);
  for (const r of rangedTargets) {
    if (!list.includes(r)) list.push(r);
  }
  renderer?.setActionHighlights(list, color);
}

/**
 * Enemy regions reachable by a ranged attack from `from`. Returns regions at
 * BFS distance EXACTLY 2 (adjacents are already covered by the move flow).
 * Empty if the source has no archers/crossbowmen — keeps the visual minimal
 * for melee-only stacks.
 */
function computeRangedTargets(
  ctx: InGameContext,
  from: number,
  myIdx: number,
): number[] {
  const u = ctx.state.regionUnits[from];
  if (!u) return [];
  if (u.archers + u.crossbowmen === 0) return [];
  if (u.movedThisTurn) return [];
  // Range model (mirrors the server in lobby_room.ts):
  //   • base                              = 2
  //   • castle tier 3                     = 3
  //   • tech "arcs" (Atelier de griffes)  = 3, but ONLY when the
  //     source region has a building (camp, castle T1/T2/T3). Without
  //     a building the bonus does not apply. T3 + 'arcs' caps at 3
  //     (no stacking past 3).
  const building = ctx.state.regionBuilding[from] ?? 0;
  const BUILDING_CAMP_C = 1;
  const BUILDING_CASTLE_T1_C = 2;
  const BUILDING_CASTLE_T2_C = 3;
  const BUILDING_CASTLE_T3_C = 4;
  let maxDepth = building === BUILDING_CASTLE_T3_C ? 3 : 2;
  const me = myIdx >= 0 ? ctx.state.participants[myIdx] : null;
  const hasArcs = !!me && (() => {
    for (let i = 0; i < me.researchedTechs.length; i++) {
      if (me.researchedTechs[i] === 'arcs') return true;
    }
    return false;
  })();
  const onBuilding =
    building === BUILDING_CAMP_C ||
    building === BUILDING_CASTLE_T1_C ||
    building === BUILDING_CASTLE_T2_C ||
    building === BUILDING_CASTLE_T3_C;
  if (hasArcs && onBuilding && maxDepth < 3) maxDepth = 3;
  const visited = new Set<number>([from]);
  let frontier: number[] = [from];
  for (let d = 0; d < maxDepth; d++) {
    const next: number[] = [];
    for (const r of frontier) {
      const adj = adjacency.get(r);
      if (!adj) continue;
      for (const n of adj) {
        if (visited.has(n)) continue;
        visited.add(n);
        next.push(n);
      }
    }
    frontier = next;
  }
  const adjFrom = adjacency.get(from) ?? new Set<number>();
  const out: number[] = [];
  for (const r of visited) {
    if (r === from) continue;
    if (adjFrom.has(r)) continue; // adjacent → move flow, not ranged
    const owner = ctx.state.regionOwners[r] ?? -1;
    if (owner >= 0 && owner !== myIdx) out.push(r);
  }
  return out;
}

function handleRegionClick(ctx: InGameContext, regionId: number): void {
  if (ctx.state.phase === 'placement') {
    const me = findMyParticipant(ctx.state);
    if (!me) return;
    if (me.turnOrder !== ctx.state.currentTurnIndex) return;
    const myIdx = findParticipantIndex(ctx.state, me);
    if (myIdx < 0 || ctx.state.castles[myIdx] !== -1) return;
    if (ctx.state.regionOwners[regionId] !== -1) return;
    ctx.room.send('castle.place', { regionId });
    return;
  }
  if (ctx.state.phase === 'playing') {
    // Branch 0 (priority): castle-shot targeting mode. Click on a highlighted
    // enemy region → fire; click anywhere else → cancel.
    if (castleShotMode !== null) {
      const { from, targets } = castleShotMode;
      if (targets.has(regionId)) {
        ctx.room.send('castle.shot', { from, to: regionId });
        playWhoosh();
      }
      clearCastleShotMode();
      forceHudRender();
      return;
    }
    // Branch A: a unit is currently selected. The click chooses the target.
    if (selectedArmyRegion !== null) {
      const from = selectedArmyRegion;
      const me = findMyParticipant(ctx.state);
      const myIdx = me ? findParticipantIndex(ctx.state, me) : -1;
      const isAdjacent = adjacency.get(from)?.has(regionId) ?? false;
      const rangedTargets = myIdx >= 0 ? computeRangedTargets(ctx, from, myIdx) : [];
      const isRangedTarget = rangedTargets.includes(regionId);
      if (from === regionId) {
        // Re-click the source = cancel.
        clearArmySelection();
      } else if (isAdjacent) {
        clearArmySelection();
        openActionConfirm(ctx, from, regionId);
        return;
      } else if (isRangedTarget) {
        clearArmySelection();
        openRangedAttackConfirm(ctx, from, regionId);
        return;
      } else {
        // Clicked a non-adjacent, non-ranged region — dismiss without acting.
        clearArmySelection();
      }
    }
    // Branch B: classic region info toggling.
    selectedRegionId = selectedRegionId === regionId ? null : regionId;
    const gameHud = document.getElementById('game-hud') as HTMLDivElement | null;
    if (gameHud) renderHud(gameHud, ctx);
    return;
  }
}

function findMyParticipant(state: LobbyState): Participant | undefined {
  const me = auth.user?.id;
  if (!me) return undefined;
  for (let i = 0; i < state.participants.length; i++) {
    const p = state.participants[i];
    if (p && p.id === me) return p;
  }
  return undefined;
}

function findParticipantIndex(state: LobbyState, target: Participant): number {
  for (let i = 0; i < state.participants.length; i++) {
    if (state.participants[i] === target) return i;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// HUD

function renderHud(host: HTMLElement, ctx: InGameContext): void {
  host.innerHTML = '';
  // Propagate the local player's banner color as a CSS custom property so
  // every faction-themed border in the HUD picks it up. Falls back to the
  // game's accent gold when there's no player yet (lobby / spectator).
  const me = findMyParticipant(ctx.state);
  const myFaction = me ? ctx.factions.find((x) => x.id === me.factionId) : null;
  const factionColorHex = myFaction?.color ?? '#e8d8a8';
  host.style.setProperty('--faction-color', factionColorHex);
  host.style.setProperty('--faction-color-soft', hexAlpha(factionColorHex, 0.25));
  host.style.setProperty('--faction-color-faint', hexAlpha(factionColorHex, 0.08));
  host.classList.add('hud-themed');
  // Surface the current game phase as a CSS class on the HUD root so
  // stylesheets can hide / show phase-specific widgets without JS.
  // Used (e.g.) by mobile CSS to hide the leaderboard + settings icons
  // during the dice-roll ceremony — they aren't actionable yet and
  // would otherwise clutter the dice overlay.
  for (const c of Array.from(host.classList)) {
    if (c.startsWith('hud-phase-')) host.classList.remove(c);
  }
  host.classList.add(`hud-phase-${ctx.state.phase}`);
  // The chat panel lives directly on <body> (not as a child of #game-hud)
  // so it survives HUD re-renders without flickering. As a side effect,
  // it sits OUTSIDE the cascade where `--faction-color` was set above —
  // mirror the variables onto the chat host so its faction-tinted send
  // button picks up the local player's banner colour.
  const chatHostForColor = document.getElementById('gh-chat-host');
  if (chatHostForColor) {
    chatHostForColor.style.setProperty('--faction-color', factionColorHex);
    chatHostForColor.style.setProperty('--faction-color-soft', hexAlpha(factionColorHex, 0.25));
    chatHostForColor.style.setProperty('--faction-color-faint', hexAlpha(factionColorHex, 0.08));
  }
  host.appendChild(renderPhaseBanner(ctx));
  // Ranked-only forfeit countdown banner — shown when any participant
  // has a non-zero `disconnectExpiresEpoch` (= they've left/dropped and
  // the room is waiting up to 2 min for them to come back). Auto-hides
  // when they rejoin or the timer expires.
  const forfeitBanner = renderForfeitCountdown(ctx);
  if (forfeitBanner) host.appendChild(forfeitBanner);
  // Top-right widget group: settings button + leaderboard crown. Both are
  // hover-to-expand slide-in panels so they stay out of the way until needed.
  host.appendChild(renderSettingsHost());
  host.appendChild(renderLeaderboardHost(ctx));
  const tip = renderTurnTip(ctx);
  if (tip) host.appendChild(tip);

  // Top HUD strip — sits centered at top of screen and holds:
  //   [leave button] [resource bar] [end-turn button]
  // Leave on the left ("get out fast"), end-turn on the right ("continue").
  //
  // The leave button is HIDDEN during dice ceremony and castle placement —
  // those phases are short, locked, and leaving mid-ceremony would put
  // the room in a half-initialised state (other participants would wait
  // for a player who's already gone). The button comes back as soon as
  // the game enters the `playing` phase.
  const strip = el('div', 'gh-hud-strip');
  // Show the top leave button during 'playing' only. During dice / placement
  // it'd disrupt a half-bootstrapped room; once 'ended' the victory overlay
  // has its own "Filer maintenant" button so duplicating the affordance
  // here would clutter the screen.
  if (ctx.state.phase === 'playing') {
    strip.appendChild(renderLeaveButton(ctx));
  }
  if (ctx.state.phase === 'playing') {
    const resHud = renderResourcesPanel(ctx);
    if (resHud) strip.appendChild(resHud);
  }
  if (ctx.state.phase === 'playing') {
    const endBtn = renderEndTurnButton(ctx);
    if (endBtn) strip.appendChild(endBtn);
  }
  host.appendChild(strip);

  // Other in-playing panels that live elsewhere on screen.
  if (ctx.state.phase === 'playing') {
    const missionPanel = renderMissionPanel(ctx);
    if (missionPanel) host.appendChild(missionPanel);
    if (selectedRegionId !== null) {
      const regionPanel = renderRegionPanel(ctx, selectedRegionId);
      if (regionPanel) host.appendChild(regionPanel);
    }
    // Note: the chat panel is NOT added here — it lives directly on
    // <body> (managed by `attachChat()` / `detachChat()`) so HUD re-renders
    // don't rebuild its DOM. Previously, every state patch would wipe
    // and re-create the chat, causing a visible flicker / "tilt".
  }
  // Show the body-attached chat only during 'playing'. Hide it otherwise
  // (dice ceremony / placement / ended) without destroying the DOM.
  const chatHostEl = document.getElementById('gh-chat-host');
  if (chatHostEl) {
    chatHostEl.style.display = ctx.state.phase === 'playing' ? '' : 'none';
  }
  // Bottom turn-order strip — shown during dice_roll (progressively, as the
  // ceremony reveals each kingdom's roll), placement (so players know who's
  // about to drop a castle), and playing.
  if (
    ctx.state.phase === 'dice_roll' ||
    ctx.state.phase === 'placement' ||
    ctx.state.phase === 'playing'
  ) {
    const orderStrip = renderTurnOrderStrip(ctx);
    if (orderStrip) host.appendChild(orderStrip);
  }

  // Castle-shot hint banner: while the player is choosing their target,
  // show a small instruction near the top of the screen.
  if (castleShotMode !== null) {
    const hint = el(
      'div',
      'gh-castle-shot-hint',
      '🎯 Choisis la cible du tir — clique sur un territoire ennemi rouge (Échap pour annuler)',
    );
    host.appendChild(hint);
  }

  // Run / clean up the 3D dice ceremony driven by the current phase.
  if (ctx.state.phase === 'dice_roll') {
    startDiceCeremony(ctx);
  } else {
    cleanupDiceCeremony();
  }

  // Victory banner when the game ends.
  if (ctx.state.phase === 'ended' && ctx.state.winnerIndex >= 0) {
    if (!endedSoundPlayed) {
      endedSoundPlayed = true;
      const me = findMyParticipant(ctx.state);
      const winner = ctx.state.participants[ctx.state.winnerIndex];
      const iWon = !!(me && winner && me.id === winner.id);
      // Triumphant melody if I won, melancholic descending meow otherwise.
      if (iWon) playVictory();
      else playMiauDesapprouv();
    }
    host.appendChild(renderVictoryBanner(ctx));
  }
}

/**
 * Compute next-turn resource income for the local player from the live state.
 * Wood/stone/gold come from every owned region; iron/silver only from
 * fully-owned continents. We recount regions and continents on every render
 * so the "+N" preview reflects late-turn captures (e.g. after a successful
 * attack flips a territory).
 */
function computeIncomePreview(state: LobbyState, myIdx: number): {
  wood: number; stone: number; gold: number; iron: number; silver: number; fruit: number;
} {
  let regions = 0;
  let castles = 0;
  for (let r = 0; r < state.regionOwners.length; r++) {
    if (state.regionOwners[r] === myIdx) {
      regions++;
      const b = state.regionBuilding[r] ?? 0;
      if (b === 2 || b === 3 || b === 4) castles++;
    }
  }
  let continents = 0;
  for (const [, regionIds] of continentRegions) {
    let allMine = regionIds.size > 0;
    for (const r of regionIds) {
      if (state.regionOwners[r] !== myIdx) { allMine = false; break; }
    }
    if (allMine) continents++;
  }
  // Mirror of server constants. KEEP IN SYNC.
  return {
    wood: regions * 2,
    stone: regions * 1,
    gold: regions * 1,
    iron: continents * 1,
    silver: continents * 1,
    fruit: Math.floor(regions / 2) + castles * 3,
  };
}

/**
 * Top-of-screen horizontal resources bar. Compact: just an icon + value +
 * tiny "+income" superscript per resource. Active buffs sit at the right
 * along with the "Sniffer" button — so the bar carries everything that
 * was in the old sidebar in one strip, freeing up vertical map space.
 */
function renderResourcesPanel(ctx: InGameContext): HTMLElement | null {
  const me = findMyParticipant(ctx.state);
  if (!me) return null;
  const myIdx = findParticipantIndex(ctx.state, me);
  const income = myIdx >= 0
    ? computeIncomePreview(ctx.state, myIdx)
    : { wood: 0, stone: 0, gold: 0, iron: 0, silver: 0, fruit: 0 };
  const wrap = el('div', 'gh-resbar');
  // Left side: the 6 resource cells in a strict horizontal row.
  const left = el('div', 'gh-resbar-left');
  left.appendChild(resCell('/sprites/ressource_bois.png', 'Bouts de bois', me.wood, income.wood));
  left.appendChild(resCell('/sprites/ressource_pierre.png', 'Cailloux', me.stone, income.stone));
  left.appendChild(resCell('/sprites/piece_or.png', 'Pièces brillantes', me.gold, income.gold));
  left.appendChild(resCell('/sprites/ressource_fer.png', 'Bouts de métal', me.iron, income.iron));
  left.appendChild(resCell('/sprites/piece_argent.png', 'Brillant argenté', me.silver, income.silver));
  left.appendChild(resCell('/sprites/ressource_fruit.png', 'Croquettes (savoir)', me.fruit, income.fruit));
  wrap.appendChild(left);

  // Right side: active buff pills + research button. Wraps under on narrow
  // screens (rare; the bar should always fit on desktop).
  const right = el('div', 'gh-resbar-right');
  if (me.attackBuffTurns > 0 || me.defenseBuffTurns > 0 || me.castleShots > 0) {
    const buffs = el('div', 'gh-buff-row');
    // Compact icon-only badges: weapon sprite + a small "↑" arrow
    // overlay (attack / defense buffs) or a "×N" chip (castle shots).
    // The remaining-turns count rides as a tiny corner number; the
    // full description lives in the title tooltip. Keeps the three
    // badges sitting side-by-side on one line instead of stacking.
    const mkBuff = (
      cls: string,
      iconSrc: string,
      cornerText: string,
      withArrow: boolean,
      title: string,
    ): HTMLElement => {
      const b = el('span', `gh-buff-badge ${cls}`);
      b.title = title;
      const img = document.createElement('img');
      img.src = iconSrc;
      img.className = 'gh-buff-icon';
      img.alt = '';
      b.appendChild(img);
      if (withArrow) {
        const arrow = el('span', 'gh-buff-arrow', '↑');
        b.appendChild(arrow);
      }
      if (cornerText) {
        b.appendChild(el('span', 'gh-buff-corner', cornerText));
      }
      return b;
    };
    if (me.attackBuffTurns > 0) {
      buffs.appendChild(mkBuff(
        'gh-buff-attack',
        '/sprites/arme_hache_combat.png',
        String(me.attackBuffTurns),
        true,
        `Coup de griffe royal — +25 % attaque pendant ${me.attackBuffTurns} patrouille${me.attackBuffTurns > 1 ? 's' : ''}`,
      ));
    }
    if (me.defenseBuffTurns > 0) {
      buffs.appendChild(mkBuff(
        'gh-buff-defense',
        '/sprites/arme_bouclier_pierre.png',
        String(me.defenseBuffTurns),
        true,
        `Posture du chat couché — +25 % défense pendant ${me.defenseBuffTurns} patrouille${me.defenseBuffTurns > 1 ? 's' : ''}`,
      ));
    }
    if (me.castleShots > 0) {
      buffs.appendChild(mkBuff(
        'gh-buff-shot',
        '/sprites/arme_warshooter_bois.png',
        `×${me.castleShots}`,
        false,
        'Défense de la litière — tire depuis tes repaires',
      ));
    }
    right.appendChild(buffs);
  }
  // Research button (compact) — replaces the 🌳 emoji with an explicit
  // iconchat_crier sprite for visual consistency with the other cat icons.
  const researchBtn = document.createElement('button');
  researchBtn.type = 'button';
  researchBtn.className = 'btn-secondary gh-resbar-research';
  const researchIcon = document.createElement('img');
  researchIcon.src = '/sprites/iconchat_crier.png';
  researchIcon.className = 'iconchat-inline';
  researchIcon.alt = '';
  researchBtn.appendChild(researchIcon);
  researchBtn.appendChild(document.createTextNode('Sniffer'));
  researchBtn.title = 'Sniffer de nouvelles idées (arbre tech)';
  researchBtn.addEventListener('click', () => openTechTreeModal(ctx));
  right.appendChild(researchBtn);
  wrap.appendChild(right);
  return wrap;
}

/** Compact horizontal resource cell: icon + value, with a tiny "+income" tag. */
function resCell(spriteSrc: string, label: string, value: number, income: number): HTMLElement {
  const c = el('div', 'gh-res-cell');
  c.title = income > 0
    ? `${label} (Prrrr +${income} en fin de patrouille)`
    : label;
  const img = document.createElement('img');
  img.src = spriteSrc;
  img.className = 'gh-res-icon-img';
  img.alt = label;
  c.appendChild(img);
  c.appendChild(el('span', 'gh-res-value', value.toString()));
  if (income > 0) {
    c.appendChild(el('span', 'gh-res-income', `+${income}`));
  }
  return c;
}

/**
 * Mission card panel. Pinned to the bottom-left of the HUD, shows the
 * player's currently active mission with name + description + reward.
 *
 * Re-renders on each HUD tick. When the underlying mission ID changes
 * (server broadcast `mission.drawn`), we add a flip-in animation class
 * for ~600 ms so the card draws attention.
 */
let lastMissionIdSeen: string | null = null;

/** Brief toast in the centre of the screen confirming a mission completion. */
function showMissionToast(missionName: string): void {
  const t = document.createElement('div');
  t.className = 'gh-mission-toast';
  const icon = document.createElement('img');
  icon.src = '/sprites/iconchat_valide.png';
  icon.className = 'inline-icon';
  t.appendChild(icon);
  t.appendChild(document.createTextNode(`Mission accomplie ! ${missionName}`));
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('gh-mission-toast-out'), 2200);
  setTimeout(() => t.remove(), 3000);
}
/** True when we're on a small (mobile / narrow tablet) screen. Used to
 *  swap the always-visible mission card for a compact badge that opens
 *  a popup on tap — saves screen real-estate where it's scarce. */
function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

/** Build the inner mission "details" DOM (icon + name + description +
 *  reward row + redraw button). Shared between the desktop flip-card
 *  front face and the mobile popup so the two stay byte-identical. */
function buildMissionDetails(
  ctx: InGameContext,
  me: Participant,
  mission: typeof MISSIONS[string],
  refreshWrap: HTMLElement,
): HTMLElement {
  const front = el('div', 'gh-mission-details');
  const body = el('div', 'gh-mission-body');
  const icon = document.createElement('img');
  icon.src = mission.icon;
  icon.className = 'gh-mission-icon';
  body.appendChild(icon);
  const txt = el('div', 'gh-mission-text');
  txt.appendChild(el('div', 'gh-mission-name', mission.name));
  txt.appendChild(el('div', 'gh-mission-desc', mission.description));
  body.appendChild(txt);
  front.appendChild(body);

  const reward = mission.reward;
  const rewardParts: Array<{ src: string; n: number }> = [];
  if (reward.wood) rewardParts.push({ src: '/sprites/ressource_bois.png', n: reward.wood });
  if (reward.stone) rewardParts.push({ src: '/sprites/ressource_pierre.png', n: reward.stone });
  if (reward.gold) rewardParts.push({ src: '/sprites/piece_or.png', n: reward.gold });
  if (reward.iron) rewardParts.push({ src: '/sprites/ressource_fer.png', n: reward.iron });
  if (reward.silver) rewardParts.push({ src: '/sprites/piece_argent.png', n: reward.silver });
  if (reward.fruit) rewardParts.push({ src: '/sprites/ressource_fruit.png', n: reward.fruit });
  if (reward.castleShots) rewardParts.push({ src: '/sprites/arme_warshooter_bois.png', n: reward.castleShots });
  if (reward.attackBuffTurns) rewardParts.push({ src: '/sprites/arme_hache_combat.png', n: reward.attackBuffTurns });
  if (reward.defenseBuffTurns) rewardParts.push({ src: '/sprites/arme_bouclier_pierre.png', n: reward.defenseBuffTurns });
  if (rewardParts.length > 0) {
    const rewardRow = el('div', 'gh-mission-reward');
    rewardRow.appendChild(el('span', 'gh-mission-reward-label', 'Récompense :'));
    for (const part of rewardParts) {
      const cell = el('span', 'gh-mission-reward-cell');
      const img = document.createElement('img');
      img.src = part.src;
      img.className = 'inline-icon';
      cell.appendChild(img);
      cell.appendChild(document.createTextNode(`×${part.n}`));
      rewardRow.appendChild(cell);
    }
    front.appendChild(rewardRow);
  }

  // Redraw button — same gating as before (own turn, 10 fruits, no action).
  const myIdx = findParticipantIndex(ctx.state, me);
  const isMyTurn = me.turnOrder === ctx.state.currentTurnIndex;
  let anyMoved = false;
  if (myIdx >= 0) {
    for (let r = 0; r < ctx.state.regionUnits.length; r++) {
      if (ctx.state.regionOwners[r] !== myIdx) continue;
      const u = ctx.state.regionUnits[r];
      if (u && u.movedThisTurn) { anyMoved = true; break; }
    }
  }
  const canRedraw = isMyTurn && me.fruit >= 10 && !me.hasRecruitedThisTurn && !anyMoved && !me.isEliminated;
  const redrawBtn = document.createElement('button');
  redrawBtn.type = 'button';
  redrawBtn.className = 'gh-mission-redraw';
  redrawBtn.disabled = !canRedraw;
  redrawBtn.title = !isMyTurn ? 'Disponible uniquement durant ton tour'
    : me.hasRecruitedThisTurn ? 'Tu as déjà recruté/bâti ce tour'
    : anyMoved ? 'Une de tes meutes a déjà bougé ce tour'
    : me.fruit < 10 ? `Manque ${10 - me.fruit} croquette${10 - me.fruit > 1 ? 's' : ''}`
    : 'Tirer une nouvelle carte mission';
  const fImg = document.createElement('img');
  fImg.src = '/sprites/ressource_fruit.png';
  fImg.className = 'inline-icon';
  redrawBtn.appendChild(fImg);
  redrawBtn.appendChild(document.createTextNode('Repiocher (10)'));
  if (canRedraw) {
    redrawBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const rect = refreshWrap.getBoundingClientRect();
      const clone = refreshWrap.cloneNode(true) as HTMLElement;
      clone.style.position = 'fixed';
      clone.style.top = `${rect.top}px`;
      clone.style.left = `${rect.left}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.zIndex = '100';
      clone.style.pointerEvents = 'none';
      clone.classList.remove('gh-mission-drawn');
      clone.classList.add('gh-mission-disintegrating');
      const innerClone = clone.querySelector('.gh-mission-inner') as HTMLElement | null;
      if (innerClone) {
        innerClone.style.transition = 'none';
        innerClone.style.transform = 'rotateY(180deg)';
      }
      document.body.appendChild(clone);
      setTimeout(() => clone.remove(), 750);
      ctx.room.send('mission.redraw');
      playSparkle();
    });
  }
  front.appendChild(redrawBtn);
  return front;
}

/** Render a full-screen popup modal showing the mission details. Used
 *  on mobile in place of the always-visible flip card so the carte
 *  mission doesn't eat half the playable area. Tap outside or the
 *  close button dismisses. */
function openMissionPopup(ctx: InGameContext): void {
  const me = findMyParticipant(ctx.state);
  if (!me || !me.missionId) return;
  const mission = MISSIONS[me.missionId];
  if (!mission) return;
  // Don't double-open if a popup is already visible.
  if (document.querySelector('.gh-mission-popup')) return;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay gh-mission-popup';
  const card = el('div', 'modal-card gh-mission-popup-card');
  card.appendChild(el('h2', 'modal-title', 'Carte mission'));
  // Reuse the shared details builder — refreshWrap = card so the redraw
  // animation clones the modal card (good enough; it's a one-shot FX).
  card.appendChild(buildMissionDetails(ctx, me, mission, card));
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn-secondary gh-mission-popup-close';
  closeBtn.textContent = 'Fermer';
  closeBtn.addEventListener('click', () => overlay.remove());
  card.appendChild(closeBtn);
  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

function renderMissionPanel(ctx: InGameContext): HTMLElement | null {
  const me = findMyParticipant(ctx.state);
  if (!me) return null;

  // ── Cooldown state: no active mission, but the player just finished
  // one and is waiting for the 3-turn lockout to expire. Render a locked
  // back-only card with "Nouvelle mission dans X tour(s)" — and NO flip
  // animation on hover (the `.gh-mission-locked` class disables it via CSS).
  if (!me.missionId && me.nextMissionTurn > ctx.state.turnNumber) {
    const remaining = me.nextMissionTurn - ctx.state.turnNumber;
    // Mobile: tiny badge with the countdown overlayed. No popup (there's
    // nothing to act on during cooldown), just a glanceable indicator.
    if (isMobileViewport()) {
      const badge = el('div', 'gh-mission gh-mission-mobile-badge gh-mission-mobile-locked');
      const badgeIcon = document.createElement('img');
      badgeIcon.src = '/sprites/iconchat_refus.png';
      badgeIcon.className = 'gh-mission-mobile-badge-icon';
      badgeIcon.alt = 'Mission en cooldown';
      badge.appendChild(badgeIcon);
      const count = el('span', 'gh-mission-mobile-badge-count', String(remaining));
      badge.appendChild(count);
      badge.title = `Nouvelle mission dans ${remaining} tour${remaining > 1 ? 's' : ''}`;
      return badge;
    }
    const wrap = el('div', 'gh-mission gh-mission-locked');
    const inner = el('div', 'gh-mission-inner');
    const back = el('div', 'gh-mission-face gh-mission-back');
    // Two stacked icons: interrogation is the default; on hover we fade
    // it out and reveal the "refus" icon, signalling "no mission available
    // right now". Pure CSS swap — `.gh-mission-back-icon-swap` (CSS) handles
    // the opacity flip on `.gh-mission-locked:hover`.
    const iconWrap = el('div', 'gh-mission-back-icon-swap');
    const iconDefault = document.createElement('img');
    iconDefault.src = '/sprites/iconchat_interrogation.png';
    iconDefault.className = 'gh-mission-back-icon gh-mission-back-icon-default';
    iconDefault.alt = '';
    const iconHover = document.createElement('img');
    iconHover.src = '/sprites/iconchat_refus.png';
    iconHover.className = 'gh-mission-back-icon gh-mission-back-icon-hover';
    iconHover.alt = '';
    iconWrap.appendChild(iconDefault);
    iconWrap.appendChild(iconHover);
    back.appendChild(iconWrap);
    back.appendChild(el('div', 'gh-mission-back-label', 'Mission accomplie !'));
    back.appendChild(
      el(
        'div',
        'gh-mission-back-hint',
        `Nouvelle mission dans ${remaining} tour${remaining > 1 ? 's' : ''}`,
      ),
    );
    if (me.missionsCompleted > 0) {
      back.appendChild(el('div', 'gh-mission-back-count', `${me.missionsCompleted} accomplie${me.missionsCompleted > 1 ? 's' : ''}`));
    }
    inner.appendChild(back);
    wrap.appendChild(inner);
    return wrap;
  }

  // No active mission and no cooldown → all missions completed for this player.
  if (!me.missionId) return null;
  const mission = MISSIONS[me.missionId];
  if (!mission) return null;

  // ── Mobile: collapse the flip-card to a tiny badge (icon + completed
  // count). Tap → full-screen popup with all the details. The desktop
  // hover-to-reveal flip card stays unchanged below.
  if (isMobileViewport()) {
    const badge = el('div', 'gh-mission gh-mission-mobile-badge');
    if (lastMissionIdSeen !== me.missionId) {
      badge.classList.add('gh-mission-drawn');
      lastMissionIdSeen = me.missionId;
    }
    const badgeIcon = document.createElement('img');
    badgeIcon.src = '/sprites/iconchat_interrogation.png';
    badgeIcon.className = 'gh-mission-mobile-badge-icon';
    badgeIcon.alt = 'Carte mission';
    badge.appendChild(badgeIcon);
    if (me.missionsCompleted > 0) {
      const count = el('span', 'gh-mission-mobile-badge-count', String(me.missionsCompleted));
      badge.appendChild(count);
    }
    badge.title = 'Voir ma carte mission';
    badge.addEventListener('click', (e) => {
      e.stopPropagation();
      openMissionPopup(ctx);
    });
    return badge;
  }

  // 3D flip card structure: outer wrapper sets perspective, inner has the
  // back and front faces. Hovering the wrapper flips the inner 180° so the
  // front reveals. A `gh-mission-drawn` class on freshly drawn missions
  // adds a one-shot draw animation.
  const wrap = el('div', 'gh-mission');
  if (lastMissionIdSeen !== me.missionId) {
    wrap.classList.add('gh-mission-drawn');
    lastMissionIdSeen = me.missionId;
  }
  const inner = el('div', 'gh-mission-inner');

  // BACK face (face-down, default state) — pixel paw + cat hint
  const back = el('div', 'gh-mission-face gh-mission-back');
  const backIcon = document.createElement('img');
  backIcon.src = '/sprites/iconchat_interrogation.png';
  backIcon.className = 'gh-mission-back-icon';
  back.appendChild(backIcon);
  back.appendChild(el('div', 'gh-mission-back-label', 'Carte mission'));
  back.appendChild(el('div', 'gh-mission-back-hint', 'Survole pour voir'));
  if (me.missionsCompleted > 0) {
    back.appendChild(el('div', 'gh-mission-back-count', `${me.missionsCompleted} accomplie${me.missionsCompleted > 1 ? 's' : ''}`));
  }
  inner.appendChild(back);

  // FRONT face — revealed on hover. Built from the shared details helper
  // so the desktop reveal + mobile popup stay in sync.
  const front = el('div', 'gh-mission-face gh-mission-front');
  front.appendChild(buildMissionDetails(ctx, me, mission, wrap));

  inner.appendChild(front);
  wrap.appendChild(inner);
  return wrap;
}

function renderEndTurnButton(ctx: InGameContext): HTMLElement | null {
  const me = findMyParticipant(ctx.state);
  if (!me) return null;
  const isMyTurn = me.turnOrder === ctx.state.currentTurnIndex;
  // Wrapper holds the button + (in ranked) the per-turn countdown below.
  const wrap = el('div', 'gh-end-turn-wrap');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn-primary gh-end-turn';
  // Compact form: iconchat + skip-forward chevrons. Title carries the full
  // meaning so screen readers / hover tooltip still describe the action.
  const icon = document.createElement('img');
  icon.src = '/sprites/iconchat_valide.png';
  icon.className = 'iconchat-inline';
  icon.alt = '';
  btn.appendChild(icon);
  const skip = document.createElement('span');
  skip.className = 'gh-end-turn-skip';
  skip.textContent = '»';
  btn.appendChild(skip);
  btn.title = isMyTurn
    ? `Aller faire la sieste — finir la patrouille ${ctx.state.turnNumber}`
    : `${currentName(ctx)} chasse en ce moment…`;
  btn.disabled = !isMyTurn;
  btn.addEventListener('click', () => {
    // Disable instantly so the player sees feedback during the 1 s
    // server-side buffer. We don't change the label — the button just goes
    // disabled until the next render snapshot.
    btn.disabled = true;
    playTap();
    ctx.room.send('turn.end');
  });
  wrap.appendChild(btn);

  // Ranked-only: live countdown below the button. The server stores an
  // absolute deadline (`turnDeadlineEpoch`) so client/server clock drift
  // only matters for ±1 s display jitter — the auto-skip itself fires
  // on the server.
  if (ctx.state.isRanked && ctx.state.turnDeadlineEpoch > 0) {
    const countdown = document.createElement('div');
    countdown.className = 'gh-end-turn-countdown';
    wrap.appendChild(countdown);
    const tick = (): void => {
      if (!countdown.isConnected) return;
      const remainingMs = ctx.state.turnDeadlineEpoch - Date.now();
      const sec = Math.max(0, Math.ceil(remainingMs / 1000));
      countdown.textContent = `${sec}s avant la sieste forcée`;
      if (sec <= 10) countdown.classList.add('gh-end-turn-countdown-low');
      else countdown.classList.remove('gh-end-turn-countdown-low');
    };
    tick();
    // 250 ms cadence so the second flips visibly without burning CPU.
    const handle = window.setInterval(tick, 250);
    // Stop the interval when the wrap is detached (next HUD re-render).
    const observer = new MutationObserver(() => {
      if (!wrap.isConnected) {
        window.clearInterval(handle);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  return wrap;
}

function currentName(ctx: InGameContext): string {
  const cur = findParticipantByTurnOrder(ctx.state, ctx.state.currentTurnIndex);
  return cur?.name ?? '???';
}

/** Last (turnNumber, currentTurnIndex) pair for which we already
 *  spawned the centered "MIAOU c'est à moi" toast. Prevents the toast
 *  from re-popping every time the HUD re-renders mid-turn. */
let lastMiaouToastKey: string | null = null;

/**
 * Ranked-only forfeit countdown banner.
 *
 * When a player leaves or drops in a ranked match they get up to 2 min
 * to reconnect before being forfeit (server schedules a timer; broadcasts
 * the deadline via `participant.disconnectExpiresEpoch`). We render a
 * persistent banner near the top of the HUD so survivors see:
 *
 *   "MIAOU ! [name] est parti faire dodo. 1:42 avant qu'il forfait."
 *
 * If the local player IS one of the disconnected ones, the banner is
 * NOT shown to them (they're not even in the room anyway).
 *
 * Updates every 250 ms via setInterval — cheap, runs only while the
 * banner is mounted, auto-stops on next renderHud().
 */
function renderForfeitCountdown(ctx: InGameContext): HTMLElement | null {
  if (!ctx.state.isRanked || ctx.state.phase !== 'playing') return null;
  const now = Date.now();
  // Collect every still-active participant with a future deadline.
  const pending: Participant[] = [];
  ctx.state.participants.forEach((p) => {
    if (!p || p.isEliminated) return;
    if (p.type !== 'human') return;
    if (!p.disconnectExpiresEpoch) return;
    if (p.disconnectExpiresEpoch <= now) return; // already expired
    pending.push(p);
  });
  if (pending.length === 0) return null;
  const wrap = el('div', 'gh-forfeit-banner');
  // One row per disconnected player.
  for (const p of pending) {
    const row = el('div', 'gh-forfeit-row');
    const head = document.createElement('img');
    head.src = `/sprites/${p.factionId}_army.png`;
    head.className = 'gh-forfeit-head';
    head.alt = '';
    row.appendChild(head);
    const text = el('div', 'gh-forfeit-text');
    text.appendChild(el('div', 'gh-forfeit-title', `${p.name} est parti faire dodo`));
    const sub = el('div', 'gh-forfeit-sub');
    sub.dataset.deadline = String(p.disconnectExpiresEpoch);
    sub.textContent = '— calcule —';
    text.appendChild(sub);
    row.appendChild(text);
    wrap.appendChild(row);
  }
  // Tick every 250 ms to update the countdown text. Stops automatically
  // once the banner is detached (MutationObserver on body).
  const tick = (): void => {
    if (!wrap.isConnected) return;
    const nw = Date.now();
    let anyLive = false;
    wrap.querySelectorAll<HTMLElement>('.gh-forfeit-sub').forEach((sub) => {
      const dl = parseInt(sub.dataset.deadline ?? '0', 10);
      const remaining = Math.max(0, dl - nw);
      if (remaining > 0) anyLive = true;
      const sec = Math.ceil(remaining / 1000);
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      sub.textContent = `Forfait dans ${m}:${String(s).padStart(2, '0')} si pas de retour`;
    });
    if (anyLive) window.setTimeout(tick, 250);
  };
  window.setTimeout(tick, 0);
  return wrap;
}

function renderPhaseBanner(ctx: InGameContext): HTMLElement {
  const wrap = el('div', 'gh-phase');
  // During play we render an EMPTY phantom wrapper — the inline label
  // was hiding nothing useful, since "Patrouille N" lives in the bottom
  // order strip and "it's your turn" is conveyed by the centered toast
  // below. The wrapper is kept so the surrounding HUD layout doesn't
  // shift on re-render.
  if (ctx.state.phase === 'playing') {
    wrap.classList.add('gh-phase-empty');
    // Spawn the centered "MIAOU c'est à moi" toast EXACTLY ONCE per
    // turn-cycle when the local player becomes the active one. The toast
    // auto-fades after ~3 s (see spawnMiaouToast) so it doesn't obscure
    // the map for the whole turn.
    const me = findMyParticipant(ctx.state);
    if (me && me.turnOrder === ctx.state.currentTurnIndex) {
      const key = `${ctx.state.turnNumber}|${ctx.state.currentTurnIndex}`;
      if (lastMiaouToastKey !== key) {
        lastMiaouToastKey = key;
        const factionColor = factionColorHex(ctx, me.factionId);
        spawnMiaouToast(factionColor);
      }
    }
    return wrap;
  }
  // Placement: the `.gh-tip` line below already says "MIAOU ! Tape sur un
  // coin de jardin…" so showing "🏰 Choisir son repaire" up top duplicates
  // the information. Hide the banner — `gh-phase-empty` triggers the CSS
  // `display: none`.
  if (ctx.state.phase === 'placement') {
    wrap.classList.add('gh-phase-empty');
    return wrap;
  }
  wrap.appendChild(el('span', 'gh-phase-label', phaseLabel(ctx.state.phase, ctx.state.turnNumber)));
  return wrap;
}

/**
 * Centered "MIAOU c'est à moi !" toast. Lives at the centre of the
 * viewport for ~3 seconds with an iconchat on each side, then fades out
 * and removes itself. Idempotent — clearing any existing one before
 * spawning so rapid turn changes don't stack.
 *
 * `factionColor` is the local player's kingdom colour (hex) — used to
 * tint the border / accent of the toast so the player sees their own
 * livery flash on the screen.
 */
function spawnMiaouToast(factionColor: string): void {
  document.querySelectorAll('.gh-miaou-toast').forEach((n) => n.remove());
  const toast = document.createElement('div');
  toast.className = 'gh-miaou-toast';
  toast.style.setProperty('--faction-color', factionColor);
  const leftIcon = document.createElement('img');
  leftIcon.src = '/sprites/iconchat_valide.png';
  leftIcon.className = 'gh-miaou-toast-icon';
  leftIcon.alt = '';
  const text = document.createElement('span');
  text.className = 'gh-miaou-toast-text';
  text.textContent = 'MIAOU c\'est à moi !';
  const rightIcon = document.createElement('img');
  rightIcon.src = '/sprites/iconchat_guerrier.png';
  rightIcon.className = 'gh-miaou-toast-icon';
  rightIcon.alt = '';
  toast.append(leftIcon, text, rightIcon);
  document.body.appendChild(toast);
  // Fade out + remove. The CSS pairs the .leaving class with a 400 ms
  // opacity transition; we trigger removal at 3000 ms (visible) +
  // 400 ms (fade) = ~3.4 s total.
  window.setTimeout(() => toast.classList.add('gh-miaou-toast-leaving'), 3000);
  window.setTimeout(() => toast.remove(), 3400);
}

/** Helper — look up a faction's hex colour by id, with a sensible
 *  fallback when the catalogue hasn't loaded yet. */
function factionColorHex(ctx: InGameContext, factionId: string): string {
  const f = ctx.factions.find((x) => x.id === factionId);
  return f?.color ?? '#d3a95f';
}

/**
 * Compact title banner that drops in at the top of the screen for a
 * random event. Just a horizontal pill with [icon · title · sub] — no
 * full-screen overlay, no rain layer. The map stays fully visible and
 * the player can act immediately. Auto-fades after ~4 s.
 * Idempotent: re-firing while a banner is active replaces it.
 */
function spawnEventBanner(opts: {
  kind: string;
  title: string;
  sub: string;
  iconSprite: string;
}): void {
  // Dedupe — drop any previous banner before mounting a new one.
  document.querySelectorAll('.event-banner').forEach((n) => n.remove());

  const wrap = document.createElement('div');
  wrap.className = `event-banner event-banner-${opts.kind}`;

  const card = document.createElement('div');
  card.className = 'event-banner-card';
  const icon = document.createElement('img');
  icon.src = opts.iconSprite;
  icon.className = 'event-banner-icon';
  icon.alt = '';
  card.appendChild(icon);
  const text = document.createElement('div');
  text.className = 'event-banner-text';
  const title = document.createElement('h2');
  title.className = 'event-banner-title';
  title.textContent = opts.title;
  text.appendChild(title);
  const sub = document.createElement('p');
  sub.className = 'event-banner-sub';
  sub.textContent = opts.sub;
  text.appendChild(sub);
  card.appendChild(text);
  wrap.appendChild(card);

  document.body.appendChild(wrap);

  // Fade out + remove after 4 seconds.
  setTimeout(() => wrap.classList.add('event-banner-leaving'), 3400);
  setTimeout(() => wrap.remove(), 4400);
}

function phaseLabel(p: Phase, turnNumber: number): string {
  switch (p) {
    case 'dice_roll':
      return 'Tirage des moustaches';
    case 'placement':
      return 'Choisir son repaire';
    case 'playing':
      return `Patrouille ${turnNumber}`;
    case 'ended':
      // No "GAME OVER" — the victory overlay (rendered when
      // state.phase === 'ended' && winnerIndex >= 0) carries all
      // the visual celebration. The phase banner stays empty so
      // there's no redundant text strip across the top.
      return '';
    default:
      return p;
  }
}

/** Localised label for an AI's strategic behaviour (cat-speak edition). */
function behaviorLabel(b: string): string {
  switch (b) {
    case 'warmonger': return 'Bagarreur';
    case 'conqueror': return 'Explorateur';
    case 'defensive': return 'Câlin couché';
    case 'economist': return 'Économe ronron';
    default: return b;
  }
}

/** Detailed tooltip describing how this AI behaviour plays. */
function behaviorDescription(b: string): string {
  switch (b) {
    case 'warmonger':
      return 'Bagarreur — attaque tout ce qui bouge, même sans odeur favorable. Préfère la baston au repos. NIA NIA NIA !';
    case 'conqueror':
      return 'Explorateur — réclame d\'abord toutes les terres libres avant de griffer ses voisins. Avide de nouveaux coins de jardin.';
    case 'defensive':
      return 'Câlin couché — ne sort que pour les batailles gagnées d\'avance (PV > 1.5× ceux de l\'ennemi). Le reste du temps il ronronne dans son repaire.';
    case 'economist':
      return 'Économe ronron — empile les trésors et n\'attaque que si c\'est vraiment évident (PV > 2× ceux de l\'ennemi). Patient, calculateur, et plein de croquettes.';
    default:
      return '';
  }
}

/** Localised label for an AI's unit-composition personality. */
function personalityLabel(p: string): string {
  switch (p) {
    case 'swarm': return 'Portée de chatons';
    case 'archers': return 'Pêcheurs d\'oiseaux';
    case 'berserkers': return 'Furieux';
    case 'elite': return 'Aristochats';
    case 'balanced': return 'Mixte ronron';
    default: return p;
  }
}

/** Detailed tooltip describing how this AI recruits its troops. */
function personalityDescription(p: string): string {
  switch (p) {
    case 'swarm':
      return 'Portée de chatons — rameute uniquement des bagarreurs. Simple et efficace, comme un chaton chasse une chaussette.';
    case 'archers':
      return 'Pêcheurs d\'oiseaux — privilégie les archers (≈60%) + quelques bagarreurs. Préfère tirer de loin que se salir les pattes.';
    case 'berserkers':
      return 'Furieux — mélange 50/50 de bagarreurs et de furieux. Aime taper fort à courte distance.';
    case 'elite':
      return 'Aristochats — investit dans les aristochats (paladins) dès que possible, et vise la Légende en endgame. Élitiste — mais quand il sort, ça pique.';
    case 'balanced':
      return 'Mixte ronron — composition variée et équilibrée. Sait s\'adapter à toutes les situations, en bon chat de la rue.';
    default:
      return '';
  }
}

/** Which scoring metric the leaderboard is currently sorted/shown by.
 *  'territories' = regionsOwned. 'power' = sum of unit (atk × hp / 10)
 *  across every region the participant owns, rounded to integer.
 *  Persisted at module scope so re-renders preserve the choice. */
let leaderboardSortMode: 'territories' | 'power' = 'territories';

/** Sum of every unit's "power" (attack × hp / 10) across every region
 *  owned by the given participant. Rounded to int. Matches the units
 *  the player can recruit, including kamikazes (~6 hp × 25 dmg → 15
 *  power each, reflecting their high lethality despite low headcount). */
function computeArmyPower(ctx: InGameContext, participantIdx: number): number {
  let power = 0;
  for (let r = 0; r < ctx.state.regionOwners.length; r++) {
    if (ctx.state.regionOwners[r] !== participantIdx) continue;
    const u = ctx.state.regionUnits[r];
    if (!u) continue;
    for (const t of UNIT_PRIORITY) {
      const count =
        t === 'warrior' ? u.warriors :
        t === 'archer' ? u.archers :
        t === 'berserker' ? u.berserkers :
        t === 'crossbowman' ? u.crossbowmen :
        t === 'paladin' ? u.paladins :
        t === 'hero' ? u.heroes :
        u.kamikazes;
      const s = UNIT_STATS[t];
      power += count * (s.damage * s.hp) / 10;
    }
  }
  return Math.round(power);
}

function renderParticipantsPanel(ctx: InGameContext): HTMLElement {
  const wrap = el('div', 'gh-players');

  // Phase-driven sort:
  //   • playing / ended → by score (regions owned, then continents,
  //     then turn order as tiebreaker) — actual leaderboard ranking
  //   • dice_roll / placement → by turn order (no scores yet)
  // The strip already handles play-order display; here we focus on rank.
  const isLeaderboardPhase = ctx.state.phase === 'playing' || ctx.state.phase === 'ended';
  wrap.appendChild(
    el('h3', 'gh-players-title', isLeaderboardPhase ? 'NIA·king des chats' : 'Les bandes de chats'),
  );

  // Tabs — only meaningful during playing / ended (when scores exist).
  // Tapping a tab updates `leaderboardSortMode` and rebuilds the panel.
  if (isLeaderboardPhase) {
    const tabs = el('div', 'gh-players-tabs');
    const mkTab = (mode: 'territories' | 'power', label: string, title: string): HTMLElement => {
      const t = document.createElement('button');
      t.type = 'button';
      t.className = leaderboardSortMode === mode
        ? 'gh-players-tab gh-players-tab-active'
        : 'gh-players-tab';
      t.textContent = label;
      t.title = title;
      t.addEventListener('click', (e) => {
        e.stopPropagation();
        if (leaderboardSortMode === mode) return;
        leaderboardSortMode = mode;
        // Rebuild this panel in place (without rebuilding the whole HUD).
        const fresh = renderParticipantsPanel(ctx);
        const parent = wrap.parentElement;
        if (parent) {
          // Preserve the modal-class state (.gh-players-in-modal) when
          // the panel sits inside the leaderboard host's slide-in.
          if (wrap.classList.contains('gh-players-in-modal')) {
            fresh.classList.add('gh-players-in-modal');
          }
          parent.replaceChild(fresh, wrap);
        }
      });
      return t;
    };
    // No emoji glyphs — use plain labels. The active-tab style already
    // makes the selected metric visually obvious.
    tabs.appendChild(mkTab('territories', 'Coins', 'Trier par nombre de coins de jardin (territoires)'));
    tabs.appendChild(mkTab('power', 'Puissance', 'Trier par puissance totale des armées (somme atk × pv / 10)'));
    wrap.appendChild(tabs);
  }

  const sorted: Participant[] = [];
  ctx.state.participants.forEach((p) => sorted.push(p));
  if (isLeaderboardPhase) {
    if (leaderboardSortMode === 'power') {
      // Sort by army power (computed from the live state). Ties fall
      // back to territory count, then turn order.
      const powerMap = new Map<number, number>();
      for (let i = 0; i < ctx.state.participants.length; i++) {
        powerMap.set(i, computeArmyPower(ctx, i));
      }
      sorted.sort((a, b) => {
        const ai = findParticipantIndex(ctx.state, a);
        const bi = findParticipantIndex(ctx.state, b);
        const pa = powerMap.get(ai) ?? 0;
        const pb = powerMap.get(bi) ?? 0;
        if (pb !== pa) return pb - pa;
        if (b.regionsOwned !== a.regionsOwned) return b.regionsOwned - a.regionsOwned;
        return a.turnOrder - b.turnOrder;
      });
    } else {
      sorted.sort((a, b) => {
        if (b.regionsOwned !== a.regionsOwned) return b.regionsOwned - a.regionsOwned;
        if (b.continentsOwned !== a.continentsOwned) return b.continentsOwned - a.continentsOwned;
        return a.turnOrder - b.turnOrder;
      });
    }
  } else if (ctx.state.phase === 'dice_roll') {
    // During the dice ceremony the leaderboard fills in progressively —
    // a kingdom only appears once their dice has been animated. Stops
    // the panel from spoiling who plays first before the ceremony finishes.
    const filtered = sorted.filter((p) => diceDisplayValues.has(p.id));
    sorted.length = 0;
    sorted.push(...filtered);
    sorted.sort((a, b) => a.turnOrder - b.turnOrder);
  } else {
    sorted.sort((a, b) => a.turnOrder - b.turnOrder);
  }

  sorted.forEach((p) => {
    const row = el('div', 'gh-player');
    if (p.turnOrder === ctx.state.currentTurnIndex && ctx.state.phase !== 'ended') {
      row.classList.add('gh-player-active');
    }
    if (p.isEliminated) row.classList.add('gh-player-eliminated');
    // The leaderboard is informational only — no click-to-focus (the
    // bottom strip handles that affordance) and no rank chip (also strip).

    const f = ctx.factions.find((x) => x.id === p.factionId);
    // Army-head sprite avatar (replaces the plain dot). Wrap it so the
    // red X overlay (for eliminated kingdoms) can be positioned over the
    // avatar without affecting flex layout.
    const avatarWrap = el('div', 'gh-player-avatar-wrap');
    if (f) {
      const avatar = document.createElement('img');
      avatar.src = `/sprites/${p.factionId}_army.png`;
      avatar.className = 'gh-player-avatar';
      avatar.alt = f.name;
      avatar.style.borderColor = f.color;
      avatarWrap.appendChild(avatar);
    } else {
      const dot = el('span', 'gh-player-dot');
      avatarWrap.appendChild(dot);
    }
    if (p.isEliminated) {
      avatarWrap.appendChild(el('span', 'gh-player-eliminated-x', '✗'));
    }
    row.appendChild(avatarWrap);

    const info = el('div', 'gh-player-info');
    info.appendChild(el('div', 'gh-player-name', p.name + (p.id === auth.user?.id ? ' (toi)' : '')));
    if (f) info.appendChild(el('div', 'gh-player-faction', f.name));
    // For AI participants, surface their personality + behavior with
    // hover tooltips that explain HOW the bot plays. Each trait is its own
    // <span> so the descriptions stay tight + distinct.
    if (p.type !== 'human' && (p.aiPersonality || p.aiBehavior)) {
      const traitsWrap = el('div', 'gh-player-ai-traits');
      if (p.aiBehavior) {
        const b = el('span', 'gh-ai-trait', behaviorLabel(p.aiBehavior));
        b.title = behaviorDescription(p.aiBehavior);
        // Stop the click from bubbling to the row (which would steal the
        // camera away — annoying when you're just hovering for info).
        b.addEventListener('click', (e) => e.stopPropagation());
        traitsWrap.appendChild(b);
      }
      if (p.aiBehavior && p.aiPersonality) {
        traitsWrap.appendChild(document.createTextNode(' · '));
      }
      if (p.aiPersonality) {
        const ps = el('span', 'gh-ai-trait', personalityLabel(p.aiPersonality));
        ps.title = personalityDescription(p.aiPersonality);
        ps.addEventListener('click', (e) => e.stopPropagation());
        traitsWrap.appendChild(ps);
      }
      info.appendChild(traitsWrap);
    }
    row.appendChild(info);

    // Score chip — content depends on the active tab. Territories tab
    // shows regions owned; Power tab shows the rounded army-power sum.
    if (isLeaderboardPhase) {
      const idx = findParticipantIndex(ctx.state, p);
      if (leaderboardSortMode === 'power') {
        const power = computeArmyPower(ctx, idx);
        const score = el('div', 'gh-player-score gh-player-score-power', power.toString());
        score.title = `Puissance totale : ${power} (somme atk × pv / 10 de toutes mes meutes)`;
        row.appendChild(score);
      } else {
        const score = el('div', 'gh-player-score', p.regionsOwned.toString());
        score.title = `${p.regionsOwned} coin${p.regionsOwned > 1 ? 's' : ''} de jardin réclamé${p.regionsOwned > 1 ? 's' : ''} NIA`;
        row.appendChild(score);
      }
    }
    wrap.appendChild(row);
  });
  return wrap;
}

function renderTurnTip(ctx: InGameContext): HTMLElement | null {
  // `dice_roll` no longer shows a tip — the 3D dice overlay + the bottom
  // turn-order strip already convey everything. Showing a redundant tip
  // here used to leave a "Les chats secouent leurs moustaches…" stub on
  // screen during any residual hold time after the ceremony finished.
  if (ctx.state.phase === 'dice_roll') return null;
  if (ctx.state.phase === 'placement') {
    const me = findMyParticipant(ctx.state);
    if (!me) return el('div', 'gh-tip', 'Les chats reniflent pour choisir leur repaire…');
    if (me.turnOrder === ctx.state.currentTurnIndex) {
      const myIdx = findParticipantIndex(ctx.state, me);
      if (myIdx >= 0 && ctx.state.castles[myIdx] === -1) {
        return el(
          'div',
          'gh-tip gh-tip-active',
          '🏰 MIAOU ! Tape sur un coin de jardin pour y poser ton repaire.',
        );
      }
    }
    const active = findParticipantByTurnOrder(ctx.state, ctx.state.currentTurnIndex);
    if (active) {
      return el(
        'div',
        'gh-tip',
        `${active.name} renifle son repaire idéal…`,
      );
    }
  }
  // During 'playing' we no longer show a tip — the bottom turn-order strip
  // (kingdom icons left-to-right) tells the player whose turn it is, and
  // the top HUD highlights "it's me" via colour.
  return null;
}

/**
 * Bottom-of-screen strip showing the play order as kingdom army-head icons
 * left-to-right with ">" separators. The currently-playing kingdom's icon
 * is enlarged (~1.35×) and gets its faction colour as a border. Replaces
 * the "X est en chasse…" text — players just see whose turn it is by
 * which icon is highlighted.
 */
function renderTurnOrderStrip(ctx: InGameContext): HTMLElement | null {
  const ordered: Participant[] = [];
  ctx.state.participants.forEach((p) => ordered.push(p));
  // During the dice ceremony, only show kingdoms whose dice has already
  // been animated — the strip "builds up" left-to-right as each kingdom
  // rolls. Skip rendering altogether if nobody has rolled yet.
  let filtered = ordered;
  if (ctx.state.phase === 'dice_roll') {
    filtered = ordered.filter((p) => diceDisplayValues.has(p.id));
    if (filtered.length === 0) return null;
  }
  filtered.sort((a, b) => a.turnOrder - b.turnOrder);
  const wrap = el('div', 'gh-order-strip');

  // RANKED badge — sits to the LEFT of the patrouille pill so the
  // player sees the mode at a glance. Hidden in casual rooms.
  if (ctx.state.isRanked) {
    const badge = el('div', 'gh-order-ranked-badge');
    const ico = document.createElement('img');
    ico.src = '/sprites/iconchat_champion.png';
    ico.className = 'gh-order-ranked-badge-icon';
    ico.alt = '';
    badge.appendChild(ico);
    badge.appendChild(el('span', 'gh-order-ranked-badge-text', 'CLASSÉ'));
    wrap.appendChild(badge);
  }

  // Patrouille pill on the LEFT of the strip — lancier icon + the
  // current turn number. Hidden during the dice ceremony (no turn yet).
  // The word "Patrouille" sits in its own span so mobile CSS can hide
  // just the label (saving space) while keeping the number visible.
  if (ctx.state.phase === 'playing' && ctx.state.turnNumber > 0) {
    const pill = el('div', 'gh-order-patrouille');
    const ico = document.createElement('img');
    ico.src = '/sprites/iconchat_lancier.png';
    ico.className = 'gh-order-patrouille-icon';
    ico.alt = '';
    pill.appendChild(ico);
    const text = el('span', 'gh-order-patrouille-text');
    text.appendChild(el('span', 'gh-order-patrouille-label', 'Patrouille '));
    text.appendChild(el('span', 'gh-order-patrouille-num', String(ctx.state.turnNumber)));
    pill.appendChild(text);
    wrap.appendChild(pill);
  }

  for (let i = 0; i < filtered.length; i++) {
    const p = filtered[i]!;
    const isActive =
      ctx.state.phase !== 'dice_roll' &&
      p.turnOrder === ctx.state.currentTurnIndex;
    const classes = ['gh-order-slot'];
    if (isActive) classes.push('gh-order-slot-active');
    if (p.isEliminated) classes.push('gh-order-slot-eliminated');
    // Ranked mode tags the slot so CSS shows the per-player colour ring
    // (instead of the kingdom colour, which would duplicate when two
    // players picked the same faction).
    if (ctx.state.isRanked && p.colorTint) classes.push('gh-order-slot-tinted');
    const slot = el('div', classes.join(' '));
    const faction = ctx.factions.find((x) => x.id === p.factionId);
    const img = document.createElement('img');
    img.src = `/sprites/${p.factionId}_army.png`;
    img.className = 'gh-order-icon';
    img.alt = faction?.name ?? p.name;
    // Outer ring colour: in ranked mode, use the unique per-player tint
    // generated server-side; otherwise fall back to the faction colour.
    const ringColor = (ctx.state.isRanked && p.colorTint) ? p.colorTint : faction?.color;
    if (ringColor) slot.style.setProperty('--slot-color', ringColor);
    slot.title = `${p.name}${faction ? ' · ' + faction.name : ''}${p.isEliminated ? ' (éliminé)' : ''}`;
    slot.appendChild(img);
    // Red X overlay for eliminated kingdoms.
    if (p.isEliminated) {
      slot.appendChild(el('span', 'gh-order-eliminated-x', '✗'));
    }
    // During dice ceremony, the badge shows the CURRENTLY animated value
    // (round 0 result first, then updated when a tie-breaker re-rolls
    // this participant). Outside dice_roll we fall back to the final value.
    if (ctx.state.phase === 'dice_roll') {
      const v = diceDisplayValues.get(p.id);
      if (v !== undefined) {
        const badge = el('span', 'gh-order-dice', v.toString());
        slot.appendChild(badge);
      }
    } else if (p.diceRoll > 0 && ctx.state.phase === 'placement') {
      // Helpful during placement too — players can still see who rolled what.
      const badge = el('span', 'gh-order-dice', p.diceRoll.toString());
      slot.appendChild(badge);
    }
    slot.style.cursor = 'pointer';
    slot.addEventListener('click', () => focusOnPlayer(ctx, p));
    // Right-click → context menu with a single "Miauler en privé" entry.
    // Opens a private chat channel with this participant. Skip on the
    // local player's own slot (you can't DM yourself).
    const me = findMyParticipant(ctx.state);
    if (!me || p.id !== me.id) {
      slot.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        showSlotContextMenu(e.clientX, e.clientY, p);
      });
    }
    wrap.appendChild(slot);
    if (i < filtered.length - 1) {
      const sep = el('span', 'gh-order-sep', '›');
      wrap.appendChild(sep);
    }
  }
  return wrap;
}

/**
 * Right-click context menu for a strip slot — two entries:
 *   • "Miauler en privé" → opens the private chat channel.
 *   • "Insulter ▸" → opens a sub-menu of canned cat-themed insults; click
 *     one and it gets sent as a private message to the target. If the
 *     target is an AI, the server auto-replies with the faction's
 *     `insultReply` voice line.
 * Closes on any outside click.
 */
function showSlotContextMenu(x: number, y: number, p: Participant): void {
  // Tear down any existing menu first — only one open at a time.
  document.querySelectorAll('.gh-slot-ctxmenu').forEach((n) => n.remove());
  const menu = document.createElement('div');
  menu.className = 'gh-slot-ctxmenu';
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;

  // Helper — builds a menu entry with an iconchat sprite + text label
  // so the right-click menu never relies on emoji glyphs (which render
  // inconsistently across Windows / macOS / Linux).
  const mkMenuItem = (iconchat: string, label: string, onClick: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gh-slot-ctxmenu-item';
    const ic = document.createElement('img');
    ic.src = `/sprites/iconchat_${iconchat}.png`;
    ic.className = 'gh-slot-ctxmenu-icon';
    ic.alt = '';
    b.appendChild(ic);
    b.appendChild(document.createTextNode(' ' + label));
    b.addEventListener('click', onClick);
    return b;
  };

  // Entry 1 — open a private chat channel with this participant.
  menu.appendChild(mkMenuItem('crier', `Miauler en privé à ${p.name}`, () => {
    openPrivateChannel(p);
    menu.remove();
  }));

  // Entry 2 — "Insulter ▸" opens an inline list of insult choices.
  menu.appendChild(mkMenuItem('refus', `Insulter ${p.name} ▸`, () => {
    void openInsultSubmenu(menu, p);
  }));

  document.body.appendChild(menu);
  // Clamp inside the viewport — without this, a slot near the bottom of
  // the screen produces a menu that gets clipped by the window border.
  clampMenuToViewport(menu);

  // Dismiss on outside click — single-shot listener.
  const dismiss = (e: MouseEvent): void => {
    if (!menu.contains(e.target as Node)) {
      menu.remove();
      document.removeEventListener('click', dismiss, true);
    }
  };
  // Defer one tick so the click that opened the menu doesn't immediately close it.
  setTimeout(() => document.addEventListener('click', dismiss, true), 0);
}

/**
 * Position helper — re-anchor a floating menu so it stays fully inside the
 * viewport. If the menu would overflow the right/bottom edges we flip it
 * so its right/bottom corner is `MARGIN` px from the edge; on the
 * top/left we clamp to a minimum of `MARGIN` so it never goes negative.
 * If the menu is taller than the viewport itself we cap its height and
 * let the inner list scroll (`overflow-y: auto` set inline).
 */
function clampMenuToViewport(menu: HTMLElement): void {
  const MARGIN = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Cap height first so the rect measurement reflects the visible size.
  const maxH = vh - MARGIN * 2;
  menu.style.maxHeight = `${maxH}px`;
  menu.style.overflowY = 'auto';
  const rect = menu.getBoundingClientRect();
  let left = parseFloat(menu.style.left) || rect.left;
  let top = parseFloat(menu.style.top) || rect.top;
  // Right edge: if overflowing, slide left so the right border sits at
  // `vw - MARGIN`. If even that's not enough (menu wider than viewport),
  // pin to `MARGIN` so the leftmost part stays visible.
  if (left + rect.width > vw - MARGIN) {
    left = Math.max(MARGIN, vw - rect.width - MARGIN);
  }
  // Bottom edge: same idea vertically.
  if (top + rect.height > vh - MARGIN) {
    top = Math.max(MARGIN, vh - rect.height - MARGIN);
  }
  // Never go negative.
  if (left < MARGIN) left = MARGIN;
  if (top < MARGIN) top = MARGIN;
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

/**
 * Inline submenu that replaces the parent menu's items with the list of
 * available insults. Picking one sends a private chat message to the
 * target via the active Colyseus room.
 */
async function openInsultSubmenu(parentMenu: HTMLElement, p: Participant): Promise<void> {
  let insults: InsultDef[] = [];
  try {
    insults = await fetchInsults();
  } catch (err) {
    console.error('Failed to load insults', err);
    return;
  }
  // Replace the menu content with the insult list.
  parentMenu.innerHTML = '';
  parentMenu.classList.add('gh-slot-ctxmenu-insults');
  const header = document.createElement('div');
  header.className = 'gh-slot-ctxmenu-header';
  header.textContent = `😾 Insulter ${p.name}`;
  parentMenu.appendChild(header);
  for (const ins of insults) {
    const choice = document.createElement('button');
    choice.type = 'button';
    choice.className = 'gh-slot-ctxmenu-item gh-slot-ctxmenu-insult-item';
    choice.textContent = ins.text;
    choice.addEventListener('click', () => {
      // Send as a private chat message — server detects the insult and
      // (if the target is an AI) schedules a faction `insultReply`.
      if (currentCtx?.room) {
        try {
          currentCtx.room.send('chat.send', { text: ins.text, targetId: p.id });
        } catch { /* room closed */ }
      }
      parentMenu.remove();
    });
    parentMenu.appendChild(choice);
  }
  // The menu just grew (2 items → 13 items + header). Re-clamp so the
  // tall insult list still fits the viewport — defer to next frame so
  // the browser has laid out the new children.
  requestAnimationFrame(() => clampMenuToViewport(parentMenu));
}

// ===========================================================================
// 3D dice ceremony — animated dice roll at the start of each game.
// ===========================================================================

/** Rotations (in degrees) that bring each cube face to the front of the
 *  camera. Indexed by face value 1-6. */
const DICE_FACE_ROTATIONS: Record<number, [number, number]> = {
  1: [0, 0],
  2: [0, -90],
  3: [0, 180],
  4: [0, 90],
  5: [-90, 0],
  6: [90, 0],
};

/**
 * Compute the next rotation value so the cube tumbles forward by at least
 * `minTurns` full revolutions AND lands with `(result mod 360) === target`.
 *
 * Cumulative accumulation needs this correction: simply adding
 * `360*3 + target` to the current accum only lands on `target` when the
 * cube was previously at a multiple of 360. After a non-trivial previous
 * roll, the cube is mid-rotation and the naive formula lands on the wrong
 * face. This helper measures the current effective rotation and rounds
 * forward to the next exact target position.
 */
function reachDiceFace(currentAccum: number, target: number, minTurns: number): number {
  const base = currentAccum + minTurns * 360;
  const wantMod = (((target % 360) + 360) % 360);
  const haveMod = (((base % 360) + 360) % 360);
  let diff = wantMod - haveMod;
  if (diff < 0) diff += 360; // always rotate forward (positive direction)
  return base + diff;
}

/** Build (once) the overlay holding the 3D dice. Subsequent calls return the
 *  same element so we can drive a single live animation across renders. */
function ensureDiceOverlay(): HTMLElement {
  let overlay = document.getElementById('dice-ceremony-overlay');
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = 'dice-ceremony-overlay';
  overlay.className = 'dice-overlay';
  const stage = document.createElement('div');
  stage.className = 'dice-stage';
  const throwWrap = document.createElement('div');
  throwWrap.className = 'dice-throw-wrap';
  const cube = document.createElement('div');
  cube.className = 'dice-cube';
  cube.id = 'dice-cube';
  for (let f = 1; f <= 6; f++) {
    const face = document.createElement('div');
    face.className = `dice-face dice-face-${f}`;
    const bg = document.createElement('img');
    bg.className = 'dice-face-bg';
    bg.alt = '';
    face.appendChild(bg);
    const num = document.createElement('span');
    num.className = 'dice-face-num';
    num.textContent = f.toString();
    face.appendChild(num);
    cube.appendChild(face);
  }
  throwWrap.appendChild(cube);
  stage.appendChild(throwWrap);
  const label = document.createElement('div');
  label.className = 'dice-label';
  label.id = 'dice-label';
  stage.appendChild(label);
  overlay.appendChild(stage);
  document.body.appendChild(overlay);
  return overlay;
}

function removeDiceOverlay(): void {
  const overlay = document.getElementById('dice-ceremony-overlay');
  if (overlay) overlay.remove();
}

/** Convenience: a Promise that resolves after `ms` ms. */
function diceSleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Poll-based wait: resolves once `predicate()` returns true. Used by
 * the dice ceremony to await server-driven state changes (cursor
 * advancing, a participant's value being revealed) without subscribing
 * to every individual Colyseus state listener. 80 ms tick is plenty
 * for a UX that's already gated by a multi-second animation budget.
 */
function waitForState(predicate: () => boolean): Promise<void> {
  return new Promise<void>((resolve) => {
    if (predicate()) { resolve(); return; }
    const id = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(id);
        resolve();
      }
    }, 80);
  });
}

/** Kick off the ceremony if not already running. Idempotent — multiple
 *  re-renders during dice_roll won't restart it.
 *
 *  We no longer wait for "all values rolled" — the server now reveals
 *  values strictly one at a time, so we just need at least one
 *  participant present and the cursor set. */
function startDiceCeremony(ctx: InGameContext): void {
  if (diceCeremonyRunning) return;
  if (ctx.state.participants.length === 0) return;
  diceCeremonyRunning = true;
  diceDisplayValues.clear();
  void runDiceCeremony(ctx);
}

/** Tear-down for when dice_roll phase ends. */
function cleanupDiceCeremony(): void {
  diceCeremonyRunning = false;
  diceDisplayValues.clear();
  removeDiceOverlay();
}

/**
 * Spawn a pixel-art confetti burst around the dice on a roll. Each piece is
 * either a coloured square (faction colour) or a tiny kingdom army-head
 * sprite. Pieces fly outward in an upward cone, with each individual piece
 * spinning infinitely on its own until the outer fly-and-fade animation
 * removes it. Positioning uses `getBoundingClientRect()` on the dice so the
 * burst is always perfectly centered on the cube, regardless of HUD layout.
 */
function spawnDiceConfetti(
  throwWrap: HTMLElement,
  factionColor: string,
  factionId: string,
): void {
  const rect = throwWrap.getBoundingClientRect();
  // Skip if the dice isn't visible yet (rect is empty).
  if (rect.width <= 0 || rect.height <= 0) return;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const container = document.createElement('div');
  container.className = 'dice-confetti-container';
  container.style.left = `${cx}px`;
  container.style.top = `${cy}px`;
  document.body.appendChild(container);

  // ~36 small bare colour pixels + ~6 faction-head sprites. The pixels are
  // plain coloured rectangles — no border, no shadow — so they read as
  // crisp pixel confetti rather than tiny squashed buttons.
  const NUM_PIXELS = 36;
  const NUM_HEADS = 6;
  const total = NUM_PIXELS + NUM_HEADS;
  for (let i = 0; i < total; i++) {
    const isHead = i >= NUM_PIXELS;
    const piece = document.createElement('div');
    piece.className = 'dice-confetti-piece';
    // Upward cone: angle 200°..340° (math convention; in screen coords
    // this means mostly negative-Y i.e. UP, with horizontal spread).
    const angleDeg = 200 + Math.random() * 140;
    const angleRad = (angleDeg * Math.PI) / 180;
    const speed = 140 + Math.random() * 200;
    const dx = Math.cos(angleRad) * speed;
    const dy = Math.sin(angleRad) * speed;
    const duration = 1.6 + Math.random() * 1.4; // 1.6 – 3.0 s
    piece.style.setProperty('--dx', `${dx}px`);
    piece.style.setProperty('--dy', `${dy}px`);
    piece.style.setProperty('--duration', `${duration}s`);
    // Tiny initial offset so pieces don't all overlap at frame 0.
    piece.style.setProperty('--ox', `${(Math.random() - 0.5) * 22}px`);
    piece.style.setProperty('--oy', `${(Math.random() - 0.5) * 22}px`);

    // Inner spin wrapper (infinite rotation) lets the outer piece animate
    // translate + fade without conflicting with rotate.
    const spin = document.createElement('div');
    const cw = Math.random() < 0.5;
    spin.className = cw ? 'dice-confetti-spin' : 'dice-confetti-spin ccw';
    const spinSpeed = 0.5 + Math.random() * 0.9; // 0.5 – 1.4 s per turn
    spin.style.setProperty('--spin-speed', `${spinSpeed}s`);

    if (!isHead) {
      // Plain coloured pixel — no border, no shadow.
      const sq = document.createElement('div');
      sq.className = 'dice-confetti-square';
      sq.style.background = factionColor;
      const size = 4 + Math.random() * 5; // 4 – 9 px tiny pixels
      sq.style.width = `${size}px`;
      sq.style.height = `${size}px`;
      spin.appendChild(sq);
    } else {
      const img = document.createElement('img');
      img.className = 'dice-confetti-head';
      img.src = `/sprites/${factionId}_army.png`;
      img.alt = '';
      img.decoding = 'async';
      const size = 22 + Math.random() * 12; // 22 – 34 px
      img.style.width = `${size}px`;
      img.style.height = `${size}px`;
      spin.appendChild(img);
    }

    piece.appendChild(spin);
    // Remove this piece when its outer fly animation ends. The infinite
    // spin animation is killed with it.
    piece.addEventListener('animationend', (e) => {
      if (e.animationName === 'dice-confetti-fly') piece.remove();
    });
    container.appendChild(piece);
  }

  // Safety net: drop the whole container after 4 s in case animationend
  // events get lost (e.g. tab backgrounded).
  window.setTimeout(() => {
    if (container.parentNode) container.remove();
  }, 4000);
}

/**
 * Wait for the user to click the dice, with a safety timeout so a slow /
 * absent player doesn't block the whole ceremony forever. Returns once the
 * promise resolves; the throw-wrap leaves `.dice-clickable` either way.
 */
function waitForDiceClick(throwWrap: HTMLElement, timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    throwWrap.classList.add('dice-clickable');
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      throwWrap.classList.remove('dice-clickable');
      throwWrap.removeEventListener('click', onClick);
      window.clearTimeout(timer);
      resolve();
    };
    const onClick = (): void => finish();
    throwWrap.addEventListener('click', onClick);
    const timer = window.setTimeout(finish, timeoutMs);
  });
}

/** Short cat-themed "let the game begin" messages. */
const DICE_GAME_START_MESSAGES = [
  '🐾 Que la chasse commence !',
  'MIAOU ! C\'est parti !',
  'À l\'attaque, les chats !',
  'Sortez les griffes !',
  '🐾 GO ! Bonne patrouille !',
];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

/**
 * Typewriter effect: clear `el` then progressively reveal `text` character
 * by character at `msPerChar` (default 22 ms). Non-blocking — returns
 * immediately and ticks via setTimeout. A module-level token cancels any
 * in-flight typewriter when a new one starts, so back-to-back
 * `label.textContent` updates don't cause flicker / race interleaving.
 */
let typewriterToken = 0;
function typeOut(el: HTMLElement, text: string, msPerChar: number = 18): void {
  const myToken = ++typewriterToken;
  el.textContent = '';
  let i = 0;
  const tick = (): void => {
    if (typewriterToken !== myToken) return; // superseded by a newer typeOut
    if (i >= text.length) return;
    i++;
    el.textContent = text.slice(0, i);
    window.setTimeout(tick, msPerChar);
  };
  window.setTimeout(tick, msPerChar);
}

/**
 * Animate one participant's single roll. Sets dice colour + face image,
 * waits for the server to reveal the value (after the local player
 * clicks, or after the server auto-rolls for AI / disconnected humans),
 * then tumbles + spawns a confetti burst. Returns once the post-roll
 * 2-second hold completes.
 *
 * The flow is server-paced: the click only TRIGGERS the reveal (by
 * sending `dice.roll` to the server); the actual value comes back via
 * a state patch on `p.diceRoll`, and every client animates the same
 * single tumble in lockstep. This way, in a 2-human game, player B
 * never sees A's result before A has clicked.
 */
async function animateOneDiceRoll(
  ctx: InGameContext,
  p: Participant,
  isLocal: boolean,
  cube: HTMLElement,
  throwWrap: HTMLElement,
  label: HTMLElement,
  accum: { x: number; y: number },
): Promise<void> {
  const faction = ctx.factions.find((x) => x.id === p.factionId);
  const color = faction?.color ?? '#888';
  // Set faction colour + army-head on every face. The actual value is
  // kept HIDDEN behind a "?" until the dice finishes rolling — preserves
  // the tension / curiosity of waiting for the reveal. The real number
  // is only written into the faces after the post-tumble settle.
  for (let f = 1; f <= 6; f++) {
    const face = cube.querySelector<HTMLElement>(`.dice-face-${f}`);
    if (!face) continue;
    face.style.setProperty('--face-color', color);
    const bg = face.querySelector<HTMLImageElement>('.dice-face-bg');
    if (bg) bg.src = `/sprites/${p.factionId}_army.png`;
    const numEl = face.querySelector<HTMLElement>('.dice-face-num');
    if (numEl) {
      numEl.textContent = '?';
      numEl.style.fontSize = '38px';
      numEl.classList.remove('dice-face-num-revealed');
    }
  }
  label.style.borderColor = color;

  // ── Click gate ────────────────────────────────────────────────────
  // Server has set `diceCurrentIndex` to this participant's seat. For
  // the local player we wait for their click and then ask the server
  // to reveal; for non-local players (or if we missed the click), we
  // just wait for the server's state patch.
  if (isLocal && p.diceRoll === 0) {
    typeOut(label, 'À toi ! Clique le dé.');
    // 30s gate: matches the server's auto-roll window for disconnected
    // humans. After that, the server will reveal on its own.
    await waitForDiceClick(throwWrap, 30000);
    try { ctx.room.send('dice.roll'); } catch { /* room closed */ }
  } else if (!isLocal && p.diceRoll === 0) {
    typeOut(label, `${p.name} prépare son tirage…`);
  }
  // Wait for the server to publish the value. This is what gates
  // everyone in lockstep — until `p.diceRoll > 0`, no client animates.
  await waitForState(
    () => ctx.state.phase !== 'dice_roll' || p.diceRoll > 0,
  );
  if (ctx.state.phase !== 'dice_roll') return;

  // Recompute textual format from the now-revealed value.
  const value = p.diceRoll;
  const numText = value.toString();
  const numFontPx = numText.length >= 3 ? 24 : numText.length === 2 ? 32 : 38;

  if (!isLocal) {
    typeOut(label, `${p.name} tire…`);
    await diceSleep(450);
  }
  if (ctx.state.phase !== 'dice_roll') return;

  // Throw the dice across the table: travel left → right, peak in the air,
  // bounce once or twice, then settle. The translateX dominates so it
  // reads like a sideways toss, not a vertical drop. The cube tumbles a
  // bit longer too (5 turns per axis) for a proper "rolling on the table"
  // feel before stopping.
  throwWrap.classList.remove('throwing');
  void throwWrap.offsetWidth; // reflow so the animation can restart
  throwWrap.classList.add('throwing');

  // Sound timing aligned with the dice-throw keyframes (total 1100 ms):
  //   • throw whoosh at 0 % — fires immediately
  //   • impact at 72 % (~790 ms) — first table hit
  //   • bounce at 80 % (~880 ms) — first vertical rebound
  //   • bounce at 92 % (~1010 ms) — final small rebound
  // Reveal sound fires AFTER the await below, alongside the number flip.
  playDiceThrow();
  window.setTimeout(() => playDiceImpact(), 790);
  window.setTimeout(() => playDiceBounce(), 880);
  window.setTimeout(() => playDiceBounce(), 1010);
  const targetFace = 1 + Math.floor(Math.random() * 6);
  const [tx, ty] = DICE_FACE_ROTATIONS[targetFace] ?? [0, 0];
  accum.x = reachDiceFace(accum.x, tx, 5);
  accum.y = reachDiceFace(accum.y, ty, 5);
  // Ease-out: rotation decelerates as the dice "rolls" to a stop.
  cube.style.transition = 'transform 1100ms cubic-bezier(0.18, 0.58, 0.25, 1)';
  cube.style.transform = `rotateX(${accum.x}deg) rotateY(${accum.y}deg)`;
  await diceSleep(1100);
  if (ctx.state.phase !== 'dice_roll') return;

  // REVEAL: the dice has stopped. Replace the "?" on every face with the
  // actual drawn value, in the correctly-sized font for 1/2/3-digit numbers.
  // The camera-forward face was just a "?" a moment ago — it now flips to
  // the real number, building the moment of discovery the player came for.
  for (let f = 1; f <= 6; f++) {
    const face = cube.querySelector<HTMLElement>(`.dice-face-${f}`);
    if (!face) continue;
    const numEl = face.querySelector<HTMLElement>('.dice-face-num');
    if (numEl) {
      numEl.textContent = numText;
      numEl.style.fontSize = `${numFontPx}px`;
      numEl.classList.add('dice-face-num-revealed');
    }
  }

  // Audio sting for the reveal — triad arpeggio + shimmer. Paired with the
  // sparkle of `playSparkle` for the confetti burst it's a satisfying
  // one-two of discovery and celebration.
  playDiceReveal();
  spawnDiceConfetti(throwWrap, color, p.factionId);
  playSparkle();
  typeOut(label, isLocal ? `Tu fais ${value} !` : `${p.name} : ${value} !`);
  diceDisplayValues.set(p.id, value);
  forceHudRender();
  await diceSleep(2000);
}

/**
 * Drive the dice ceremony — one tumble per participant, strictly
 * sequential and server-paced via `state.diceCurrentIndex`.
 *
 * The server holds the pre-rolled values privately and only publishes
 * one at a time (after the local player clicks, or after an auto-roll
 * timer fires for AI / disconnected humans). The client never iterates
 * through participants on its own — it follows the cursor.
 *
 * That fixes the old race where, in a 2-human game, player B's screen
 * would already be animating A's roll while A hadn't even clicked yet.
 */
async function runDiceCeremony(ctx: InGameContext): Promise<void> {
  const overlay = ensureDiceOverlay();
  const cube = overlay.querySelector<HTMLElement>('#dice-cube');
  const throwWrap = overlay.querySelector<HTMLElement>('.dice-throw-wrap');
  const label = overlay.querySelector<HTMLElement>('#dice-label');
  if (!cube || !throwWrap || !label) return;

  const me = findMyParticipant(ctx.state);
  const myId = me?.id ?? null;

  const accum = { x: 0, y: 0 };
  const animatedIndexes = new Set<number>();

  // Loop until the phase ends or the server signals "all rolled" by
  // setting diceCurrentIndex to -1.
  while (ctx.state.phase === 'dice_roll') {
    // Wait for the server's cursor to land on a fresh seat we
    // haven't animated yet.
    await waitForState(() => {
      if (ctx.state.phase !== 'dice_roll') return true;
      const idx = ctx.state.diceCurrentIndex;
      return idx >= 0 && !animatedIndexes.has(idx);
    });
    if (ctx.state.phase !== 'dice_roll') break;
    const idx = ctx.state.diceCurrentIndex;
    if (idx < 0) break; // server signalled completion

    // Find the participant at this seat. If they're not in the
    // replicated state yet (shouldn't happen — `tryStartGame` builds
    // them first), bail to avoid spinning forever.
    let cur: Participant | undefined;
    ctx.state.participants.forEach((p) => {
      if (p.index === idx) cur = p;
    });
    if (!cur) break;

    const isLocal = myId !== null && cur.id === myId;
    await animateOneDiceRoll(ctx, cur, isLocal, cube, throwWrap, label, accum);
    animatedIndexes.add(idx);
  }

  // End-of-ceremony short cat-themed announcement. Plays while the
  // server is waiting out the extended "last roll" hold before it
  // flips the phase to `placement`.
  if (ctx.state.phase === 'dice_roll') {
    label.style.borderColor = '#e8d8a8'; // accent gold
    label.classList.add('dice-label-celebrate');
    typeOut(label, pickRandom(DICE_GAME_START_MESSAGES));
    await diceSleep(2500);
    label.classList.remove('dice-label-celebrate');
  }

  // Final reveal: blank the label so the placement phase enters cleanly.
  if (overlay.parentNode) {
    label.textContent = '';
  }
  // Note: we DON'T remove the overlay here — cleanupDiceCeremony() does
  // when phase transitions. This lets us animate the last result fully.
}

/**
 * Top-right crown widget. Combines a small crown button + a sliding panel
 * that contains the participants/leaderboard. Hovering the button (or the
 * panel itself) slides the panel in from the right edge. Moving the mouse
 * out triggers a short delay before sliding the panel back out — so the
 * user can move the cursor from button to panel without it disappearing.
 *
 * The "is open" flag lives at module scope so the panel survives HUD
 * re-renders triggered by state patches.
 */
/**
 * Cat-themed audio-settings widget — slides in on hover next to the crown.
 * Two sliders (sfx + music) with paw-pad/note iconography, plus a
 * "skip track" button so the player can shuffle to the next song without
 * waiting for the current one to finish. All settings persist to
 * localStorage so the player's tuning carries across sessions.
 */
function renderSettingsHost(): HTMLElement {
  const host = el('div', settingsOpen ? 'gh-settings-host open' : 'gh-settings-host');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gh-settings-btn';
  const img = document.createElement('img');
  img.src = '/sprites/ui_bouton_editer.png';
  img.className = 'gh-settings-btn-icon';
  img.alt = '';
  btn.appendChild(img);
  btn.title = 'Réglages des sons — survole pour ouvrir';
  host.appendChild(btn);

  // Sliding panel — cat-themed audio controls.
  const panel = el('div', 'gh-settings-panel');
  panel.appendChild(el('h3', 'gh-settings-title', 'Réglages mignons'));

  // SFX slider — paw pad icon on the left, ear icon as "louder" cue right.
  const sfxRow = el('div', 'gh-settings-row');
  const sfxIcon = document.createElement('img');
  sfxIcon.src = '/sprites/iconchat_neutre.png';
  sfxIcon.className = 'gh-settings-icon';
  sfxIcon.alt = '';
  sfxRow.appendChild(sfxIcon);
  const sfxLabel = el('div', 'gh-settings-label');
  sfxLabel.appendChild(el('div', 'gh-settings-row-title', 'Pattounes & miaous'));
  const sfxSlider = document.createElement('input');
  sfxSlider.type = 'range';
  sfxSlider.min = '0';
  sfxSlider.max = '100';
  sfxSlider.step = '1';
  sfxSlider.value = Math.round(getMasterVolume() * 100).toString();
  sfxSlider.className = 'gh-settings-slider';
  const sfxValue = el('span', 'gh-settings-value', `${sfxSlider.value}%`);
  sfxSlider.addEventListener('input', () => {
    const v = parseInt(sfxSlider.value, 10) / 100;
    setMasterVolume(v);
    sfxValue.textContent = `${sfxSlider.value}%`;
  });
  // Preview on commit — short paw step so the player hears the new level.
  sfxSlider.addEventListener('change', () => playPawStep());
  sfxLabel.appendChild(sfxSlider);
  sfxRow.appendChild(sfxLabel);
  sfxRow.appendChild(sfxValue);
  panel.appendChild(sfxRow);

  // Music slider + skip button.
  const musicRow = el('div', 'gh-settings-row');
  const musicIcon = document.createElement('img');
  musicIcon.src = '/sprites/iconchat_valide.png';
  musicIcon.className = 'gh-settings-icon';
  musicIcon.alt = '';
  musicRow.appendChild(musicIcon);
  const musicLabel = el('div', 'gh-settings-label');
  const musicTitleRow = el('div', 'gh-settings-row-title-row');
  musicTitleRow.appendChild(el('span', 'gh-settings-row-title', 'Ronron de fond'));
  const trackCount = getMusicTrackCount();
  const curIdx = getCurrentMusicIndex();
  if (trackCount && trackCount > 0 && curIdx !== null) {
    musicTitleRow.appendChild(el('span', 'gh-settings-track', `· piste ${curIdx}/${trackCount}`));
  }
  musicLabel.appendChild(musicTitleRow);
  const musicSlider = document.createElement('input');
  musicSlider.type = 'range';
  musicSlider.min = '0';
  musicSlider.max = '100';
  musicSlider.step = '1';
  musicSlider.value = Math.round(getMusicVolume() * 100).toString();
  musicSlider.className = 'gh-settings-slider';
  const musicValue = el('span', 'gh-settings-value', `${musicSlider.value}%`);
  musicSlider.addEventListener('input', () => {
    const v = parseInt(musicSlider.value, 10) / 100;
    setMusicVolume(v);
    musicValue.textContent = `${musicSlider.value}%`;
  });
  musicLabel.appendChild(musicSlider);
  // Mute toggle + skip button row.
  const musicControls = el('div', 'gh-settings-music-controls');
  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.className = 'gh-settings-mini-btn';
  muteBtn.textContent = isMusicMuted() ? '🔇 Muet' : '🔊 Son';
  muteBtn.addEventListener('click', () => {
    const newMuted = !isMusicMuted();
    setMusicMuted(newMuted);
    muteBtn.textContent = newMuted ? '🔇 Muet' : '🔊 Son';
  });
  musicControls.appendChild(muteBtn);
  const skipBtn = document.createElement('button');
  skipBtn.type = 'button';
  skipBtn.className = 'gh-settings-mini-btn';
  skipBtn.textContent = '⏭ Piste suivante';
  skipBtn.addEventListener('click', () => {
    skipToNextMusic();
    // No re-render hook → next render via state patch will update the
    // displayed track number. For now we tweak the visible label locally.
    setTimeout(() => {
      const idx = getCurrentMusicIndex();
      const total = getMusicTrackCount();
      if (idx !== null && total !== null) {
        const t = musicTitleRow.querySelector<HTMLElement>('.gh-settings-track');
        if (t) t.textContent = `· piste ${idx}/${total}`;
      }
    }, 50);
  });
  musicControls.appendChild(skipBtn);
  musicLabel.appendChild(musicControls);
  musicRow.appendChild(musicLabel);
  musicRow.appendChild(musicValue);
  panel.appendChild(musicRow);

  // ── Camera-follow toggle ────────────────────────────────────────────
  // Sits below the sound/music rows so all player preferences live in
  // the same panel. Persists via setCameraFollow() → localStorage.
  const camRow = el('div', 'gh-settings-row gh-settings-row-toggle');
  const camIcon = document.createElement('img');
  camIcon.src = '/sprites/iconchat_interrogation.png';
  camIcon.className = 'gh-settings-icon';
  camIcon.alt = '';
  camRow.appendChild(camIcon);
  const camLabelWrap = el('div', 'gh-settings-label');
  camLabelWrap.appendChild(el('div', 'gh-settings-row-title', 'Suivre la caméra'));
  camLabelWrap.appendChild(el(
    'div',
    'gh-settings-row-sub',
    'Glisse automatiquement vers les actions',
  ));
  camRow.appendChild(camLabelWrap);
  const camToggle = document.createElement('input');
  camToggle.type = 'checkbox';
  camToggle.checked = cameraFollowEnabled;
  camToggle.className = 'gh-settings-checkbox';
  camToggle.addEventListener('change', () => {
    setCameraFollow(camToggle.checked);
  });
  camRow.appendChild(camToggle);
  panel.appendChild(camRow);

  panel.appendChild(el(
    'div',
    'gh-settings-hint',
    'Les volumes sont automatiquement équilibrés entre les pistes 🐾',
  ));
  host.appendChild(panel);

  const open = (): void => {
    if (settingsHideTimer !== null) {
      window.clearTimeout(settingsHideTimer);
      settingsHideTimer = null;
    }
    if (!settingsOpen) {
      settingsOpen = true;
      host.classList.add('open');
    }
  };
  const closeDelayed = (): void => {
    if (settingsHideTimer !== null) window.clearTimeout(settingsHideTimer);
    settingsHideTimer = window.setTimeout(() => {
      settingsOpen = false;
      host.classList.remove('open');
      settingsHideTimer = null;
    }, 220);
  };
  btn.addEventListener('mouseenter', open);
  btn.addEventListener('mouseleave', closeDelayed);
  panel.addEventListener('mouseenter', open);
  panel.addEventListener('mouseleave', closeDelayed);
  btn.addEventListener('click', () => {
    if (settingsOpen) closeDelayed();
    else open();
  });
  return host;
}

function renderLeaderboardHost(ctx: InGameContext): HTMLElement {
  const host = el('div', leaderboardOpen ? 'gh-leaderboard-host open' : 'gh-leaderboard-host');
  // Crown button — always visible
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gh-leaderboard-btn';
  const img = document.createElement('img');
  img.src = '/sprites/crown.png';
  img.className = 'gh-leaderboard-btn-icon';
  img.alt = '';
  btn.appendChild(img);
  btn.title = 'Classement des chats — survole pour voir qui mène la chasse';
  host.appendChild(btn);

  // Sliding panel — reuses renderParticipantsPanel for the actual content.
  const panel = el('div', 'gh-leaderboard-panel');
  const headerWrap = el('div', 'gh-leaderboard-header');
  const titleIcon = document.createElement('img');
  titleIcon.src = '/sprites/crown.png';
  titleIcon.className = 'gh-leaderboard-title-icon';
  titleIcon.alt = '';
  headerWrap.appendChild(titleIcon);
  headerWrap.appendChild(el('h3', 'gh-leaderboard-title', 'NIA·king des chats'));
  panel.appendChild(headerWrap);
  const body = renderParticipantsPanel(ctx);
  body.classList.add('gh-players-in-modal');
  panel.appendChild(body);

  // Note: the camera-follow toggle moved to the audio/settings panel
  // (next to the sound + music sliders) so all player preferences live
  // in one place. The leaderboard now only shows the ranking.

  host.appendChild(panel);

  // Hover / leave handlers — delay close so the user can travel from
  // button → panel without the panel disappearing.
  const open = (): void => {
    if (leaderboardHideTimer !== null) {
      window.clearTimeout(leaderboardHideTimer);
      leaderboardHideTimer = null;
    }
    if (!leaderboardOpen) {
      leaderboardOpen = true;
      host.classList.add('open');
    }
  };
  const closeDelayed = (): void => {
    if (leaderboardHideTimer !== null) window.clearTimeout(leaderboardHideTimer);
    leaderboardHideTimer = window.setTimeout(() => {
      leaderboardOpen = false;
      host.classList.remove('open');
      leaderboardHideTimer = null;
    }, 220);
  };
  btn.addEventListener('mouseenter', open);
  btn.addEventListener('mouseleave', closeDelayed);
  panel.addEventListener('mouseenter', open);
  panel.addEventListener('mouseleave', closeDelayed);
  // Tap on touch devices: toggle.
  btn.addEventListener('click', () => {
    if (leaderboardOpen) closeDelayed();
    else open();
  });
  return host;
}

/** Count camps owned by participant `idx` (used to enforce the 3-max cap on the client UI). */
function countMyCamps(state: LobbyState, idx: number): number {
  let n = 0;
  for (let r = 0; r < state.regionBuilding.length; r++) {
    if (state.regionOwners[r] === idx && state.regionBuilding[r] === 1) n++;
  }
  return n;
}

function findParticipantByTurnOrder(state: LobbyState, order: number): Participant | undefined {
  for (let i = 0; i < state.participants.length; i++) {
    const p = state.participants[i];
    if (p && p.turnOrder === order) return p;
  }
  return undefined;
}

/**
 * Bottom-left panel: details of the selected region. Shows owner, units, and
 * action buttons (recruit, move) when applicable.
 */
function renderRegionPanel(ctx: InGameContext, regionId: number): HTMLElement | null {
  const wrap = el('div', 'gh-region');
  const header = el('div', 'gh-region-header');
  header.appendChild(el('h3', 'gh-region-title', regionLabel(ctx.state, regionId)));
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'gh-region-close';
  const closeIcon = document.createElement('img');
  closeIcon.src = '/sprites/ui_fermer.png';
  closeIcon.className = 'inline-icon';
  closeIcon.alt = 'Fermer';
  close.appendChild(closeIcon);
  close.title = 'Fermer';
  close.addEventListener('click', () => {
    selectedRegionId = null;
    const gh = document.getElementById('game-hud') as HTMLDivElement | null;
    if (gh) renderHud(gh, ctx);
  });
  header.appendChild(close);
  wrap.appendChild(header);

  // Owner — banner flag + name. The flag is much more recognisable than the
  // little colour dot used previously, especially on a busy map.
  const ownerIdx = ctx.state.regionOwners[regionId] ?? -1;
  const owner = ownerIdx >= 0 ? ctx.state.participants[ownerIdx] : null;
  const ownerLine = el('div', 'gh-region-owner');
  if (owner) {
    ownerLine.appendChild(factionFlagImg(owner.factionId, 'gh-region-flag'));
    const f = ctx.factions.find((x) => x.id === owner.factionId);
    const nameSpan = el(
      'span',
      'gh-region-owner-name',
      `${owner.name}${owner.id === auth.user?.id ? ' (toi)' : ''}`,
    );
    if (f) nameSpan.style.color = f.color;
    ownerLine.appendChild(nameSpan);
  } else {
    ownerLine.appendChild(factionFlagImg('neutre', 'gh-region-flag'));
    ownerLine.appendChild(el('span', 'gh-region-owner-name', 'Personne (chats errants)'));
  }
  wrap.appendChild(ownerLine);

  // Building marker — drives off the per-region regionBuilding tag now.
  const building = ctx.state.regionBuilding[regionId] ?? 0;
  const regionCastleTier = building === 4 ? 3 : building === 3 ? 2 : building === 2 ? 1 : 0;
  const regionIsCamp = building === 1;
  if (regionCastleTier > 0) {
    const box = el('div', 'gh-region-castle');
    const img = document.createElement('img');
    img.src = `/sprites/chateau_tier${regionCastleTier}.png`;
    img.className = 'gh-region-castle-img';
    img.alt = `Repaire niveau ${regionCastleTier}`;
    box.appendChild(img);
    box.appendChild(el('span', 'gh-region-castle-label', `Repaire · niveau ${regionCastleTier}`));
    wrap.appendChild(box);
  } else if (regionIsCamp) {
    const box = el('div', 'gh-region-castle');
    const img = document.createElement('img');
    img.src = '/sprites/camp.png';
    img.className = 'gh-region-castle-img';
    img.alt = 'Coin secret';
    box.appendChild(img);
    box.appendChild(el('span', 'gh-region-castle-label', 'Coin secret · bagarreurs/pêcheurs/furieux'));
    wrap.appendChild(box);
  }

  // Units
  const u = ctx.state.regionUnits[regionId];
  const total = u ? totalUnits(u) : 0;
  const units = el('div', 'gh-region-units');
  if (total === 0) {
    units.textContent = 'Aucun chaton ici. Le coin ronronne tranquille.';
  } else {
    const factionId = owner?.factionId ?? '';
    if ((u?.warriors ?? 0) > 0) units.appendChild(unitChip('warrior', 'Bagarreurs', u!.warriors, factionId));
    if ((u?.archers ?? 0) > 0) units.appendChild(unitChip('archer', 'Pêcheurs d\'oiseaux', u!.archers, factionId));
    if ((u?.berserkers ?? 0) > 0) units.appendChild(unitChip('berserker', 'Furieux', u!.berserkers, factionId));
    if ((u?.crossbowmen ?? 0) > 0) units.appendChild(unitChip('crossbowman', 'Tireurs précis', u!.crossbowmen, factionId));
    if ((u?.paladins ?? 0) > 0) units.appendChild(unitChip('paladin', 'Aristochats', u!.paladins, factionId));
    if ((u?.heroes ?? 0) > 0) units.appendChild(unitChip('hero', 'Légende', u!.heroes, factionId));
  }
  wrap.appendChild(units);

  // Numeric power readout.
  if (u && total > 0) {
    const power = el('div', 'gh-region-power');
    const atk = armyAttack(u);
    const hp = armyHp(u);
    const atkEl = el('span', 'gh-region-power-atk', `⚔ ${atk} griffe`);
    const hpEl = el('span', 'gh-region-power-hp', `🛡 ${hp} PV`);
    power.append(atkEl, hpEl);
    wrap.appendChild(power);
  }

  // Actions — only if it's your region and your turn.
  const me = findMyParticipant(ctx.state);
  if (me) {
    const myIdx = findParticipantIndex(ctx.state, me);
    const isMine = ownerIdx === myIdx;
    const isMyTurn = me.turnOrder === ctx.state.currentTurnIndex;
    const isMyCastle = isMine && ctx.state.castles[myIdx] === regionId;

    if (isMine && isMyTurn) {
      const actions = el('div', 'gh-region-actions');
      const hasBuilding = regionCastleTier > 0 || regionIsCamp;

      // Recruit button — per-region recruit cap. Each castle/camp can
      // recruit once per turn; recruiting at A doesn't lock B. The flag
      // lives on `regionUnits[regionId].recruitedThisTurn` server-side.
      if (hasBuilding) {
        const regionUnitsHere = ctx.state.regionUnits[regionId];
        const alreadyRecruitedHere = !!regionUnitsHere?.recruitedThisTurn;
        const recruit = document.createElement('button');
        recruit.type = 'button';
        recruit.className = 'btn-primary gh-region-btn';
        if (alreadyRecruitedHere) {
          recruit.innerHTML = '';
          const sleepIcon = document.createElement('img');
          sleepIcon.src = '/sprites/iconchat_dodo.png';
          sleepIcon.className = 'inline-icon';
          sleepIcon.alt = '';
          recruit.appendChild(sleepIcon);
          recruit.appendChild(document.createTextNode(' Déjà rameuté ici'));
          recruit.disabled = true;
        } else {
          recruit.textContent = regionIsCamp ? 'Rameuter au coin secret…' : 'Rameuter la meute…';
          recruit.addEventListener('click', () => openRecruitModal(ctx, owner!, regionId));
        }
        actions.appendChild(recruit);
      }

      // Castle upgrade button (any castle I own, tier < 3) — compact
      // visual layout: castle sprite with an up-arrow overlay, the next
      // tier number, then each required resource as <icon × N>.
      if (regionCastleTier > 0 && regionCastleTier < 3) {
        const cost = CASTLE_UPGRADE_COSTS[regionCastleTier]!;
        const nextTier = regionCastleTier + 1;
        const canUpgrade =
          me.wood >= cost.wood && me.stone >= cost.stone && me.gold >= cost.gold &&
          me.iron >= cost.iron && me.silver >= cost.silver;
        const up = document.createElement('button');
        up.type = 'button';
        up.className = 'btn-secondary gh-region-btn gh-region-upgrade';
        up.disabled = !canUpgrade;
        up.addEventListener('click', () => ctx.room.send('castle.upgrade', { regionId }));

        // ── Left: castle icon with up-arrow overlay
        const iconWrap = el('span', 'gh-upgrade-icon');
        const castleImg = document.createElement('img');
        castleImg.src = `/sprites/chateau_tier${nextTier}.png`;
        castleImg.className = 'gh-upgrade-castle-img';
        castleImg.alt = '';
        iconWrap.appendChild(castleImg);
        const arrowImg = document.createElement('img');
        arrowImg.src = '/sprites/ui_fleche.png';
        // The arrow image has its tip pointing right; rotate -90° (CCW) so
        // it points UP for the "level up" cue.
        arrowImg.className = 'gh-upgrade-arrow-img';
        arrowImg.alt = '';
        iconWrap.appendChild(arrowImg);
        up.appendChild(iconWrap);

        // ── Middle: "Niv. N" label
        up.appendChild(el('span', 'gh-upgrade-tier-label', `Niv. ${nextTier}`));

        // ── Right: resource cost as <icon × count> chips
        const costWrap = el('span', 'gh-cost-row');
        const addCostCell = (src: string, n: number): void => {
          if (n <= 0) return;
          const cell = el('span', 'gh-cost-cell');
          const im = document.createElement('img');
          im.src = src;
          im.className = 'gh-cost-icon';
          im.alt = '';
          cell.appendChild(im);
          cell.appendChild(document.createTextNode(`×${n}`));
          costWrap.appendChild(cell);
        };
        addCostCell('/sprites/ressource_bois.png', cost.wood);
        addCostCell('/sprites/ressource_pierre.png', cost.stone);
        addCostCell('/sprites/piece_or.png', cost.gold);
        addCostCell('/sprites/ressource_fer.png', cost.iron);
        addCostCell('/sprites/piece_argent.png', cost.silver);
        up.appendChild(costWrap);

        actions.appendChild(up);
      }

      // Build camp button — on regions without any building, capped at 3.
      // Compact layout: camp sprite + small label + resource chips.
      if (!hasBuilding) {
        const myCamps = countMyCamps(ctx.state, myIdx);
        const cost = { wood: 10, stone: 5 };
        const canAfford = me.wood >= cost.wood && me.stone >= cost.stone;
        const atCap = myCamps >= 3;
        const camp = document.createElement('button');
        camp.type = 'button';
        camp.className = 'btn-secondary gh-region-btn gh-region-camp';
        if (atCap) {
          camp.textContent = `Coins secrets : ${myCamps}/3 (panier plein)`;
          camp.disabled = true;
        } else {
          const iconWrap = el('span', 'gh-camp-icon');
          const campImg = document.createElement('img');
          campImg.src = '/sprites/camp.png';
          campImg.className = 'gh-camp-icon-img';
          campImg.alt = '';
          iconWrap.appendChild(campImg);
          camp.appendChild(iconWrap);
          camp.appendChild(el('span', 'gh-camp-label', 'Creuser un coin secret'));
          const costWrap = el('span', 'gh-cost-row');
          const addCell = (src: string, n: number): void => {
            const cell = el('span', 'gh-cost-cell');
            const im = document.createElement('img');
            im.src = src; im.className = 'gh-cost-icon'; im.alt = '';
            cell.appendChild(im);
            cell.appendChild(document.createTextNode(`×${n}`));
            costWrap.appendChild(cell);
          };
          addCell('/sprites/ressource_bois.png', cost.wood);
          addCell('/sprites/ressource_pierre.png', cost.stone);
          camp.appendChild(costWrap);
          camp.disabled = !canAfford;
          camp.addEventListener('click', () => {
            ctx.room.send('camp.build', { regionId });
            // Tap-tap-tap construction sound — fires immediately so the
            // player gets feedback even before the server echo arrives.
            playCampBuild();
          });
        }
        actions.appendChild(camp);
      }

      // Dismantle a camp I own — compact layout with refund icons.
      if (regionIsCamp) {
        const dis = document.createElement('button');
        dis.type = 'button';
        dis.className = 'btn-secondary gh-region-btn gh-region-camp gh-region-camp-dismantle';
        const iconWrap = el('span', 'gh-camp-icon');
        const campImg = document.createElement('img');
        campImg.src = '/sprites/camp.png';
        campImg.className = 'gh-camp-icon-img';
        campImg.alt = '';
        iconWrap.appendChild(campImg);
        // A small "✗" overlay to convey "remove this".
        const xMark = el('span', 'gh-camp-icon-x', '✗');
        iconWrap.appendChild(xMark);
        dis.appendChild(iconWrap);
        dis.appendChild(el('span', 'gh-camp-label', 'Reboucher'));
        const refundWrap = el('span', 'gh-cost-row');
        const addRefund = (src: string, n: number): void => {
          const cell = el('span', 'gh-cost-cell');
          const sign = el('span', 'gh-cost-refund', '+');
          cell.appendChild(sign);
          const im = document.createElement('img');
          im.src = src; im.className = 'gh-cost-icon'; im.alt = '';
          cell.appendChild(im);
          cell.appendChild(document.createTextNode(`×${n}`));
          refundWrap.appendChild(cell);
        };
        addRefund('/sprites/ressource_bois.png', 5);
        addRefund('/sprites/ressource_pierre.png', 2);
        dis.appendChild(refundWrap);
        dis.title = 'Reboucher ce coin secret — récupère une partie des matériaux.';
        dis.addEventListener('click', () => ctx.room.send('camp.dismantle', { regionId }));
        actions.appendChild(dis);
      }

      // "Tirer depuis le repaire" button — appears when I have at least
      // one Défense de la litière charge AND this region is one of my
      // castles. Click → enters castleShotMode: enemy regions within range
      // light up red on the map, then a single click on one fires the
      // cannonball. No modal — feels like the archer attack flow.
      if (regionCastleTier > 0 && me.castleShots > 0) {
        const shoot = document.createElement('button');
        shoot.type = 'button';
        shoot.className = 'btn-primary gh-region-btn gh-region-shoot';
        // Bow sprite + label — replaces the unicode bow emoji which
        // renders as a colour glyph on macOS but a thin glyph on
        // Windows and breaks the button's visual weight.
        const bowIcon = document.createElement('img');
        bowIcon.src = '/sprites/arme_arc_bois.png';
        bowIcon.className = 'inline-icon';
        bowIcon.alt = '';
        shoot.appendChild(bowIcon);
        shoot.appendChild(document.createTextNode(` Tirer depuis le repaire (×${me.castleShots} charge${me.castleShots > 1 ? 's' : ''})`));
        shoot.addEventListener('click', () => enterCastleShotMode(ctx, regionId, regionCastleTier));
        actions.appendChild(shoot);
      }

      // The "move/attack" flow now lives exclusively in the army-sprite
      // selection path: click the army on the map → highlighted neighbours
      // appear → click target. The redundant button is gone.
      void isMyCastle;
      // Still show a status hint if the stack has already moved this turn
      // (purely informational — there's no action to take).
      if (total > 0 && u?.movedThisTurn) {
        const status = el('div', 'gh-region-hint', 'Meute déjà partie en chasse 😴');
        actions.appendChild(status);
      }
      wrap.appendChild(actions);
    } else if (isMine && !isMyTurn) {
      wrap.appendChild(el('div', 'gh-region-hint', 'Patience… tu pourras agir à ta prochaine patrouille. Ronron.'));
    } else if (!isMine && isMyTurn) {
      const adjacentToMe = adjacency.get(regionId);
      const hasNeighbour = adjacentToMe
        ? Array.from(adjacentToMe).some((rid) => ctx.state.regionOwners[rid] === myIdx)
        : false;
      if (hasNeighbour) {
        wrap.appendChild(
          el(
            'div',
            'gh-region-hint',
            'Juste à côté d\'un de tes coins — bondis dessus depuis le voisinage !',
          ),
        );
      }
    }
  }

  return wrap;
}

/**
 * Build a faction flag `<img>` tag. Used in the region panel and the move
 * confirm modal so the player visually identifies enemies and own banners
 * without having to read names.
 */
function factionFlagImg(factionId: string, className: string): HTMLImageElement {
  const img = document.createElement('img');
  img.src = `/sprites/${factionId}_flag.png`;
  img.className = className;
  img.alt = '';
  img.decoding = 'async';
  return img;
}

function unitChip(
  type: 'warrior' | 'archer' | 'berserker' | 'crossbowman' | 'paladin' | 'hero',
  label: string,
  count: number,
  factionId: string,
): HTMLElement {
  // Honor the local player's cosmetic loadout when this chip belongs to
  // their faction; everyone else uses defaults.
  const recipe = getEffectiveRecipe(type, factionId);
  const c = el('div', 'gh-region-unit');
  c.title = `${label} ×${count}`;
  // Sprite stack: [optional left weapon] [body] [right weapon]. Heroes use
  // the dedicated <faction>_hero.png art instead of the generic *_army.png.
  const stack = el('div', 'gh-unit-stack');
  if (recipe.left) {
    const lw = document.createElement('img');
    lw.src = recipe.left;
    lw.className = 'gh-unit-weapon gh-unit-weapon-l';
    lw.alt = '';
    stack.appendChild(lw);
  }
  if (factionId) {
    const body = document.createElement('img');
    body.src = `/sprites/${factionId}_${recipe.bodyKind}.png`;
    body.className = 'gh-unit-body';
    body.alt = '';
    stack.appendChild(body);
  }
  const rw = document.createElement('img');
  rw.src = recipe.right;
  rw.className = 'gh-unit-weapon gh-unit-weapon-r';
  rw.alt = '';
  stack.appendChild(rw);

  c.appendChild(stack);
  c.appendChild(el('span', 'gh-region-unit-count', `×${count}`));
  return c;
}

/**
 * Per-unit cost catalogue mirroring the server. Used for the recruit modal so
 * the client can show the running total cost.
 */
const UNIT_COSTS: Record<
  ServerUnitType,
  { wood: number; stone: number; gold: number; iron: number; silver: number; label: string }
> = {
  warrior:     { label: 'Bagarreur',          wood: 2, stone: 2, gold: 1, iron: 0, silver: 0 },
  archer:      { label: 'Pêcheur d\'oiseaux', wood: 2, stone: 1, gold: 2, iron: 0, silver: 0 },
  berserker:   { label: 'Furieux',            wood: 3, stone: 2, gold: 2, iron: 1, silver: 0 },
  crossbowman: { label: 'Tireur précis',      wood: 2, stone: 2, gold: 2, iron: 1, silver: 0 },
  paladin:     { label: 'Aristochat',         wood: 2, stone: 3, gold: 2, iron: 2, silver: 0 },
  hero:        { label: 'Légende',            wood: 3, stone: 3, gold: 3, iron: 2, silver: 1 },
  kamikaze:    { label: 'Kamikaze',           wood: 2, stone: 2, gold: 3, iron: 2, silver: 1 },
};

/**
 * Multi-type army creation modal. The host can compose ANY mix of unit types
 * in one action (e.g. 5 archers + 2 warriors + 5 paladins). The server will
 * validate the cumulative cost atomically; we mirror it here for live UX.
 */
function openRecruitModal(ctx: InGameContext, owner: Participant, regionId: number): void {
  // What kind of building is this? Determines which unit types are allowed.
  const building = ctx.state.regionBuilding[regionId] ?? 0;
  const isCamp = building === 1;
  const tier = building === 4 ? 3 : building === 3 ? 2 : building === 2 ? 1 : 0;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card recruit-card');
  card.appendChild(el('h2', 'modal-title', isCamp
    ? `🐾 Rameuter la meute · ${regionLabel(ctx.state, regionId)}`
    : `🐾 Rameuter la meute — Repaire niv. ${tier} · ${regionLabel(ctx.state, regionId)}`));
  const help = el(
    'p',
    'modal-help',
    isCamp
      ? 'Coin secret : bagarreurs, pêcheurs d\'oiseaux et furieux seulement. Une seule rameutage par patrouille, MIAOU.'
      : tier >= 3
        ? 'Niv. 3 : toutes les unités sont là (Aristochats + Légende). Une seule meute par patrouille.'
        : tier >= 2
          ? 'Niv. 2 : Aristochats débloqués. Une seule meute par patrouille.'
          : 'Une seule meute par patrouille. Mixe librement tes chatons.',
  );
  card.appendChild(help);

  const composition: Record<ServerUnitType, number> = {
    warrior: 0, archer: 0, berserker: 0, crossbowman: 0, paladin: 0, hero: 0, kamikaze: 0,
  };

  // Hide the hero row entirely if a hero is already alive on the map for
  // this player — heroes are unique, so showing the row would be deceptive.
  const myIdx = findParticipantIndex(ctx.state, owner);
  let heroAlive = false;
  for (let r = 0; r < ctx.state.regionUnits.length; r++) {
    const u = ctx.state.regionUnits[r];
    if (!u) continue;
    if (ctx.state.regionOwners[r] !== myIdx) continue;
    if (u.heroes > 0) { heroAlive = true; break; }
  }

  // Track the stepper "+" buttons so refreshTotal can enable/disable each one
  // based on whether the player can afford ONE MORE of that type.
  const plusBtns: Partial<Record<ServerUnitType, HTMLButtonElement>> = {};
  const rowEls: Partial<Record<ServerUnitType, HTMLElement>> = {};

  // Helper: does the owner currently have a given tech researched?
  const hasOwnerTech = (id: string): boolean => {
    const techs = owner.researchedTechs;
    for (let i = 0; i < techs.length; i++) if (techs[i] === id) return true;
    return false;
  };

  /**
   * Compute the lock status for a given unit type at this region. Returns
   * null when the unit is recruitable; otherwise a short human-readable
   * reason explaining what needs to be unlocked first. Mirrors the server
   * gate logic in lobby_room.ts (paladin requires tier ≥ 2 + `garnison`
   * tech; hero requires tier ≥ 3 + `sanctuaire` tech).
   */
  const unitLockReason = (type: ServerUnitType): string | null => {
    // Camps can only recruit the three base unit types.
    if (isCamp && type !== 'warrior' && type !== 'archer' && type !== 'berserker') {
      return 'Coin secret : seulement bagarreurs, pêcheurs et furieux';
    }
    if (!isCamp && type === 'paladin') {
      if (tier < 2) return 'Améliore le repaire au niveau 2';
      if (!hasOwnerTech('garnison')) return 'Recherche l\'Aristochaterie';
    }
    if (!isCamp && type === 'hero') {
      if (tier < 3) return 'Améliore le repaire au niveau 3';
      if (!hasOwnerTech('sanctuaire')) return 'Recherche le Sanctuaire des héros';
    }
    if (type === 'kamikaze') {
      // Soft-locked: surfaced if max-cap reached or already used this
      // patrol. Server enforces independently; this hint is just UX.
      if (owner.kamikazeRecruitedThisTurn) return '1 par patrouille — déjà fait ce tour';
      // Live count of alive kamikazes across owner's regions.
      let alive = 0;
      for (let r = 0; r < ctx.state.regionUnits.length; r++) {
        if (ctx.state.regionOwners[r] !== findParticipantIndex(ctx.state, owner)) continue;
        alive += ctx.state.regionUnits[r]?.kamikazes ?? 0;
      }
      if (alive >= 3) return 'Max 3 kamikazes vivants à la fois';
    }
    return null;
  };

  const list = el('div', 'recruit-list');
  // Kamikazes were removed as a recruitable unit — only the 6 classic
  // types remain in the recruit modal.
  for (const type of ['warrior', 'archer', 'berserker', 'crossbowman', 'paladin', 'hero'] as ServerUnitType[]) {
    if (type === 'hero' && heroAlive) continue; // already have one — never recruit another
    // For castles: paladin / hero are SHOWN even when locked, with a hint
    // explaining what to unlock. (For camps, only the base three are shown.)
    if (isCamp && type !== 'warrior' && type !== 'archer' && type !== 'berserker') continue;
    const lockReason = unitLockReason(type);
    const def = UNIT_COSTS[type];
    const row = el('div', lockReason ? 'recruit-row recruit-row-locked' : 'recruit-row');

    // Sprite (faction-coloured body + weapon, like the chip)
    const recipe = getEffectiveRecipe(type, owner.factionId);
    const sprite = el('div', 'gh-unit-stack');
    if (recipe.left) {
      const lw = document.createElement('img');
      lw.src = recipe.left;
      lw.className = 'gh-unit-weapon gh-unit-weapon-l';
      sprite.appendChild(lw);
    }
    const body = document.createElement('img');
    body.src = `/sprites/${owner.factionId}_${recipe.bodyKind}.png`;
    body.className = 'gh-unit-body';
    sprite.appendChild(body);
    const rw = document.createElement('img');
    rw.src = recipe.right;
    rw.className = 'gh-unit-weapon gh-unit-weapon-r';
    sprite.appendChild(rw);
    row.appendChild(sprite);

    const info = el('div', 'recruit-info');
    info.appendChild(el('div', 'recruit-name', def.label));
    // Cost as icon chips — much faster to scan than free-text "X bouts de
    // bois · Y cailloux · …". Reuses the gh-cost-cell pattern from the
    // castle-upgrade button so the visual is consistent across the HUD.
    const costRow = el('div', 'recruit-cost gh-cost-row');
    const addRecruitCost = (src: string, n: number): void => {
      if (n <= 0) return;
      const cell = el('span', 'gh-cost-cell');
      const im = document.createElement('img');
      im.src = src;
      im.className = 'gh-cost-icon';
      im.alt = '';
      cell.appendChild(im);
      cell.appendChild(document.createTextNode(`×${n}`));
      costRow.appendChild(cell);
    };
    if (def.wood) addRecruitCost('/sprites/ressource_bois.png', def.wood);
    if (def.stone) addRecruitCost('/sprites/ressource_pierre.png', def.stone);
    if (def.gold) addRecruitCost('/sprites/piece_or.png', def.gold);
    if (def.iron) addRecruitCost('/sprites/ressource_fer.png', def.iron);
    if (def.silver) addRecruitCost('/sprites/piece_argent.png', def.silver);
    info.appendChild(costRow);
    // Locked units show a small hint with what unlock is required.
    if (lockReason) {
      info.appendChild(el('div', 'recruit-locked-hint', `🔒 ${lockReason}`));
    }
    row.appendChild(info);

    // Counter buttons — disabled (no event listener, visually flat) when
    // the unit is locked. The row stays in the list so the player sees
    // what they'll unlock by upgrading. ±10 buttons + hold-to-repeat
    // (see stepperBtn / attachHoldToRepeat) reuse the same UX as the
    // move modal so the experience is consistent.
    const stepper = el('div', 'recruit-stepper');
    const countDisplay = el('span', 'recruit-count', '0');
    const hardCap = type === 'hero' ? 1 : 20;
    // Compute how many MORE of `type` the player can actually pay for
    // given the current composition. Used to clamp +1 / +10 / "Tout
    // sélectionner" so the UI never shows a count the server would
    // reject (player asked: "+10 ne doit pas mettre 10 si on n'a pas
    // les ressources"). Returns ∞ when the resource isn't required.
    const maxAffordableAddOf = (t: ServerUnitType): number => {
      const c = UNIT_COSTS[t];
      // Cumulative current cost across the WHOLE composition.
      let usedWood = 0, usedStone = 0, usedGold = 0, usedIron = 0, usedSilver = 0;
      for (const tt of Object.keys(composition) as ServerUnitType[]) {
        const n = composition[tt];
        const cc = UNIT_COSTS[tt];
        usedWood += cc.wood * n;
        usedStone += cc.stone * n;
        usedGold += cc.gold * n;
        usedIron += cc.iron * n;
        usedSilver += cc.silver * n;
      }
      const remainingWood = owner.wood - usedWood;
      const remainingStone = owner.stone - usedStone;
      const remainingGold = owner.gold - usedGold;
      const remainingIron = owner.iron - usedIron;
      const remainingSilver = owner.silver - usedSilver;
      const div = (rem: number, cost: number): number =>
        cost <= 0 ? Number.POSITIVE_INFINITY : Math.max(0, Math.floor(rem / cost));
      return Math.min(
        div(remainingWood, c.wood),
        div(remainingStone, c.stone),
        div(remainingGold, c.gold),
        div(remainingIron, c.iron),
        div(remainingSilver, c.silver),
      );
    };
    const update = (next: number): void => {
      const current = composition[type];
      let target = Math.max(0, Math.min(hardCap, next));
      // Only resource-clamp when ADDING (subtractions are free).
      if (target > current) {
        const want = target - current;
        const affordable = maxAffordableAddOf(type);
        target = current + Math.min(want, affordable);
      }
      composition[type] = target;
      countDisplay.textContent = composition[type].toString();
      scheduleRecruitRefresh();
    };
    const minusTen = stepperBtn('−10', () => update(composition[type] - 10));
    const minus = stepperBtn('−', () => update(composition[type] - 1));
    const plus = stepperBtn('+', () => update(composition[type] + 1));
    const plusTen = stepperBtn('+10', () => update(composition[type] + 10));
    if (lockReason) {
      minus.disabled = true;
      plus.disabled = true;
      minusTen.disabled = true;
      plusTen.disabled = true;
      minus.title = plus.title = minusTen.title = plusTen.title = lockReason;
    }
    stepper.append(minusTen, minus, countDisplay, plus, plusTen);
    row.appendChild(stepper);
    plusBtns[type] = plus;
    rowEls[type] = row;

    list.appendChild(row);
  }
  card.appendChild(list);

  const total = el('div', 'recruit-total');
  card.appendChild(total);
  // Live "wallet preview" — what your stash becomes after the purchase.
  const wallet = el('div', 'recruit-wallet');
  card.appendChild(wallet);
  const error = el('div', 'auth-error');
  card.appendChild(error);

  // rAF-debounced wrapper — see openActionConfirm for the rationale.
  // Coalesces rapid stepper clicks (e.g. holding +10 on mobile) into
  // one rebuild per animation frame instead of one per click.
  let recruitRefreshScheduled = false;
  const scheduleRecruitRefresh = (): void => {
    if (recruitRefreshScheduled) return;
    recruitRefreshScheduled = true;
    requestAnimationFrame(() => {
      recruitRefreshScheduled = false;
      refreshTotal();
    });
  };

  const refreshTotal = (): void => {
    let w = 0, s = 0, g = 0, i = 0, ag = 0;
    let count = 0;
    for (const type of Object.keys(composition) as ServerUnitType[]) {
      const n = composition[type];
      const c = UNIT_COSTS[type];
      w += c.wood * n;
      s += c.stone * n;
      g += c.gold * n;
      i += c.iron * n;
      ag += c.silver * n;
      count += n;
    }
    const me = findMyParticipant(ctx.state)!;
    const enough =
      me.wood >= w && me.stone >= s && me.gold >= g && me.iron >= i && me.silver >= ag;
    const parts: string[] = [];
    if (w) parts.push(`${w} bouts de bois`);
    if (s) parts.push(`${s} cailloux`);
    if (g) parts.push(`${g} pièces brillantes`);
    if (i) parts.push(`${i} bouts de métal`);
    if (ag) parts.push(`${ag} brillants argentés`);
    total.innerHTML = '';
    total.appendChild(el('span', 'recruit-total-label', `Meute : ${count} chaton${count > 1 ? 's' : ''} · `));
    total.appendChild(
      el('span', enough ? 'recruit-total-cost ok' : 'recruit-total-cost low', parts.join(' · ') || 'gratos'),
    );

    // Wallet preview: « il te restera : … ». Each resource line goes red if
    // we'd dip below 0. Helps the player gauge how much they can still spend
    // without committing to the purchase.
    wallet.innerHTML = '';
    wallet.appendChild(el('span', 'recruit-wallet-label', 'Dans ta poche : '));
    const remW = me.wood - w;
    const remS = me.stone - s;
    const remG = me.gold - g;
    const remI = me.iron - i;
    const remAg = me.silver - ag;
    const cell = (icon: string, value: number): HTMLElement => {
      const e = el('span', 'recruit-wallet-cell');
      const img = document.createElement('img');
      img.src = `/sprites/ressource_${icon}.png`;
      img.alt = icon;
      img.className = 'recruit-wallet-icon';
      e.appendChild(img);
      e.appendChild(document.createTextNode(value.toString()));
      if (value < 0) e.classList.add('low');
      return e;
    };
    wallet.appendChild(cell('bois', remW));
    wallet.appendChild(cell('pierre', remS));
    // Gold uses `piece_or` per the existing recipe; silver uses `piece_argent`.
    const goldCell = el('span', 'recruit-wallet-cell');
    const goldImg = document.createElement('img');
    goldImg.src = '/sprites/piece_or.png';
    goldImg.className = 'recruit-wallet-icon';
    goldCell.appendChild(goldImg);
    goldCell.appendChild(document.createTextNode(remG.toString()));
    if (remG < 0) goldCell.classList.add('low');
    wallet.appendChild(goldCell);
    wallet.appendChild(cell('fer', remI));
    const silverCell = el('span', 'recruit-wallet-cell');
    const silverImg = document.createElement('img');
    silverImg.src = '/sprites/piece_argent.png';
    silverImg.className = 'recruit-wallet-icon';
    silverCell.appendChild(silverImg);
    silverCell.appendChild(document.createTextNode(remAg.toString()));
    if (remAg < 0) silverCell.classList.add('low');
    wallet.appendChild(silverCell);

    submit.disabled = count === 0 || !enough;
    if (count > 0 && !enough) {
      error.textContent = 'Pchhh, pas assez de trésors !';
    } else {
      error.textContent = '';
    }

    // Per-row "+" disable: greyed out if buying ONE MORE of this type would
    // overshoot any resource. Recomputed every tick of the steppers so the
    // affordability picture stays live.
    for (const t of Object.keys(plusBtns) as ServerUnitType[]) {
      const btn = plusBtns[t];
      const rowEl = rowEls[t];
      if (!btn) continue;
      // Locked units (tier / tech gate not yet satisfied) stay disabled
      // regardless of resources.
      if (unitLockReason(t)) { btn.disabled = true; continue; }
      const c = UNIT_COSTS[t];
      const heroHardCap = t === 'hero' ? 1 : 20;
      const atHardCap = composition[t] >= heroHardCap;
      const canAfford =
        me.wood - w - c.wood >= 0 &&
        me.stone - s - c.stone >= 0 &&
        me.gold - g - c.gold >= 0 &&
        me.iron - i - c.iron >= 0 &&
        me.silver - ag - c.silver >= 0;
      const disabled = atHardCap || !canAfford;
      btn.disabled = disabled;
      if (rowEl) rowEl.classList.toggle('recruit-row-disabled', disabled && composition[t] === 0);
    }
  };

  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Pchhh non';
  cancel.addEventListener('click', () => overlay.remove());
  const submit = document.createElement('button');
  submit.type = 'button';
  submit.className = 'btn-primary';
  submit.textContent = 'NIA, rameuter !';
  submit.disabled = true;
  submit.addEventListener('click', () => {
    const list: Array<{ type: ServerUnitType; count: number }> = [];
    for (const type of Object.keys(composition) as ServerUnitType[]) {
      if (composition[type] > 0) list.push({ type, count: composition[type] });
    }
    if (list.length === 0) return;
    ctx.room.send('unit.recruit', { regionId, units: list });
    playMeow();
    // Faction-tinted overlay so each kingdom's recruits sound distinct.
    playMeowForFaction(owner.factionId);
    overlay.remove();
  });
  buttons.append(cancel, submit);
  card.appendChild(buttons);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.appendChild(overlay);
  refreshTotal();
}

function stepperBtn(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'recruit-step-btn';
  // "+" / "−" use the pixel-art sprites; multi-step labels ("+10", "−10",
  // "max", etc.) render as plain text so they stay readable at small
  // mobile sizes without needing extra art assets.
  if (label === '+' || label === '−') {
    const img = document.createElement('img');
    img.src = label === '+' ? '/sprites/ui_ajouter.png' : '/sprites/ui_supprimer.png';
    img.className = 'recruit-step-icon';
    img.alt = label;
    img.decoding = 'async';
    // The sprite is purely decorative — Three.js / browser doesn't need
    // to repaint it on each click. `pointer-events: none` ensures the
    // click target stays the button itself.
    img.style.pointerEvents = 'none';
    b.appendChild(img);
  } else {
    b.textContent = label;
    b.classList.add('recruit-step-btn-text');
  }
  attachHoldToRepeat(b, onClick);
  return b;
}

/**
 * Wire a button so it fires `onClick` once on tap AND then on a 90 ms
 * interval while held down (mouse OR touch). Mirrors the iOS / Android
 * stepper UX — players can adjust counts without mashing the button.
 *
 * Initial click fires immediately, repeats start after a 320 ms delay
 * so a quick tap stays a single +1 / −1 without surprise multiples.
 */
function attachHoldToRepeat(b: HTMLButtonElement, onClick: () => void): void {
  // Single fire on click (mouse OR keyboard via Enter/Space).
  b.addEventListener('click', onClick);
  let initialTimer: number | null = null;
  let repeatTimer: number | null = null;
  const clearTimers = (): void => {
    if (initialTimer !== null) { window.clearTimeout(initialTimer); initialTimer = null; }
    if (repeatTimer !== null) { window.clearInterval(repeatTimer); repeatTimer = null; }
  };
  const startHold = (): void => {
    clearTimers();
    initialTimer = window.setTimeout(() => {
      // After the 320 ms grace period, fire every 90 ms until release.
      // First tick fires immediately so the hold-and-go feels responsive.
      onClick();
      repeatTimer = window.setInterval(onClick, 90);
    }, 320);
  };
  b.addEventListener('pointerdown', startHold);
  b.addEventListener('pointerup', clearTimers);
  b.addEventListener('pointercancel', clearTimers);
  b.addEventListener('pointerleave', clearTimers);
}

/**
 * Compact confirmation popup that appears after the player has already chosen
 * the source army (left-click on sprite) AND the target region (left-click
 * on an adjacent highlighted region). Only asks "how many warriors?" and
 * tells the player what kind of action it'll be (claim / reinforce / attack).
 *
 * Replaces the older "openMoveModal" with the big target grid; that modal
 * was confusing because it asked you to choose BOTH count AND target after
 * an already-committed selection.
 */
function openActionConfirm(ctx: InGameContext, from: number, to: number): void {
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0) return;
  const u = ctx.state.regionUnits[from];
  if (!u) return;
  // Available units per type AT THE SOURCE. Nothing is reserved — the castle
  // can be left empty if the player wants (it'll be capturable, that's the
  // trade-off).
  const available: Record<ServerUnitType, number> = {
    warrior: u.warriors,
    archer: u.archers,
    berserker: u.berserkers,
    crossbowman: u.crossbowmen,
    paladin: u.paladins,
    hero: u.heroes,
    kamikaze: u.kamikazes,
  };
  const totalAvailable = Object.values(available).reduce((a, b) => a + b, 0);
  if (totalAvailable <= 0) return;

  const destOwnerIdx = ctx.state.regionOwners[to] ?? -1;
  const destOwner = destOwnerIdx >= 0 ? ctx.state.participants[destOwnerIdx] : null;
  const destFaction = destOwner ? ctx.factions.find((x) => x.id === destOwner.factionId) : null;
  const destUnits = ctx.state.regionUnits[to];
  const enemyTotal = destUnits ? totalUnits(destUnits) : 0;

  let action: 'claim' | 'reinforce' | 'attack';
  if (destOwnerIdx === -1) action = 'claim';
  else if (destOwnerIdx === myIdx) action = 'reinforce';
  else action = 'attack';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card recruit-card');

  const targetLabel = regionLabel(ctx.state, to);
  const title = action === 'attack' ? `⚔ Griffer ${targetLabel}`
              : action === 'reinforce' ? `🛡 Renforcer ${targetLabel}`
              : `🚩 Réclamer ${targetLabel}`;
  card.appendChild(el('h2', 'modal-title', title));

  // Source/target context line (faction flag + name + defender count)
  const ctxLine = el('p', 'action-context');
  ctxLine.appendChild(document.createTextNode(`Depuis #${from + 1} → #${to + 1} · `));
  if (destOwner && destFaction) {
    ctxLine.appendChild(factionFlagImg(destFaction.id, 'action-context-flag'));
    ctxLine.appendChild(document.createTextNode(` ${destOwner.name}`));
  } else {
    ctxLine.appendChild(document.createTextNode('Personne (chats errants)'));
  }
  if (enemyTotal > 0) {
    ctxLine.appendChild(renderTextWithIcons(` · ⚔ ${enemyTotal} chat${enemyTotal > 1 ? 's' : ''} adverse${enemyTotal > 1 ? 's' : ''}`));
  } else if (action === 'attack' || action === 'claim') {
    ctxLine.appendChild(document.createTextNode(' · personne ne défend'));
  }
  card.appendChild(ctxLine);

  // Numeric power summary (defender side). Updated once at modal open.
  if (action === 'attack' && destUnits && enemyTotal > 0) {
    const defLine = el('p', 'action-context');
    defLine.appendChild(
      renderTextWithIcons(`Défense adverse : ⚔ ${armyAttack(destUnits)} griffes · 🛡 ${armyHp(destUnits)} PV`),
    );
    card.appendChild(defLine);
  }

  // Per-type stepper rows — capped at the source's available count.
  const composition: Record<ServerUnitType, number> = {
    warrior: 0, archer: 0, berserker: 0, crossbowman: 0, paladin: 0, hero: 0, kamikaze: 0,
  };
  const factionId = me.factionId;

  const list = el('div', 'recruit-list');
  // Kamikazes removed from the recruitable roster — also gone from
  // the move modal. Legacy kamikaze stacks (from in-flight games)
  // will still move via the move handler since the schema field
  // remains, but no NEW UI for them.
  const types: ServerUnitType[] = ['warrior', 'archer', 'berserker', 'crossbowman', 'paladin', 'hero'];
  // Skip rows that have 0 available — keeps the modal short.
  const visibleTypes = types.filter((t) => available[t] > 0);

  const refs: Partial<Record<ServerUnitType, HTMLElement>> = {};
  const rowRefs: Partial<Record<ServerUnitType, HTMLElement>> = {};
  // No-op shim — kept so the call sites below still compile but the
  // lock logic is dead since `composition.kamikaze` will always be 0.
  const applySoloKamikazeLock = (): void => { /* no-op */ };
  for (const type of visibleTypes) {
    const max = available[type];
    const def = UNIT_COSTS[type];
    const row = el('div', 'recruit-row');
    rowRefs[type] = row;

    // Sprite (faction body + weapon)
    const recipe = getEffectiveRecipe(type, factionId);
    const sprite = el('div', 'gh-unit-stack');
    if (recipe.left) {
      const lw = document.createElement('img');
      lw.src = recipe.left;
      lw.className = 'gh-unit-weapon gh-unit-weapon-l';
      sprite.appendChild(lw);
    }
    const body = document.createElement('img');
    body.src = `/sprites/${factionId}_${recipe.bodyKind}.png`;
    body.className = 'gh-unit-body';
    sprite.appendChild(body);
    const rw = document.createElement('img');
    rw.src = recipe.right;
    rw.className = 'gh-unit-weapon gh-unit-weapon-r';
    sprite.appendChild(rw);
    row.appendChild(sprite);

    const info = el('div', 'recruit-info');
    info.appendChild(el('div', 'recruit-name', def.label));
    info.appendChild(el('div', 'recruit-cost', `Dispo : ${max} chaton${max > 1 ? 's' : ''}`));
    row.appendChild(info);

    // Stepper — count display capped at `max` (per type).
    // ±10 buttons let the player adjust large stacks without 30 taps
    // (e.g. moving 50 warriors). All four buttons clamp to [0, max]
    // so "+10" on a count of 47 with max=50 results in 50, not 57.
    // Hold-to-repeat (see stepperBtn / attachHoldToRepeat) makes
    // even fine adjustments fast on mobile.
    const stepper = el('div', 'recruit-stepper');
    const countDisplay = el('span', 'recruit-count', '0');
    refs[type] = countDisplay;
    const updateCount = (next: number): void => {
      composition[type] = Math.max(0, Math.min(max, next));
      countDisplay.textContent = composition[type].toString();
      applySoloKamikazeLock();
      scheduleRefresh();
    };
    const minusTen = stepperBtn('−10', () => updateCount(composition[type] - 10));
    const minus = stepperBtn('−', () => updateCount(composition[type] - 1));
    const plus = stepperBtn('+', () => updateCount(composition[type] + 1));
    const plusTen = stepperBtn('+10', () => updateCount(composition[type] + 10));
    stepper.append(minusTen, minus, countDisplay, plus, plusTen);
    row.appendChild(stepper);

    list.appendChild(row);
  }
  card.appendChild(list);

  // Quick-action buttons
  const quick = el('div', 'recruit-quick');
  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.className = 'btn-secondary recruit-quick-btn';
  allBtn.textContent = 'Tout le monde y va !';
  allBtn.addEventListener('click', () => {
    // "Tout le monde y va" excludes kamikazes — they're solo by design
    // and would otherwise nullify the regular stack (server rule).
    for (const t of visibleTypes) {
      composition[t] = available[t];
      const d = refs[t];
      if (d) d.textContent = composition[t].toString();
    }
    applySoloKamikazeLock();
    scheduleRefresh();
  });
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'btn-secondary recruit-quick-btn';
  clearBtn.textContent = 'Personne ne bouge';
  clearBtn.addEventListener('click', () => {
    for (const t of visibleTypes) {
      composition[t] = 0;
      const d = refs[t];
      if (d) d.textContent = '0';
    }
    applySoloKamikazeLock();
    scheduleRefresh();
  });
  quick.append(allBtn, clearBtn);
  card.appendChild(quick);

  const total = el('div', 'recruit-total');
  card.appendChild(total);
  const preview = el('div', 'action-preview');
  card.appendChild(preview);

  // Helper: convert composition (singular keys) → UnitsLike (plural).
  const compToUnits = (): UnitsLike => ({
    warriors: composition.warrior,
    archers: composition.archer,
    berserkers: composition.berserker,
    crossbowmen: composition.crossbowman,
    paladins: composition.paladin,
    heroes: composition.hero,
    kamikazes: composition.kamikaze,
  });

  // rAF-debounced wrapper so multiple stepper clicks within the same
  // frame coalesce into ONE rebuild. Without this, mashing +10 ten
  // times re-runs `simulateCombat` (100-round loop) and rebuilds the
  // preview DOM ten times in 100 ms — on mobile this caused noticeable
  // lag and heat. With the debounce, only the LAST state per frame
  // actually computes.
  let refreshScheduled = false;
  const scheduleRefresh = (): void => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    requestAnimationFrame(() => {
      refreshScheduled = false;
      refreshTotal();
    });
  };

  const refreshTotal = (): void => {
    const n = Object.values(composition).reduce((a, b) => a + b, 0);
    const sending = compToUnits();
    total.innerHTML = '';
    total.appendChild(
      el('span', 'recruit-total-label', `En route : ${n} chaton${n > 1 ? 's' : ''}`),
    );
    if (n > 0) {
      total.appendChild(
        el('span', 'recruit-total-cost ok', ` · ⚔ ${armyAttack(sending)} griffes · 🛡 ${armyHp(sending)} PV`),
      );
    }
    confirm.disabled = n === 0;

    // Hover-style annihilation preview: in attack mode, simulate the fight
    // with the CURRENT composition vs. the defender garrison and show who
    // survives in each unit type (zero means "annihilated").
    preview.innerHTML = '';
    if (n > 0 && action === 'attack' && destUnits) {
      // Mirror server combat opts: castle/camp gives a 100 PV floor + tech
      // multipliers on the defender side. Without these the preview lies.
      const destBuilding = ctx.state.regionBuilding[to] ?? 0;
      const defenderMinHp = destBuilding !== 0 ? 100 : 0;
      let defenderHpMult = 1.0;
      if (destOwner) {
        const techs = destOwner.researchedTechs;
        const has = (id: string): boolean => {
          for (let i = 0; i < techs.length; i++) if (techs[i] === id) return true;
          return false;
        };
        if (has('murs')) defenderHpMult *= 1.25;
        else if (has('palissade')) defenderHpMult *= 1.10;
        if (has('caserne') && destBuilding !== 0) defenderHpMult *= 1.20;
      }
      const sim = simulateCombat(sending, destUnits, { defenderMinHp, defenderHpMult });
      preview.appendChild(el('div', 'action-preview-title', '🐾 Aperçu de la bagarre'));
      preview.appendChild(simRow('Mes chatons survivants', sim.attackerSurvivors, sending, me.factionId));
      preview.appendChild(simRow('Chats adverses restants', sim.defenderSurvivors, destUnits, destOwner?.factionId ?? ''));
      const verdict = sim.winner === 'attacker'
        ? '✓ NIA ! Coin de jardin réclamé'
        : sim.winner === 'defender'
          ? '✗ Pchhh, on s\'est fait griffer'
          : '~ Match nul — tout le monde lèche ses plaies';
      preview.appendChild(el('div', 'action-preview-verdict ' + sim.winner, verdict));
    }
  };

  // Buttons
  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Pchhh non';
  cancel.addEventListener('click', () => overlay.remove());
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'btn-primary';
  confirm.textContent = action === 'attack' ? 'GRIFFER !' : action === 'reinforce' ? 'Renforcer NIA !' : 'Réclamer NIA !';
  confirm.disabled = true;
  confirm.addEventListener('click', () => {
    const n = Object.values(composition).reduce((a, b) => a + b, 0);
    if (n <= 0) return;
    ctx.room.send('unit.move', { from, to, units: composition });
    overlay.remove();
  });
  buttons.append(cancel, confirm);
  card.appendChild(buttons);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  // Default to "Tout sélectionner" so the most common action is one click.
  allBtn.click();
  setTimeout(() => confirm.focus(), 0);
  document.addEventListener(
    'keydown',
    function onKey(e) {
      if (!document.body.contains(overlay)) {
        document.removeEventListener('keydown', onKey);
        return;
      }
      if (e.key === 'Enter') confirm.click();
      if (e.key === 'Escape') overlay.remove();
    },
  );
}

/**
 * Confirm a ranged attack from `from` onto `to`. Unlike `openActionConfirm`
 * there's no count input — the source stack fires its FULL archer+crossbowman
 * complement in one volley, then becomes spent for the turn (movedThisTurn).
 * Source units do NOT physically move; only damage travels.
 */
/**
 * Modal target picker for a castle shot (Défense de la litière). Lists every
 * enemy region within the castle's range (2 hops, 3 for tier 3). On click,
 * fires `castle.shot` which consumes one charge.
 */
// openCastleShotPicker was removed when the castle-shot UX moved from a
// modal target picker to a map-click targeting flow. See enterCastleShotMode
// and the `castleShotMode` branch in handleRegionClick.

function openRangedAttackConfirm(ctx: InGameContext, from: number, to: number): void {
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0) return;
  const u = ctx.state.regionUnits[from];
  if (!u) return;
  if (u.archers + u.crossbowmen <= 0) return;
  if (u.movedThisTurn) return;

  const destOwnerIdx = ctx.state.regionOwners[to] ?? -1;
  const destOwner = destOwnerIdx >= 0 ? ctx.state.participants[destOwnerIdx] : null;
  const destFaction = destOwner ? ctx.factions.find((x) => x.id === destOwner.factionId) : null;
  const destUnits = ctx.state.regionUnits[to];
  const enemyTotal = destUnits ? totalUnits(destUnits) : 0;
  if (!destOwner || destOwnerIdx === myIdx || destOwnerIdx < 0) return; // safeguard

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card action-confirm');
  card.appendChild(el('h2', 'modal-title', `Volée sur ${regionLabel(ctx.state, to)}`));

  const ctxLine = el('p', 'action-context');
  ctxLine.appendChild(document.createTextNode(`Depuis #${from + 1} · `));
  if (destOwner && destFaction) {
    const dot = el('span', 'gh-region-dot');
    dot.style.background = destFaction.color;
    ctxLine.appendChild(dot);
    ctxLine.appendChild(document.createTextNode(` ${destOwner.name}`));
  }
  ctxLine.appendChild(
    renderTextWithIcons(` · ⚔ ${enemyTotal} chat${enemyTotal > 1 ? 's' : ''} adverse${enemyTotal > 1 ? 's' : ''}`),
  );
  card.appendChild(ctxLine);

  const composition: string[] = [];
  if (u.archers > 0) composition.push(`${u.archers} pêcheur${u.archers > 1 ? 's' : ''} d'oiseaux`);
  if (u.crossbowmen > 0) composition.push(`${u.crossbowmen} tireur${u.crossbowmen > 1 ? 's' : ''} précis`);
  card.appendChild(
    el(
      'p',
      'auth-hint',
      `Volée : ${composition.join(' + ')}. Les tireurs restent au chaud et finissent leur patrouille après le tir. Prrrr.`,
    ),
  );

  // Ranged attack preview — mirrors the server's executeRangedAttack:
  // total damage = archers × 3 + crossbowmen × 4, defender survives at
  // ratio (defenderHpAfter / defenderHpBefore), each unit type scaled by
  // that ratio. We show the per-type survivor count vs. original.
  if (destUnits && enemyTotal > 0) {
    const dmg = u.archers * UNIT_STATS.archer.damage + u.crossbowmen * UNIT_STATS.crossbowman.damage;
    const defHp0 = armyHp(destUnits);
    const survRatio = defHp0 > 0 ? Math.max(0, (defHp0 - dmg) / defHp0) : 0;
    const survivors: UnitsLike = {
      warriors: Math.floor(destUnits.warriors * survRatio),
      archers: Math.floor(destUnits.archers * survRatio),
      berserkers: Math.floor(destUnits.berserkers * survRatio),
      crossbowmen: Math.floor(destUnits.crossbowmen * survRatio),
      paladins: Math.floor(destUnits.paladins * survRatio),
      heroes: Math.floor(destUnits.heroes * survRatio),
      // Kamikazes can't be defenders (they always solo-stack and die on
      // attack), but include the field for type completeness — the ranged
      // damage doesn't touch this count.
      kamikazes: destUnits.kamikazes,
    };
    const preview = el('div', 'action-preview');
    preview.appendChild(el('div', 'action-preview-title', `Volée : ${dmg} dégâts`));
    preview.appendChild(
      simRow(
        'Chats adverses restants',
        survivors,
        destUnits,
        destOwner.factionId,
      ),
    );
    const annihilated = totalUnits(survivors) === 0;
    const verdict = annihilated
      ? '✓ Léchouille fatale ! (mais le coin reste à l\'adversaire)'
      : `${totalUnits(survivors)} survivant${totalUnits(survivors) > 1 ? 's' : ''} sur ${enemyTotal}`;
    preview.appendChild(
      el(
        'div',
        'action-preview-verdict ' + (annihilated ? 'attacker' : 'defender'),
        verdict,
      ),
    );
    card.appendChild(preview);
  }

  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Pchhh non';
  cancel.addEventListener('click', () => overlay.remove());
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'btn-primary';
  confirm.textContent = 'NIA, lancez la volée !';
  confirm.addEventListener('click', () => {
    ctx.room.send('unit.range_attack', { from, to });
    overlay.remove();
  });
  buttons.append(cancel, confirm);
  card.appendChild(buttons);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  setTimeout(() => confirm.focus(), 0);
  document.addEventListener(
    'keydown',
    function onKey(e) {
      if (!document.body.contains(overlay)) {
        document.removeEventListener('keydown', onKey);
        return;
      }
      if (e.key === 'Enter') confirm.click();
      if (e.key === 'Escape') overlay.remove();
    },
  );
}

/**
 * Modal: pick a count and an adjacent target region. Sends `unit.move` to the
 * server, which validates and either reinforces, claims, or fights.
 *
 * If `preselectTarget` is provided (e.g. the user clicked a highlighted
 * adjacent region after selecting an army), that target's card is visually
 * pre-emphasised and we'd accept a one-click confirmation. For Phase F we
 * still show the full grid so the player can pick a different neighbour.
 */
function openMoveModal(
  ctx: InGameContext,
  fromRegion: number,
  _preselectTarget?: number,
): void {
  const me = findMyParticipant(ctx.state);
  if (!me) return;
  const myIdx = findParticipantIndex(ctx.state, me);
  if (myIdx < 0) return;
  const u = ctx.state.regionUnits[fromRegion];
  if (!u) return;
  const total = u.warriors + u.archers + u.berserkers + u.crossbowmen + u.paladins + u.heroes;
  if (total <= 0) return;
  const targets = adjacency.get(fromRegion);
  if (!targets || targets.size === 0) return;

  // The right-click shortcut: pick a target first, THEN compose. We open
  // a small adjacency picker and delegate the composition to `openActionConfirm`
  // so there's a single source of truth for "how to move troops".
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card');
  card.appendChild(el('h2', 'modal-title', `🎯 Qui on griffe depuis ${regionLabel(ctx.state, fromRegion)} ?`));

  const grid = el('div', 'gh-move-grid');
  for (const rid of targets) {
    const ownerIdx = ctx.state.regionOwners[rid] ?? -1;
    const owner = ownerIdx >= 0 ? ctx.state.participants[ownerIdx] : null;
    const f = owner ? ctx.factions.find((x) => x.id === owner.factionId) : null;
    const ru = ctx.state.regionUnits[rid];
    const tot = ru
      ? ru.warriors + ru.archers + ru.berserkers + ru.crossbowmen + ru.paladins + ru.heroes
      : 0;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gh-move-target';
    if (ownerIdx === myIdx) btn.classList.add('gh-move-mine');
    else if (ownerIdx >= 0) btn.classList.add('gh-move-enemy');

    // Use a flag thumbnail instead of a tiny dot — more legible at a glance.
    if (f) {
      btn.appendChild(factionFlagImg(f.id, 'gh-move-flag'));
    } else {
      const dot = el('span', 'gh-region-dot');
      dot.style.background = '#444';
      btn.appendChild(dot);
    }

    const info = el('div', 'gh-move-info');
    info.appendChild(el('div', 'gh-move-name', `#${rid + 1}`));
    info.appendChild(
      el('div', 'gh-move-owner', owner ? owner.name : 'Personne'),
    );
    info.appendChild(el('div', 'gh-move-units', `⚔️ ${tot}`));
    btn.appendChild(info);

    btn.addEventListener('click', () => {
      overlay.remove();
      openActionConfirm(ctx, fromRegion, rid);
      selectedRegionId = null;
    });
    grid.appendChild(btn);
  }
  card.appendChild(grid);

  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Pchhh non';
  cancel.addEventListener('click', () => overlay.remove());
  buttons.appendChild(cancel);
  card.appendChild(buttons);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.appendChild(overlay);
}

/** Confetti shower in the winner's faction colour — pure CSS via inline
 *  styles so we don't need a separate library. ~28 squares falling down
 *  the screen with random horizontal positions / delays / rotations.
 *  Auto-cleaned after 8 s. */
function spawnVictoryConfetti(color: string): void {
  document.querySelectorAll('.gh-victory-confetti').forEach((n) => n.remove());
  const wrap = document.createElement('div');
  wrap.className = 'gh-victory-confetti';
  for (let i = 0; i < 28; i++) {
    const piece = document.createElement('span');
    piece.className = 'gh-victory-confetti-piece';
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.background = color;
    piece.style.animationDelay = `${Math.random() * 1.2}s`;
    piece.style.animationDuration = `${2.8 + Math.random() * 1.6}s`;
    piece.style.setProperty('--rot-start', `${(Math.random() - 0.5) * 90}deg`);
    piece.style.setProperty('--rot-end', `${(Math.random() - 0.5) * 540}deg`);
    wrap.appendChild(piece);
  }
  document.body.appendChild(wrap);
  setTimeout(() => wrap.remove(), 9000);
}

let victoryAutoLeaveScheduled = false;

function renderVictoryBanner(ctx: InGameContext): HTMLElement {
  const wrap = el('div', 'gh-victory');
  const winner = ctx.state.participants[ctx.state.winnerIndex];
  const me = findMyParticipant(ctx.state);
  const iWon = !!(winner && me && winner.id === me.id);
  const winnerFaction = winner ? ctx.factions.find((x) => x.id === winner.factionId) : null;
  const winnerColor = winnerFaction?.color ?? '#e8d8a8';
  // Trigger the confetti shower once.
  if (winner && !document.querySelector('.gh-victory-confetti')) {
    spawnVictoryConfetti(winnerColor);
  }

  // ── Trophy stack: winner head on top, every eliminated kingdom
  // crushed underneath. Reads as "Pacha at the top of the pile".
  const stack = el('div', 'gh-victory-stack');
  // Eliminated kingdoms first (rendered behind/below the winner).
  // SchemaArray doesn't expose .filter — iterate manually.
  const losers: Participant[] = [];
  ctx.state.participants.forEach((p) => {
    if (p && p !== winner && p.factionId && p.type !== 'barbarian') losers.push(p);
  });
  for (let i = 0; i < losers.length; i++) {
    const l = losers[i]!;
    const head = document.createElement('img');
    head.src = `/sprites/${l.factionId}_army.png`;
    head.className = 'gh-victory-loser-head';
    // Spread the losers across a small fan under the winner.
    const angleDeg = -20 + (i / Math.max(1, losers.length - 1)) * 40;
    const lift = i * 4;
    head.style.transform = `rotate(${angleDeg}deg) translateY(${lift}px)`;
    head.style.zIndex = String(i);
    head.alt = l.name;
    head.title = `${l.name} (battu)`;
    stack.appendChild(head);
  }
  // Winner — bigger, on top, with a coloured halo.
  if (winner) {
    const win = el('div', 'gh-victory-winner-wrap');
    win.style.setProperty('--winner-color', winnerColor);
    const head = document.createElement('img');
    head.src = `/sprites/${winner.factionId}_army.png`;
    head.className = 'gh-victory-winner-head';
    head.alt = winner.name;
    win.appendChild(head);
    const flag = factionFlagImg(winner.factionId, 'gh-victory-flag');
    win.appendChild(flag);
    stack.appendChild(win);
  }
  wrap.appendChild(stack);

  // ── Headline text — cat-themed, no "GAME OVER", no emoji glyphs.
  const headline = el('div', 'gh-victory-headline');
  if (iWon) {
    headline.appendChild(el('div', 'gh-victory-title', 'NIA-DA ! Tu règnes sur le jardin !'));
    headline.appendChild(el('div', 'gh-victory-sub', `Les autres chats t'ont déjà cédé leur coussin préféré.`));
  } else if (winner) {
    headline.appendChild(el('div', 'gh-victory-title', `${winner.name} a tout léché !`));
    headline.appendChild(el('div', 'gh-victory-sub', 'Toutes les autres meutes vont bouder leur gamelle.'));
  } else {
    headline.appendChild(el('div', 'gh-victory-title', 'Fin de la chasse'));
  }
  wrap.appendChild(headline);

  // ── Auto-leave countdown — 8 s after end, redirect to /. Player
  // can click "Filer maintenant" to leave instantly, or hover the
  // countdown to cancel auto-leave (mouseenter pauses).
  const action = el('div', 'gh-victory-action');
  const leaveBtn = document.createElement('button');
  leaveBtn.type = 'button';
  leaveBtn.className = 'btn-primary gh-victory-leave';
  leaveBtn.textContent = 'Filer maintenant';
  leaveBtn.addEventListener('click', () => {
    window.history.pushState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  });
  action.appendChild(leaveBtn);
  const countdown = el('div', 'gh-victory-countdown');
  let remaining = 5;
  countdown.textContent = `Retour au jardin dans ${remaining} s`;
  action.appendChild(countdown);
  wrap.appendChild(action);

  // Schedule the auto-leave only ONCE per ended state (the HUD
  // re-renders on every patch — we don't want to re-arm it).
  if (!victoryAutoLeaveScheduled) {
    victoryAutoLeaveScheduled = true;
    let paused = false;
    countdown.addEventListener('mouseenter', () => { paused = true; countdown.textContent = 'Auto-leave en pause — clique "Filer maintenant" quand tu veux.'; });
    const tick = (): void => {
      if (paused) return;
      remaining -= 1;
      if (remaining <= 0) {
        window.history.pushState(null, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
        return;
      }
      countdown.textContent = `Retour au jardin dans ${remaining} s`;
      window.setTimeout(tick, 1000);
    };
    window.setTimeout(tick, 1000);
  }
  return wrap;
}

function renderLeaveButton(ctx: InGameContext): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn-secondary gh-leave';
  // Compact form: sad iconchat + right arrow. The arrow makes the "exit /
  // leave" intent crystal clear; the full label lives in the title tooltip.
  const icon = document.createElement('img');
  icon.src = '/sprites/iconchat_perdu.png';
  icon.className = 'iconchat-inline';
  icon.alt = '';
  btn.appendChild(icon);
  const arrow = document.createElement('span');
  arrow.className = 'gh-leave-arrow';
  arrow.textContent = '→';
  btn.appendChild(arrow);
  btn.title = 'Filer sur les toits — quitter la partie';
  btn.addEventListener('click', () => openLeaveConfirmModal(ctx));
  return btn;
}

/**
 * Confirmation modal that pops between the leave button and the actual
 * room exit. Cat-themed, sad face up top, every kingdom's head swaying
 * gently in goodbye, and a yes/no pair. The "no" closes the modal;
 * the "yes" tears the room down for real.
 *
 * Mounted on document.body via the shared `.modal-overlay`/`.modal-card`
 * recipe so it picks up the canonical paw bg + faction-coloured border
 * automatically. Cleaned up by `unmountInGame`'s blanket `.modal-overlay`
 * sweep if the player navigates away without dismissing.
 */
function openLeaveConfirmModal(ctx: InGameContext): void {
  // De-dupe so spam-clicking the leave button doesn't stack popups.
  if (document.querySelector('.gh-leave-confirm')) return;
  // Faction colour for the border — local player's banner.
  const me = findMyParticipant(ctx.state);
  const myColor = me ? factionColorHex(ctx, me.factionId) : '#dbb87c';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card gh-leave-confirm');
  card.style.setProperty('--faction-color', myColor);
  card.style.borderColor = myColor;

  // Sad cat face at the top.
  const sad = document.createElement('img');
  sad.className = 'gh-leave-confirm-sad';
  sad.src = '/sprites/iconchat_perdu.png';
  sad.alt = '';
  card.appendChild(sad);

  // Title + sub copy. Funny cat-flavoured "don't leave me".
  card.appendChild(el('h2', 'modal-title gh-leave-confirm-title', 'Tu vas vraiment me quitter ?'));
  card.appendChild(
    el(
      'p',
      'modal-help gh-leave-confirm-sub',
      'MIAOU triste ! Les chatons vont rester tout seuls à se lécher les pattes en pleurnichant dans le jardin… Tu reviens hein ? Dis ?',
    ),
  );
  // Ranked-only hint: you have 2 minutes to come back via "Revenir à
  // ma chasse" from the hub before the server forfaits you.
  if (ctx.state.isRanked) {
    const rankedHint = el('p', 'modal-help gh-leave-confirm-ranked');
    const warnIcon = document.createElement('img');
    warnIcon.src = '/sprites/iconchat_choquer.png';
    warnIcon.className = 'inline-icon';
    warnIcon.alt = '';
    rankedHint.appendChild(warnIcon);
    rankedHint.appendChild(document.createTextNode(' Partie classée — tu as 2 minutes pour revenir via "Revenir à ma chasse". Au-delà, c\'est forfait.'));
    card.appendChild(rankedHint);
  }

  // Row of swaying kingdom heads — one per participant. Each head
  // wobbles on its own phase so the cluster reads as a small worried
  // crowd, not a synchronised dance.
  const heads = el('div', 'gh-leave-confirm-heads');
  ctx.state.participants.forEach((p, i) => {
    const head = document.createElement('img');
    head.className = 'gh-leave-confirm-head';
    head.src = `/sprites/${p.factionId}_army.png`;
    head.alt = '';
    // Stagger animation delay so heads don't sway in lockstep.
    head.style.animationDelay = `${(i % 4) * -0.35}s`;
    heads.appendChild(head);
  });
  card.appendChild(heads);

  // Buttons row — YES (valide) on the left, NO (refus) on the right.
  const buttons = el('div', 'modal-buttons gh-leave-confirm-buttons');

  const yesBtn = document.createElement('button');
  yesBtn.type = 'button';
  yesBtn.className = 'btn-secondary gh-leave-confirm-yes';
  const yesIcon = document.createElement('img');
  yesIcon.src = '/sprites/iconchat_valide.png';
  yesIcon.className = 'iconchat-inline';
  yesIcon.alt = '';
  yesBtn.appendChild(yesIcon);
  yesBtn.appendChild(document.createTextNode('Oui, je file'));
  yesBtn.addEventListener('click', () => {
    overlay.remove();
    void ctx.room.leave().catch(() => null);
    // History-API router : navigate to root via pushState rather than
    // mutating window.location.hash (which would re-add the `#`).
    window.history.pushState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  const noBtn = document.createElement('button');
  noBtn.type = 'button';
  noBtn.className = 'btn-primary gh-leave-confirm-no';
  const noIcon = document.createElement('img');
  noIcon.src = '/sprites/iconchat_refus.png';
  noIcon.className = 'iconchat-inline';
  noIcon.alt = '';
  noBtn.appendChild(noIcon);
  noBtn.appendChild(document.createTextNode('Non, je reste'));
  noBtn.addEventListener('click', () => overlay.remove());

  buttons.append(yesBtn, noBtn);
  card.appendChild(buttons);

  overlay.appendChild(card);
  // Click outside the card = same as "Non, je reste".
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.appendChild(overlay);
}

// ===========================================================================
// Tech tree modal
// ===========================================================================

/**
 * Compute a node's status from the player's current state.
 *   researched → already done
 *   available  → all prereqs satisfied + I can afford it
 *   unaffordable → prereqs satisfied but not enough fruit yet
 *   locked     → at least one prereq is missing
 */
type TechStatus = 'researched' | 'available' | 'unaffordable' | 'locked';

function getTechStatus(
  me: Participant,
  tech: TechDef,
  myRegions: number,
  maxCastleTier: number,
  researchedSet: Set<string>,
): { status: TechStatus; missing: string[] } {
  if (researchedSet.has(tech.id)) return { status: 'researched', missing: [] };
  const missing: string[] = [];
  for (const reqId of tech.requiresTechs) {
    if (!researchedSet.has(reqId)) {
      missing.push(`Tech : ${TECHS[reqId]?.name ?? reqId}`);
    }
  }
  if (myRegions < tech.requiresRegions) {
    missing.push(`${tech.requiresRegions} coins de jardin (tu en as ${myRegions})`);
  }
  if (tech.tier >= 2 && maxCastleTier < tech.tier) {
    missing.push(`Repaire niveau ${tech.tier}`);
  }
  if (missing.length > 0) return { status: 'locked', missing };
  if (me.fruit < tech.costFruit) return { status: 'unaffordable', missing: [] };
  return { status: 'available', missing: [] };
}

function openTechTreeModal(ctx: InGameContext): void {
  const initial = findMyParticipant(ctx.state);
  if (!initial) return;
  const initialIdx = findParticipantIndex(ctx.state, initial);
  if (initialIdx < 0) return;

  // Compute snapshot inputs every time the tree is (re-)rendered.
  // `me` is re-fetched on every refresh so the panel always reflects
  // the latest state — fruit balance, researched set, region count.
  // Previously this captured a single `me` reference at modal open
  // time, which combined with a fast double-click on "Sniffer !"
  // could leave the button visible even after the state had moved
  // on. We now ALSO disable the button on click (see renderTechCard)
  // so spam-clicking can't fire the same research twice in a row.
  const buildPanel = (me: Participant, myIdx: number): HTMLElement => {
    const wrap = el('div', 'tech-tree');

    let regions = 0;
    let maxCastleTier = 0;
    for (let r = 0; r < ctx.state.regionOwners.length; r++) {
      if (ctx.state.regionOwners[r] !== myIdx) continue;
      regions++;
      const b = ctx.state.regionBuilding[r] ?? 0;
      const tier = b === 4 ? 3 : b === 3 ? 2 : b === 2 ? 1 : 0;
      if (tier > maxCastleTier) maxCastleTier = tier;
    }
    const researchedSet = new Set<string>();
    for (let i = 0; i < me.researchedTechs.length; i++) {
      const t = me.researchedTechs[i];
      if (t) researchedSet.add(t);
    }

    // Header — fruit balance + summary stats
    const header = el('div', 'tech-tree-header');
    header.appendChild(el('h2', 'modal-title', '🌳 Sniffer de nouvelles idées'));
    const stats = el('div', 'tech-tree-stats');
    const fruitImg = document.createElement('img');
    fruitImg.src = '/sprites/ressource_fruit.png';
    fruitImg.className = 'tech-fruit-icon';
    stats.appendChild(fruitImg);
    stats.appendChild(el('span', 'tech-fruit-count', `${me.fruit} croquette${me.fruit !== 1 ? 's' : ''}`));
    stats.appendChild(el('span', 'tech-stat-sep', '·'));
    stats.appendChild(el('span', '', `${regions} coin${regions !== 1 ? 's' : ''} de jardin`));
    stats.appendChild(el('span', 'tech-stat-sep', '·'));
    stats.appendChild(el('span', '', `Repaire niv. max : ${maxCastleTier || '–'}`));
    header.appendChild(stats);
    wrap.appendChild(header);

    // The grid: 4 branches × 3 tiers
    const grid = el('div', 'tech-grid');
    grid.style.position = 'relative';

    // Container for the prereq arrows — plain HTML, no SVG. We position a
    // single ">" character per arrow, rotated to point along the link
    // from parent card to child. Cheaper to read than dashed SVG lines
    // and matches the pixel-font aesthetic of the rest of the modal.
    const arrowsLayer = el('div', 'tech-arrows');
    grid.appendChild(arrowsLayer);

    // Branch columns
    const branches: Array<{ id: 'def' | 'eco' | 'mil' | 'utl'; label: string; color: string }> = [
      { id: 'def', label: '🛡 Coussinets', color: '#8fd1ff' },
      { id: 'eco', label: '💰 Trésors', color: '#ffd073' },
      { id: 'mil', label: '⚔ Bagarre', color: '#f48a8a' },
      { id: 'utl', label: '🔧 Astuces', color: '#b6e88c' },
    ];

    // Layout: each branch is a column with up to 3 stacked tiers.
    const cardRefs = new Map<string, HTMLElement>();
    for (const br of branches) {
      const col = el('div', 'tech-col');
      const title = el('div', 'tech-col-title', br.label);
      title.style.color = br.color;
      col.appendChild(title);
      for (let tier = 1; tier <= 3; tier++) {
        const tiersWrap = el('div', 'tech-col-tier');
        for (const techId of Object.keys(TECHS)) {
          const tech = TECHS[techId]!;
          if (tech.branch !== br.id || tech.tier !== tier) continue;
          const card = renderTechCard(ctx, tech, me, regions, maxCastleTier, researchedSet, () => {
            ctx.room.send('tech.research', { techId: tech.id });
            playTinkle();
            // Lock EVERY buy button on the panel until the next state
            // patch arrives (which rebuilds the panel from fresh data).
            // Without this, the player could click another card whose
            // affordability was computed from the pre-deduction fruit
            // value — server would reject but the UI looked enabled.
            lockEveryBuyButton();
          });
          cardRefs.set(tech.id, card);
          tiersWrap.appendChild(card);
        }
        col.appendChild(tiersWrap);
      }
      grid.appendChild(col);
    }

    // Helper closure shared by both the research click handler above
    // and the boost click handler below. Walks the panel and disables
    // every "Sniffer !" / "Acheter NIA !" button until the next render.
    function lockEveryBuyButton(): void {
      wrap.querySelectorAll<HTMLButtonElement>('.tech-card-btn').forEach((b) => {
        if (b.disabled) return;
        b.disabled = true;
        b.textContent = 'En attente…';
      });
    }

    // ── 5th column: Croquettes magiques (consumable boosts) ──────────────
    // Distinct visual treatment: a single tall stack, no tiers, and a
    // banner explaining that these are re-buyable.
    const boostCol = el('div', 'tech-col tech-col-boost');
    // Gold-tinted fruit icon followed by the column name. The fruit
    // sprite is rendered via CSS filter to a warm gold so it matches the
    // "magical croquette" vibe instead of staying its natural orange.
    const boostTitle = document.createElement('div');
    boostTitle.className = 'tech-col-title';
    const boostIcon = document.createElement('img');
    boostIcon.src = '/sprites/ressource_fruit.png';
    boostIcon.className = 'tech-col-fruit-icon';
    boostIcon.alt = '';
    boostTitle.appendChild(boostIcon);
    boostTitle.appendChild(document.createTextNode(' Croquettes magiques'));
    boostTitle.style.color = '#dbb87c';
    boostCol.appendChild(boostTitle);
    const boostBanner = el(
      'div',
      'tech-col-banner',
      'Re-achetable à chaque fois · effet temporaire ou chargeable',
    );
    boostCol.appendChild(boostBanner);
    const boostTiers = el('div', 'tech-col-tier');
    for (const boostId of Object.keys(BOOSTS)) {
      const boost = BOOSTS[boostId]!;
      const card = renderBoostCard(boost, me, regions, () => {
        ctx.room.send('tech.purchase_boost', { boostId: boost.id });
        playSparkle();
        // Same global lock as the research path — fruit deducted,
        // all other cards' affordability needs to be re-evaluated
        // from the next state patch.
        lockEveryBuyButton();
      });
      boostTiers.appendChild(card);
    }
    boostCol.appendChild(boostTiers);
    grid.appendChild(boostCol);

    wrap.appendChild(grid);

    // Draw the prereq arrows AFTER the DOM has settled — measure card
    // positions, then drop a rotated ">" chevron right between each
    // parent → child pair. Lightweight, in pixel-font style.
    requestAnimationFrame(() => {
      arrowsLayer.innerHTML = '';
      const gridRect = grid.getBoundingClientRect();
      for (const [techId, tech] of Object.entries(TECHS)) {
        for (const reqId of tech.requiresTechs) {
          const from = cardRefs.get(reqId);
          const to = cardRefs.get(techId);
          if (!from || !to) continue;
          const fr = from.getBoundingClientRect();
          const tr = to.getBoundingClientRect();
          // Midpoint of the link, relative to the grid origin.
          const x1 = fr.left + fr.width / 2 - gridRect.left;
          const y1 = fr.bottom - gridRect.top;
          const x2 = tr.left + tr.width / 2 - gridRect.left;
          const y2 = tr.top - gridRect.top;
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;
          // Angle of the "from → to" vector. ">" naturally points right
          // (angle 0), so add 90° so a vertically-downward link (90°)
          // makes the chevron face down — and any diagonal works too.
          const angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
          const branch = TECHS[techId]?.branch;
          const branchColor = branches.find((b) => b.id === branch)?.color ?? '#e8d8a8';
          const arrow = document.createElement('div');
          arrow.className = 'tech-arrow';
          arrow.textContent = '>';
          arrow.style.left = `${mx}px`;
          arrow.style.top = `${my}px`;
          arrow.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
          arrow.style.color = branchColor;
          arrowsLayer.appendChild(arrow);
        }
      }
    });

    return wrap;
  };

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const card = el('div', 'modal-card tech-modal');

  // Explicit close button — a small ui_fermer chip in the top-right
  // corner of the modal. Mobile users especially need this since
  // "click outside the card" is hard to discover on a tech modal
  // that fills most of the viewport.
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'tech-modal-close';
  closeBtn.title = 'Fermer';
  const closeIcon = document.createElement('img');
  closeIcon.src = '/sprites/ui_fermer.png';
  closeIcon.alt = 'Fermer';
  closeIcon.className = 'tech-modal-close-icon';
  closeBtn.appendChild(closeIcon);
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    overlay.remove();
  });
  card.appendChild(closeBtn);

  // Mobile-only behaviour: tap a tech card to expand its details
  // (description / cost / status / "Sniffer !" button). By default
  // the mobile CSS collapses every card to icon + name only so the
  // grid fits without 4-axis scrolling. Tapping toggles a single
  // `.tech-card-expanded` class on the tapped card — and collapses
  // any other expanded card so the layout never bloats with multiple
  // open details panels.
  //
  // Delegated handler on the modal card so it survives refresh()
  // (which rebuilds the inner panel but keeps the outer modal alive).
  card.addEventListener('click', (e) => {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Tapping the Sniffer / Acheter button should NOT also toggle
    // expand — it has its own action.
    if (target.closest('.tech-card-btn')) return;
    if (target.closest('.tech-modal-close')) return;
    const tapped = target.closest('.tech-card') as HTMLElement | null;
    if (!tapped) return;
    const wasOpen = tapped.classList.contains('tech-card-expanded');
    card.querySelectorAll('.tech-card-expanded').forEach((n) => {
      n.classList.remove('tech-card-expanded');
    });
    if (!wasOpen) tapped.classList.add('tech-card-expanded');
  });

  card.appendChild(buildPanel(initial, initialIdx));

  // Live re-render when the room state updates (a successful research
  // mutates `me.researchedTechs` and `me.fruit`). We re-find `me` from
  // the live state on every tick so the rebuilt panel always shows
  // the current fruit + researched set — not a stale snapshot.
  const room = ctx.room;
  const refresh = (): void => {
    if (!document.body.contains(overlay)) {
      room.onStateChange.remove(refresh);
      return;
    }
    const fresh = findMyParticipant(ctx.state);
    if (!fresh) return;
    const freshIdx = findParticipantIndex(ctx.state, fresh);
    if (freshIdx < 0) return;
    // Preserve the close button across refreshes — only wipe the panel.
    card.innerHTML = '';
    card.appendChild(closeBtn);
    card.appendChild(buildPanel(fresh, freshIdx));
  };
  room.onStateChange(refresh);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

/** Render a single tech card. */
/**
 * Render a single consumable BOOST card (5th column "Croquettes magiques").
 * Differs from a regular tech card:
 *   - No "Researched" terminal state — always purchasable
 *   - Shows current stock / remaining buff turns instead
 *   - Disabled only if unaffordable or region prereq unmet
 */
function renderBoostCard(
  boost: BoostDef,
  me: Participant,
  myRegions: number,
  onBuy: () => void,
): HTMLElement {
  const card = el('div', 'tech-card tech-card-boost');
  const head = el('div', 'tech-card-head');
  const iconImg = document.createElement('img');
  iconImg.src = boost.icon;
  iconImg.className = 'tech-card-icon-img';
  iconImg.alt = '';
  head.appendChild(iconImg);
  head.appendChild(el('span', 'tech-card-name', boost.name));
  card.appendChild(head);

  card.appendChild(el('div', 'tech-card-desc', boost.description));

  // Current state: stock count for shots, turn count for buffs.
  let stateLabel = '';
  if (boost.id === 'attackBuff' && me.attackBuffTurns > 0) {
    stateLabel = `En cours : ${me.attackBuffTurns} patrouille${me.attackBuffTurns > 1 ? 's' : ''} restante${me.attackBuffTurns > 1 ? 's' : ''}`;
  } else if (boost.id === 'defenseBuff' && me.defenseBuffTurns > 0) {
    stateLabel = `En cours : ${me.defenseBuffTurns} patrouille${me.defenseBuffTurns > 1 ? 's' : ''} restante${me.defenseBuffTurns > 1 ? 's' : ''}`;
  } else if (boost.id === 'castleShot' && me.castleShots > 0) {
    stateLabel = `Stock : ×${me.castleShots} charge${me.castleShots > 1 ? 's' : ''}`;
  }
  if (stateLabel) {
    card.appendChild(el('div', 'tech-card-state', stateLabel));
  }

  const cost = el('div', 'tech-card-cost');
  const fImg = document.createElement('img');
  fImg.src = '/sprites/ressource_fruit.png';
  fImg.className = 'tech-card-cost-icon';
  cost.appendChild(fImg);
  cost.appendChild(document.createTextNode(`${boost.costFruit}`));
  card.appendChild(cost);

  const missing: string[] = [];
  if (myRegions < boost.requiresRegions) {
    missing.push(`${boost.requiresRegions} coins min. (tu en as ${myRegions})`);
  }
  const canAfford = me.fruit >= boost.costFruit;

  if (missing.length > 0) {
    card.classList.add('tech-card-locked');
    const m = el('div', 'tech-card-status');
    m.innerHTML = '🔒 ' + missing.map((x) => `<span class="tech-req">${x}</span>`).join(' · ');
    // Re-render emoji-as-icon (renderTextWithIcons handles it)
    card.appendChild(m);
  } else if (!canAfford) {
    card.classList.add('tech-card-unaffordable');
    const need = boost.costFruit - me.fruit;
    card.appendChild(el('div', 'tech-card-status', `Manque ${need} croquette${need > 1 ? 's' : ''} 🐟`));
  } else {
    card.classList.add('tech-card-available');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-primary tech-card-btn';
    btn.textContent = 'Acheter NIA !';
    btn.addEventListener('click', () => {
      // Same anti-spam pattern as the research button — disable on
      // click so a double-click doesn't buy two charges / two buffs
      // back-to-back. The refresh on state change rebuilds the card
      // with fresh fruit + stock numbers.
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Achat…';
      onBuy();
    });
    card.appendChild(btn);
  }
  return card;
}

function renderTechCard(
  _ctx: InGameContext,
  tech: TechDef,
  me: Participant,
  myRegions: number,
  maxCastleTier: number,
  researchedSet: Set<string>,
  onResearch: () => void,
): HTMLElement {
  const { status, missing } = getTechStatus(me, tech, myRegions, maxCastleTier, researchedSet);
  const card = el('div', `tech-card tech-card-${status}`);

  const head = el('div', 'tech-card-head');
  // Tech icon is now a sprite path — render it as an <img>.
  const iconImg = document.createElement('img');
  iconImg.src = tech.icon;
  iconImg.className = 'tech-card-icon-img';
  iconImg.alt = '';
  head.appendChild(iconImg);
  head.appendChild(el('span', 'tech-card-name', tech.name));
  card.appendChild(head);

  card.appendChild(el('div', 'tech-card-desc', tech.description));

  const cost = el('div', 'tech-card-cost');
  const fImg = document.createElement('img');
  fImg.src = '/sprites/ressource_fruit.png';
  fImg.className = 'tech-card-cost-icon';
  cost.appendChild(fImg);
  cost.appendChild(document.createTextNode(`${tech.costFruit}`));
  card.appendChild(cost);

  if (status === 'researched') {
    card.appendChild(el('div', 'tech-card-status', '✓ Sniffé Ronron'));
  } else if (status === 'locked') {
    const m = el('div', 'tech-card-status');
    m.innerHTML = '🔒 ' + missing.map((x) => `<span class="tech-req">${x}</span>`).join(' · ');
    card.appendChild(m);
  } else if (status === 'unaffordable') {
    const need = tech.costFruit - me.fruit;
    card.appendChild(el('div', 'tech-card-status', `Manque ${need} croquette${need > 1 ? 's' : ''} 🐟`));
  } else {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-primary tech-card-btn';
    btn.textContent = 'Sniffer !';
    btn.addEventListener('click', () => {
      // Disable immediately so a fast double-click can't fire the
      // research twice. The state-driven refresh will rebuild this
      // card with the "researched" status as soon as the server's
      // ack arrives — at which point the whole button is replaced
      // by the "✓ Sniffé Ronron" status line. The brief "Sniffing…"
      // label fills the gap so the player sees instant feedback.
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Sniffing…';
      onResearch();
    });
    card.appendChild(btn);
  }
  return card;
}
